import { FeatureKey } from "@workspace/api-client-react";

export interface HelpArticle {
  id: string;
  title: string;
  emoji: string;
  module: string;
  featureKey?: FeatureKey;
  requiredFeatures?: FeatureKey[];
  requiredPermission?: string;
  summary: string;
  body: string;
  tips: string[];
  relatedIds: string[];
  pageIds: string[];
}

// hint: Logic changed on both sides. Requires understanding intent of each change.
export const HELP_ARTICLES: HelpArticle[] = [
  {
    id: "everyday-bullion-live-commerce-overlay",
    title: "EverydayBullion Live Commerce overlay in OBS",
    emoji: "◉",
    module: "Markets",
    requiredPermission: "streaming.view",
     summary: "Create, publish and safely connect the approved overlay to OBS at its authoritative canvas size.",
    body: `## EverydayBullion — Live Commerce

Choose **EverydayBullion — Live Commerce** in **Markets → Overlay Studio**, then create a draft. The protected gallery preset is never edited directly: your copy can be moved, restyled, duplicated and restored without changing the system preset.

The first **230 px** is deliberately clear for TikTok's native LIVE and profile controls. Keep essential content outside the right and bottom guides. Draft autosaves and editor previews do not change OBS; press **Publish** when the live output is ready.

## Add it to OBS

1. Publish the overlay.
2. Generate a **secure scoped browser-source key** and copy the URL while it is shown. This is the primary live source.
3. Add an **OBS Browser Source** using the published canvas dimensions shown in Overlay Studio (the approved preset is **1080 × 1920**) and **30 or 60 FPS**.
4. Leave the browser-source background transparent. Legacy /api/overlays links remain available only for existing installations and are not the recommended source for new setups. “Shutdown source when not visible” and “Refresh browser when scene becomes active” are optional.

Prices come from MetalUI's server-side live-price service. **Delayed**, **Last updated**, or **Unavailable** describes the current feed; the browser source never calls a provider directly and never substitutes example prices. Rotate or revoke a leaked key from Publishing. Profile and scene controls can switch the published overlay and live copy without exposing editor access.`,
    tips: [
      "Use Preview in OBS to check transparency and safe zones before publishing.",
      "The official transparent logo is intentionally unboxed and must remain uncropped, undistorted and free of effects.",
      "Free plans always show the MetalUI badge; server-side entitlement rules decide whether removal is allowed.",
    ],
    relatedIds: [],
    pageIds: ["market-overlay-studio", "overlay-designer"],
  },
  // ── Dashboard ────────────────────────────────────────────────────────────
  {
    id: "product-performance",
    title: "Product Performance: measures and reorder recommendations",
    emoji: "📈",
    module: "Dashboard",
    featureKey: FeatureKey.product_performance_enabled,
    requiredPermission: "view_inventory",
    summary: "Understand recognized sales, reconciled margin, stock cover and transparent, review-first reorder recommendations.",
    body: `## 📈 Product Performance

Product Performance combines product sales, stock and incoming purchase-order facts to help you decide what needs attention. It does not replace Sales, Inventory or Purchasing; use those records to correct source data.

**Recognized sales** include paid, non-cancelled sale activity in the selected tenant-local period. Open baskets, wishlists, checkout intent and unpaid orders are demand signals only — they never increase revenue, units sold or profit.

**Revenue, COGS and profit** reconcile to recognized sale lines and allocated costs. Gross profit is revenue less allocated COGS. True profit includes the additional recorded allocation inputs where available. If you do not have finance access, price, cost, profit and margin are intentionally hidden rather than shown as zero.

**Sellable stock** is on-hand stock less reservations. Confirmed incoming purchase-order quantities are displayed separately, so delayed or unconfirmed supply cannot make a stockout disappear.

**Stock cover and reorders** use recent recognized-sales velocity, lead time, safety/minimum/target stock, confirmed supply, MOQ and pack size. The recommendation explains its inputs and confidence. Review or adjust it, then create a **draft** purchase order; nothing is submitted automatically.`,
    tips: [
      "The default week is Monday–Sunday in your tenant timezone; compare like-for-like periods before acting on a trend.",
      "A low-confidence recommendation is a prompt to inspect the inputs, not a confirmed demand forecast.",
      "Dismissal and override decisions are recorded. Draft POs remain subject to your normal purchasing approval workflow.",
      "Use alerts and digests for stockout, velocity, margin and supplier-risk conditions; each can be adjusted in Notification preferences.",
    ],
    relatedIds: ["dashboard-overview", "inventory-overview"],
    pageIds: ["dashboard", "product-performance"],
  },
  {
    id: "dashboard-overview",
    title: "Understanding your Dashboard",
    emoji: "📊",
    module: "Dashboard",
    requiredPermission: "view_dashboard",
    summary: "Your live snapshot — revenue, profit, open orders and low-stock alerts, all at a glance.",
    body: `## 📊 Your command centre

The Dashboard shows you everything that matters right now, without having to dig through multiple pages.

**Key numbers at the top**

- **Revenue today / this week / this month** — total sales value collected
- **Gross profit** — revenue minus the landed cost of goods sold
- **Open orders** — sales that haven't been fully packed and dispatched yet
- **Low-stock items** — products whose quantity on hand has dipped below your reorder threshold

**Recent activity feed**

The feed on the right lists the last 20 actions across the system — new sales, stock adjustments, packing completions — so you know what your team has been up to at a glance.

**What to do when profit looks wrong**

Check the date range filter at the top. The dashboard defaults to the current calendar month. If a big sale landed yesterday but your comparison window is set to "Today", you won't see it.`,
    tips: [
      "Click any KPI card to drill into the underlying list (e.g. clicking 'Open orders' jumps to the filtered Sales page).",
      "Low-stock alerts are driven by the per-SKU 'Reorder at' threshold you set on each inventory item.",
      "The profit figure uses landed cost — if your supplier costs aren't entered accurately, the profit will be off.",
    ],
    relatedIds: ["reports-pl", "inventory-overview"],
    pageIds: ["dashboard"],
  },

  // ── Sales ────────────────────────────────────────────────────────────────
  {
    id: "sales-overview",
    title: "Recording and managing sales",
    emoji: "🛒",
    module: "Sales",
    requiredPermission: "view_sales",
    summary: "Create manual sales, import from eBay/Shopify, issue invoices and track what's open.",
    body: `## 🛒 Sales — from order to invoice

**Creating a manual sale**

1. Click **New Sale** in the top-right corner of the Sales page.
2. Pick or create the customer, then add each line item (SKU, quantity, sale price).
3. Hit **Create** — stock is deducted from inventory immediately and profit is recalculated.

**Importing from a marketplace**

Click **Import Sales**, choose the source (eBay CSV, Shopify, WooCommerce…) and follow the wizard. The system deduplicates by order ID, so re-importing won't create doubles.

**Sale statuses**

| Status | Meaning |
|--------|---------|
| Unpaid | Order exists but payment not recorded |
| Paid | Payment confirmed — ready to pack |
| Packed | Dispatched by your packing team |
| Cancelled | Voided — stock is restocked automatically |

**Invoices**

Once a sale is in *Paid* status you can issue a PDF invoice from the sale detail page. Invoice branding (logo, colours, footer) is configured under **Settings → Invoice template**.`,
    tips: [
      "Use the eBay title parser (icon next to the title field when creating a manual eBay sale) to auto-fill the product details from the listing title.",
      "Cancelling a sale restores the stock quantity — you don't need to manually adjust inventory.",
      "You can attach photos or documents to a sale using the Attachments tab — useful for postage labels and proof of delivery.",
    ],
    relatedIds: ["inventory-overview", "packing-overview"],
    pageIds: ["sales"],
  },

  // ── Order payments ───────────────────────────────────────────────────────
  {
    id: "payments-overview",
    title: "Order payment statuses explained",
    emoji: "💳",
    module: "Sales",
    requiredPermission: "sales.payment.view_unpaid",
    summary:
      "Track what a manual order has been paid — Unpaid, Part Paid, Outstanding, Paid, Refunded and more.",
    body: `## 💳 Knowing exactly what you're owed

Every **manual order** carries a payment status so you always know where it stands. (Marketplace orders — eBay, Shopify, TikTok — show a status too, derived from what the channel tells us.)

**The statuses**

| Status | Meaning |
|--------|---------|
| Unpaid | No payment recorded yet |
| Part Paid | Some money received, a balance still remains |
| Outstanding | Unpaid or Part Paid **and past its due date** |
| Paid | Paid in full |
| Refunded | Fully refunded |
| Part Refunded | Some money returned to the customer |
| Cancelled | Order voided |
| Written Off | Remaining balance you've decided not to chase |

**Outstanding is special — it's worked out for you**

You never set "Outstanding" by hand. The system derives it the moment an Unpaid or Part Paid order passes the due date you set, so the figure is always live and accurate.

**Where you'll see it**

- A status badge on every order in the Sales list and on the order detail page.
- A balance figure on the customer's record (Customer 360).
- Warnings on the Dashboard when balances fall overdue.`,
    tips: [
      "Outstanding is never stored — it's calculated from the due date in real time, so you can trust it always reflects today.",
      "Only manual orders have payment controls you can edit; marketplace order statuses come from the channel.",
      "Set a due date on an order to unlock overdue tracking, dashboard warnings and the overdue reports.",
    ],
    relatedIds: ["payments-recording", "payments-outstanding", "payments-reports"],
    pageIds: ["sales"],
  },
  {
    id: "payments-recording",
    title: "Recording payments on an order",
    emoji: "💷",
    module: "Sales",
    requiredPermission: "sales.payment.add_payment",
    summary: "Add a payment, mark an order paid or unpaid, and keep a clean money trail.",
    body: `## 💷 Logging the money as it comes in

Open any manual order and use the **Payments** panel on the detail page.

**Adding a payment**

1. Click **Add payment**.
2. Enter the amount received (and optionally a method and note).
3. Save. The balance recalculates instantly and the status moves to **Part Paid** or **Paid**.

**Mark as Paid / Mark as Unpaid**

- **Mark as Paid** records a payment for the full remaining balance in one click.
- **Mark as Unpaid** reverses it with a balancing entry — it never silently deletes history.

**Every change is kept**

Each payment writes a line to the order's payment ledger, and every status change is recorded with who did it and when. The ledger — not a single field — is the source of truth for how much has been paid, so the numbers always reconcile.`,
    tips: [
      "The amount paid is the running total of the ledger, so corrections are always traceable.",
      "Marking an order unpaid posts a reversing entry rather than erasing the original — your audit trail stays intact.",
      "Adding a partial payment automatically moves the order to Part Paid; paying the rest moves it to Paid.",
    ],
    relatedIds: ["payments-overview", "payments-outstanding", "payments-writeoff"],
    pageIds: ["sales"],
  },
  {
    id: "payments-outstanding",
    title: "Due dates, overdue orders and chasing balances",
    emoji: "⏰",
    module: "Sales",
    requiredPermission: "sales.payment.view_unpaid",
    summary: "Set due dates, let the system flag overdue orders, and get alerted when balances slip.",
    body: `## ⏰ Never lose track of an unpaid balance

**Set a due date**

On a manual order's Payments panel, set a **payment due date**. From that point the order is watched automatically.

**Automatic overdue detection**

Once an Unpaid or Part Paid order passes its due date it becomes **Outstanding**. A background check runs every hour (using UK time) so you don't have to chase the clock — it stamps the order and raises alerts.

**Alerts you'll receive**

- **Payment due today** — a gentle nudge on the day a balance is due.
- **Payment overdue** — raised once a balance slips past its due date.

These appear in your notifications and on the Dashboard's warnings, so overdue money surfaces without anyone hunting for it.`,
    tips: [
      "Overdue checks use Europe/London time and run hourly — an order due today won't be flagged overdue until the day actually ends.",
      "You'll only get one due-today and one overdue alert per order per day — no notification spam.",
      "No due date means no overdue tracking — set one if you want the order chased automatically.",
    ],
    relatedIds: ["payments-overview", "payments-recording", "payments-reports"],
    pageIds: ["sales"],
  },
  {
    id: "payments-writeoff",
    title: "Writing off and cancelling balances",
    emoji: "🗂️",
    module: "Sales",
    requiredPermission: "sales.payment.write_off",
    summary: "Close out a balance you'll never collect, or cancel an order entirely — cleanly and on the record.",
    body: `## 🗂️ Closing the book on a balance

Sometimes a balance won't be paid. You have two clean ways to deal with it.

**Write off the balance**

Use **Write off** on the order's Payments panel when you've decided not to chase the remaining amount. The order moves to **Written Off**, drops out of your open/overdue lists, but stays fully visible in reports and history.

**Cancel the order**

Use **Cancel** to void the order. Cancelled orders are excluded from outstanding balances and overdue chasing.

**Always accountable**

Both actions are permission-controlled and recorded with who did it and when, so a written-off or cancelled balance is always explainable later.`,
    tips: [
      "Writing off keeps the order in your records — it's reported as Written Off, never deleted.",
      "Written-off and cancelled orders are removed from the unpaid, outstanding and balance-by-customer reports automatically.",
      "These actions are gated on the write-off permission, so only trusted roles can close out a balance.",
    ],
    relatedIds: ["payments-overview", "payments-recording", "payments-reports"],
    pageIds: ["sales"],
  },
  {
    id: "payments-reports",
    title: "Payment reports",
    emoji: "📈",
    module: "Sales",
    requiredPermission: "sales.payment.view_unpaid",
    summary: "See every unpaid, part-paid, outstanding and written-off balance in one place under Reports → Payments.",
    body: `## 📈 The whole picture of what you're owed

Open **Reports → Payments** for a complete view of manual-order money.

**What you get**

- **Outstanding Orders** — everything overdue, sorted by how many days late, with the balance due.
- **Unpaid Orders** — manual orders with nothing paid yet.
- **Part Paid Orders** — orders with a partial payment recorded.
- **Balance Due by Customer** — total open balance grouped per customer, so you know who to chase.
- **Overdue Payments by Date** — overdue balances grouped by due date.
- **Written Off Balances** — what's been written off (in the chosen date window).
- **Manual Order Payment Summary** — totals invoiced, paid and outstanding, broken down by status.

**Reading the dates right**

The open-balance views (Unpaid, Part Paid, Outstanding, Balance by Customer) always show the **current** position regardless of the date filter — an old unpaid order still needs chasing. The date range only narrows the Written Off list and the summary.`,
    tips: [
      "Open balances ignore the date filter on purpose — a six-month-old unpaid order still appears so it never falls through the cracks.",
      "Use Balance Due by Customer to prioritise your chasing — the biggest debtors sort to the top.",
      "The Payments tab only appears for roles allowed to view unpaid orders.",
    ],
    relatedIds: ["payments-overview", "payments-outstanding", "reports-pl"],
    pageIds: ["reports", "sales"],
  },

  // ── Inventory ────────────────────────────────────────────────────────────
  {
    id: "inventory-overview",
    title: "Managing your inventory",
    emoji: "📦",
    module: "Inventory",
    requiredPermission: "view_inventory",
    summary: "Add stock, track quantities, set reorder thresholds and understand owned vs consignment.",
    body: `## 📦 Got new stock? Let's get it listed, tracked and ready to sell.

**Adding stock manually**

1. Open **Inventory** from the sidebar.
2. Click **Add Manually** — or **Import Data** if you have a spreadsheet.
3. Fill in: SKU (must be unique), product title, quantity, landed cost per unit, and choose **Owned** or **Consignment**.
4. Click **Save** — your item appears immediately and feeds into profit calculations.

**Owned vs Consignment**

- **Owned** — you bought it. Profit = sale price − landed cost.
- **Consignment** — the supplier owns it; you sell it on their behalf and owe them a payout when it sells. Profit = your share of the sale price.

**Reorder thresholds**

Set a "Reorder at" quantity on each item. When stock dips to or below this number the Dashboard shows a low-stock alert and you can choose to receive an email or push notification.

**Photos and composite images**

Upload photos on the item detail page. The system can generate a composite image (your item on a branded background) which is used on marketplaces and the Live view.`,
    tips: [
      "Bulk-import via the CSV template (download it from the Import page) to add hundreds of items at once.",
      "The Live view (/inventory/live) shows a compact, printable snapshot of all in-stock items — useful for shop-floor reference.",
      "Editing the landed cost of an existing item retroactively changes profit on all past sales of that SKU — do it carefully.",
    ],
    relatedIds: ["purchase-orders-overview", "sales-overview"],
    pageIds: ["inventory"],
  },

  // ── Purchase Orders ───────────────────────────────────────────────────────
  {
    id: "purchase-orders-overview",
    title: "Raising and receiving purchase orders",
    emoji: "🧾",
    module: "Purchase Orders",
    requiredPermission: "view_purchase_orders",
    summary: "Create POs for new stock, record costs at two stages (order & receipt) and link them to inventory.",
    body: `## 🧾 Purchase orders — from supplier quote to stock on shelf

Purchase orders let you track what you've ordered, at what price, and when it arrived — keeping your landed costs accurate.

**Creating a PO**

1. Go to **Purchase Orders → New PO**.
2. Pick a supplier and add line items (product, qty, estimated cost).
3. Optionally send the PO as a PDF to the supplier via email.

**Two-stage costing**

When the goods arrive you "receive" the PO. This is when you enter the actual landed cost (which may differ from the estimate if freight, import duty or exchange rates changed). The system updates inventory and recalculates profit.

**Shipment groups**

Multiple POs arriving on the same container? Group them under a Shipment Group to track freight allocation across orders.`,
    tips: [
      "The estimated cost on the PO is for reference only — profit uses the actual cost you enter on receipt.",
      "You can partially receive a PO if some items arrive earlier than others.",
    ],
    relatedIds: ["inventory-overview", "suppliers-overview"],
    pageIds: ["purchase-orders"],
  },

  // ── Suppliers ────────────────────────────────────────────────────────────
  {
    id: "suppliers-overview",
    title: "Managing suppliers and consignment payouts",
    emoji: "🤝",
    module: "Suppliers",
    requiredPermission: "view_purchase_orders",
    summary: "Keep supplier records clean and pay out consignment suppliers accurately.",
    body: `## 🤝 Suppliers — who you buy from and what you owe them

**Supplier list**

The Suppliers page lists every supplier you've traded with. Each supplier has a name, optional contact details, and — if they have any consignment stock — a running balance of what you owe them.

**Consignment supplier payouts**

When consignment items sell, the system accrues a balance owed to the supplier. On the supplier detail page:

1. See **Ready to pay** — the total outstanding.
2. Click **Add Manual Payout** to record a bank transfer, cheque or cash payment.
3. The balance updates immediately and the transaction appears on the Payouts tab.

**Statements**

You can generate a PDF statement for a supplier showing all their items sold during a date range and what you paid them — useful for monthly reconciliation.`,
    tips: [
      "Consignment stock must have the correct supplier name on each inventory item — this is how the system knows who to credit.",
      "Payouts are recorded in GBP regardless of the supplier's currency — add a note on the payout record if you paid in a different currency.",
    ],
    relatedIds: ["inventory-overview", "purchase-orders-overview"],
    pageIds: ["suppliers"],
  },

  // ── Packing ───────────────────────────────────────────────────────────────
  {
    id: "packing-overview",
    title: "Packing and dispatch workflow",
    emoji: "📮",
    module: "Packing",
    featureKey: FeatureKey.packing_module_enabled,
    requiredPermission: "view_packing",
    summary: "Walk paid orders through the pack, record-video and confirm-dispatch flow.",
    body: `## 📮 Packing — turning paid orders into dispatched parcels

**The packing queue**

Every sale that reaches *Paid* status appears in the Packing queue. The queue is sorted by oldest-first so nothing slips through.

**Starting a packing job**

1. Click the order in the queue to open the packing job.
2. The page shows the line items to pick and pack.
3. Hit **Start packing** to begin — the system logs the start time.

**Recording a packing video**

If video recording is enabled in packing settings, the page will prompt you to record a short clip as you pack. This protects you in the event of a customer dispute about the item's condition.

**Completing and dispatching**

1. Mark each line item as packed.
2. Click **Complete packing** — the order moves to *Packed* and the customer can be notified (email, SMS or WhatsApp depending on your settings).

**Pick lists**

For large batches, use **Pick List** to print a consolidated list of everything you need to pull from the shelves before you start packing.`,
    tips: [
      "Packing videos are stored against the sale and can be shared with the customer via a secure link.",
      "Configure which notification channels to use for dispatch confirmation under Settings → Packing.",
      "The Print & Pack station (/print-pack) is a minimal interface optimised for warehouse screens.",
    ],
    relatedIds: ["sales-overview", "postage-overview"],
    pageIds: ["packing"],
  },

  // ── Postage ────────────────────────────────────────────────────────────────
  {
    id: "postage-overview",
    title: "Postage and shipping",
    emoji: "🚚",
    module: "Postage",
    featureKey: FeatureKey.postage_module_enabled,
    summary: "Create shipments, buy labels, track parcels and manage postage hold.",
    body: `## 🚚 Postage — from label creation to doorstep delivery

The Postage module integrates with your courier accounts to let you buy and print shipping labels without leaving the system.

**Creating a shipment**

1. Open **Postage → Shipments**.
2. Click **New Shipment**, select the order and fill in the parcel dimensions and weight.
3. Choose a service and buy the label — it's deducted from your courier account balance.

**Postage hold (eBay)**

By default, eBay postage costs are held out of your profit/performance figures until the order is marked as shipped or a set window elapses. This prevents a batch of unshipped orders distorting your numbers. You can adjust or disable this under **Settings → eBay postage hold**.

**Tracking**

Once a label is created the system polls the courier for tracking updates. You can see the current status on the Shipment detail page and on the Sale.`,
    tips: [
      "Combined shipments let you dispatch multiple orders in one parcel — useful for wholesale customers.",
      "The postage hold window (default 4 hours) is configurable. Setting it to 0 hours effectively disables the hold.",
    ],
    relatedIds: ["packing-overview", "sales-overview"],
    pageIds: ["postage"],
  },

  // ── Returns ────────────────────────────────────────────────────────────────
  {
    id: "returns-overview",
    title: "Handling returns and refunds",
    emoji: "↩️",
    module: "Returns",
    featureKey: FeatureKey.returns_module_enabled,
    summary: "Open a return case, gather evidence, decide on refund or replacement, and restock.",
    body: `## ↩️ Returns — fair, documented, efficient

**Opening a return**

1. Go to **Returns** and click **New Return**.
2. Attach it to the original sale, pick the items being returned and state the reason.
3. The return enters *Open* status.

**Evidence gathering**

Upload photos or video of the item as received. You can share a secure evidence link with the customer so they can upload their own photos directly.

**Decisions**

Once evidence is collected, mark the return as:

- **Approved** — issue a refund (full or partial) and optionally restock the item.
- **Rejected** — close the case with a reason.
- **Replacement** — create a new sale to send a replacement item.

Restocking puts the item back into inventory at its original cost — no manual adjustment needed.`,
    tips: [
      "Return audit logs capture every status change and message so you have a complete, tamper-proof trail for any dispute.",
      "eBay returns can be synced automatically — the system polls the eBay Returns API and creates return cases here so you manage everything in one place.",
    ],
    relatedIds: ["sales-overview", "inventory-overview"],
    pageIds: ["returns"],
  },

  // ── HR ─────────────────────────────────────────────────────────────────────
  {
    id: "hr-overview",
    title: "HR module — staff, time and pay",
    emoji: "👥",
    module: "HR",
    featureKey: FeatureKey.hr_module_enabled,
    requiredPermission: "hr.view",
    summary: "Manage staff records, track time and attendance, run rotas and process payroll.",
    body: `## 👥 Your team — organised and paid on time

**Staff records**

HR → Staff lists every team member. Each profile holds their role, contact details, right-to-work documents, bank details (visible only to users with the bank-details permission) and employment history.

**Timekeeping**

Staff can clock in/out from the self-service portal. Admins see the time log on the Performance tab — late arrivals and absences are highlighted automatically based on each staff member's expected schedule.

**Rota**

HR → Rota shows the week's shifts. Drag to create a shift, assign a staff member, and the system will notify them. Unfilled shifts surface as alerts.

**Payroll / Payments**

HR → Payments shows each staff member's next pay date, calculated from their pay cadence. Generate a payroll digest email on a chosen day of the week. Payments are authorised by the designated approver before they're marked as processed.

**Time off**

Staff submit time-off requests through the self-service portal. Managers approve or reject from HR → Time Off.`,
    tips: [
      "The HR module is gated — if you can't see it in the sidebar, ask your platform owner to enable it.",
      "Bank details are only visible to users with the 'hr.bank.view' permission — not all admins see them by default.",
      "Onboarding templates save time when setting up a new starter — create a checklist once and assign it each time.",
    ],
    relatedIds: ["admin-overview"],
    pageIds: ["hr"],
  },

  // ── Tasks ──────────────────────────────────────────────────────────────────
  {
    id: "tasks-overview",
    title: "Tasks and to-do management",
    emoji: "✅",
    module: "Tasks",
    requiredPermission: "tasks.view",
    summary: "Create, assign, and track tasks for yourself and your team.",
    body: `## ✅ Tasks — nothing falls through the cracks

**My Tasks**

This is your personal view — tasks assigned to you, sorted by due date. Overdue items are highlighted in red.

**Team Tasks**

Team Tasks shows all open tasks across the business. You need the 'tasks.view_team' permission to access this.

**Creating a task**

Click **New Task**, add a title, optional description, assign it to someone, pick a due date, and link it to a sale, customer or inventory item if relevant.

**Projects**

Group related tasks into a Project for a structured view — useful for campaigns, audits or one-off initiatives.

**Templates**

Create a task template for repeating workflows (e.g. "End of month close"). Each time you create a task from a template you can customise the checklist before saving.`,
    tips: [
      "Tasks linked to a sale appear on the sale's activity tab so the sales team sees them in context.",
      "Set a reminder on a task to receive a notification at a specific time.",
    ],
    relatedIds: ["dashboard-overview"],
    pageIds: ["tasks"],
  },

  // ── Scrap ──────────────────────────────────────────────────────────────────
  {
    id: "scrap-overview",
    title: "Scrap metal buying and refining",
    emoji: "⚗️",
    module: "Scrap",
    featureKey: FeatureKey.scrap_module_enabled,
    requiredPermission: "view_scrap",
    summary: "Log scrap purchases, track purity and weight, and record refining results.",
    body: `## ⚗️ Scrap — buy, weigh, refine, profit

**Buying scrap**

Go to **Scrap → New Purchase**. For each lot, enter:

- **Metal type** (gold, silver, platinum…)
- **Gross weight** in grams
- **Assumed purity %** — e.g. 9ct gold = 37.5 %, 18ct = 75 %. This is your estimate before assay.
- **Price paid** in GBP

The system calculates the implied cost per gram of fine metal so you can check you paid a fair rate.

**Default purities**

Under **Scrap → Settings** you can set default purity percentages per metal type. These are pre-filled when you add new lots, saving time.

**Refining**

When you send a batch to the refinery, create a Refining record. Once you receive the assay results back, enter them under **Refining Results**. The system shows how close your assumed purity was to the actual outturn.`,
    tips: [
      "The purity field accepts decimals — e.g. 58.5 for 14ct gold (14/24 = 58.33 %).",
      "Refining records feed into the Scrap Reports page which shows your average purchase cost vs outturn per metal.",
    ],
    relatedIds: ["inventory-overview"],
    pageIds: ["scrap"],
  },

  // ── Social ─────────────────────────────────────────────────────────────────
  {
    id: "social-overview",
    title: "Social media publishing",
    emoji: "📣",
    module: "Social",
    summary: "Create, schedule and publish posts across connected social accounts.",
    body: `## 📣 Social — post once, publish everywhere

**Connecting accounts**

Go to **Social → Integrations** and connect your Facebook, Instagram, LinkedIn, X (Twitter), TikTok or Google Business account. Each platform uses an OAuth connection — you'll be redirected to authorise access.

**Composing a post**

Click **New Post** (or **Compose** from the calendar). Write your caption, upload media, tag products from inventory, then choose which platforms to publish on.

**Scheduling**

Pick a future date and time, click **Schedule** — the post will be published automatically. Scheduled posts appear on the **Calendar** view.

**Approvals**

If your workspace has the approvals workflow enabled, drafted posts go to a reviewer before they can be scheduled or published.

**Analytics**

Social → Analytics shows reach, impressions and engagement per post for connected platforms.`,
    tips: [
      "Drafts are saved automatically — you won't lose a post if you close the compose window before publishing.",
      "You can schedule posts months in advance — useful for planning gold/silver spot-price comment posts.",
    ],
    relatedIds: ["integrations-overview"],
    pageIds: ["social"],
  },

  // ── Documents ─────────────────────────────────────────────────────────────
  {
    id: "documents-overview",
    title: "Document generation",
    emoji: "📄",
    module: "Documents",
    featureKey: FeatureKey.document_generation_enabled,
    requiredPermission: "documents.view",
    summary: "Generate branded PDFs from templates — certificates, appraisals, packing slips.",
    body: `## 📄 Documents — professional PDFs in seconds

**Templates**

Go to **Documents → Templates** to manage your PDF templates. Each template has a set of variable placeholders (e.g. {{customer_name}}, {{item_title}}) that are filled in at generation time.

**Generating a document**

From the Documents page (or from a sale or inventory item), click **Generate Document**, pick a template, fill in any required fields and download or email the PDF.

**Generated documents log**

Every generated document is stored with a timestamp and the user who generated it — useful for audit purposes.`,
    tips: [
      "Templates are written in HTML — if you know basic HTML you can fully customise the layout and branding.",
      "Documents generated from a sale are linked to that sale and appear on its activity tab.",
    ],
    relatedIds: ["sales-overview"],
    pageIds: ["documents"],
  },

  // ── Integrations ─────────────────────────────────────────────────────────
  {
    id: "integrations-overview",
    title: "Marketplace and platform integrations",
    emoji: "🔗",
    module: "Integrations",
    requiredPermission: "view_integrations",
    summary: "Connect eBay, Shopify, WooCommerce, TikTok Shop and more to sync stock and orders.",
    body: `## 🔗 Integrations — your platforms, talking to each other

**Connecting a marketplace**

Go to **Integrations** and click **Connect** next to the platform you want to link. Most platforms use OAuth — you'll be taken to their site to grant permission and then redirected back.

**eBay**

Once connected, eBay orders sync automatically. The system matches orders to your inventory by SKU and deducts stock. You can also push inventory listings from here.

**Shopify / WooCommerce**

Two-way sync: new orders pull in as sales, and when you publish/update a product in inventory the change is pushed to the store.

**TikTok Shop**

Orders sync in the same way as eBay. You can also manage TikTok Shop product listings and respond to buyer messages.

**Sync logs**

Integrations → Sync Logs (or the marketplace-specific log page) shows every sync attempt, the result, and any errors — so you can diagnose a failed push without guessing.`,
    tips: [
      "If a marketplace sync is failing, check the sync log first — the error message is usually self-explanatory (e.g. 'SKU not found on eBay').",
      "You can manually trigger a sync from the integration detail page — useful after a bulk stock import.",
    ],
    relatedIds: ["sales-overview", "inventory-overview"],
    pageIds: ["integrations"],
  },

  // ── QuickBooks ────────────────────────────────────────────────────────────
  {
    id: "quickbooks-overview",
    title: "QuickBooks Online sync",
    emoji: "📒",
    module: "QuickBooks",
    featureKey: FeatureKey.quickbooks_integration_enabled,
    summary: "Sync sales, customers and tax data to QuickBooks Online automatically.",
    body: `## 📒 QuickBooks — your books stay up to date automatically

**Connecting**

Go to **Integrations** and click **Connect QuickBooks**. You'll be redirected to QuickBooks to authorise the connection.

**Sync modes**

| Mode | What it creates in QBO |
|------|----------------------|
| Per order | One Sales Receipt (or Invoice) per sale |
| Daily summary | One journal per day |
| Marketplace summary | One journal per marketplace per day |
| Monthly summary | One journal per month |

**Sales document type**

- **Sales Receipt** — use for cash/immediate payment sales.
- **Invoice + payment** — creates an Invoice then applies a Payment. Useful if you want open receivables to appear in QBO.
- **Journal entry** — a balanced journal. Requires you to set up account mappings.

**Auto-sync**

Turn on Auto-sync under QuickBooks → Settings and choose a frequency (hourly, daily). The system will push new Paid sales automatically.`,
    tips: [
      "Set up account mappings (under QB → Account Mappings) before your first sync so income goes to the right chart-of-accounts line.",
      "The sync log shows every record pushed, whether it succeeded, and lets you retry failures individually.",
    ],
    relatedIds: ["integrations-overview", "sales-overview"],
    pageIds: ["quickbooks"],
  },

  // ── Reports / P&L ─────────────────────────────────────────────────────────
  {
    id: "reports-pl",
    title: "Profit & Loss and financial reports",
    emoji: "📈",
    module: "Reports",
    requiredPermission: "view_reports",
    summary: "Understand where your money comes from and where it goes.",
    body: `## 📈 Reports — know your numbers

**Profit & Loss**

The P&L page shows revenue, cost of goods sold and gross profit for any date range. You can split by channel (eBay, Shopify, manual…) or by stock type (owned vs consignment).

**Advanced Analytics**

Dig deeper with cohort analysis, channel performance trends and product-level breakdowns. Requires the Advanced Analytics feature to be enabled.

**Sales Performance**

Track revenue and units sold per product, per customer or per marketplace channel.

**Expenses**

Record one-off business expenses (packaging, postage account top-ups, etc.) on the Expenses page. These feed into the P&L as a separate cost category.`,
    tips: [
      "Profit is always gross profit (sale price minus landed cost) — it doesn't account for overheads like rent or wages unless you record them as Expenses.",
      "Export any report to CSV using the Download button in the top-right corner.",
    ],
    relatedIds: ["dashboard-overview", "sales-overview"],
    pageIds: ["reports", "profit-loss"],
  },

  // ── Feature Management ────────────────────────────────────────────────────
  {
    id: "feature-management",
    title: "Feature flags and tenant management",
    emoji: "🚩",
    module: "Feature Management",
    summary: "Enable or disable add-on modules (HR, Returns, Scrap, Packing…) per tenant.",
    body: `## 🚩 Feature flags — turn modules on and off

Feature flags let platform owners enable specific add-on modules for individual tenants without a code deploy.

**Where to find them**

Platform → Feature Management (visible to platform owners only).

**Common flags**

| Flag | What it unlocks |
|------|----------------|
| hr_module_enabled | HR staff/time/pay module |
| returns_module_enabled | Returns & refund management |
| scrap_module_enabled | Scrap buying and refining |
| packing_module_enabled | Packing queue and video |
| quickbooks_integration_enabled | QuickBooks sync |
| advanced_analytics_enabled | Advanced Analytics page |

**After enabling**

Some modules require a one-off database migration to create their tables. Follow the runbook in the operator notes (visible to platform owners) for the specific module.`,
    tips: [
      "Disabling a flag hides the module from the UI but doesn't delete any data — re-enable it and everything returns.",
      "Role permissions for a module (e.g. hr.view) are only meaningful when the module flag is on.",
    ],
    relatedIds: ["admin-overview"],
    pageIds: ["feature-management", "admin-flags"],
  },

  // ── Admin ─────────────────────────────────────────────────────────────────
  {
    id: "admin-overview",
    title: "Admin — users, roles and system settings",
    emoji: "🔧",
    module: "Admin",
    requiredPermission: "view_admin",
    summary: "Manage team members, roles, permissions, sidebar layout and system health.",
    body: `## 🔧 Admin — running the back-end

**User permissions**

Admin → User Permissions lets you see each team member's role and customise their packing permissions. A permission override replaces the role's default set for that specific user.

**Roles**

Roles define what users can see and do:

| Role | Typical use |
|------|------------|
| owner | Full access, including billing and feature flags |
| admin | Full operational access, no billing |
| sales | Sales, customers, inventory read |
| packer | Packing queue and dispatch only |
| marketing | Social, email campaigns, read-only reports |
| pending | Awaiting approval — no access |

**Sidebar manager**

Admin → Sidebar Manager lets you reorder, hide or rename sidebar items and add custom links for third-party tools.

**Audit log**

Every significant action (sale create/edit, stock adjustment, payout, user permission change) is logged with a timestamp and the user who made it.

**System health**

Admin → System Health shows background job status and any sync errors across all integrations.`,
    tips: [
      "The owner role is a superset of admin — it passes every permission gate automatically.",
      "Hiding a sidebar item doesn't restrict access — it just removes the shortcut. Permission gates are enforced server-side.",
    ],
    relatedIds: ["feature-management", "integrations-overview"],
    pageIds: ["admin", "admin-user-permissions"],
  },

  // ── Tenants Dashboard (platform admin) ──────────────────────────────────────
  {
    id: "admin-tenants-dashboard-overview",
    title: "Tenants Dashboard — monitoring every tenant at a glance",
    emoji: "🏢",
    module: "Admin",
    requiredPermission: "view_admin",
    summary: "A platform-wide health view across every tenant — status, alerts, and drill-down into any account.",
    body: `## 🏢 Tenants Dashboard — platform-wide health

The Tenants Dashboard gives platform operators a single view of every tenant running on the system.

**Health status**

Each tenant is scored into one of five states: Healthy, Needs attention, At risk, Critical, or Inactive. The score factors in recent activity, sync failures, support ticket backlog, and usage against plan limits.

**Alerts**

The dashboard surfaces platform-level alerts as they fire — critical health drops, service peaks, repeated sync failures, system health issues, support backlog spikes, and upgrade opportunities — so nothing quietly goes wrong in the background.

**Drilling into a tenant**

Click any row in the tenant table to open that tenant's detail page: usage against plan limits, feature adoption, recent activity events, and an admin notes thread for internal context.`,
    tips: [
      "Row click navigates straight to the tenant detail page — no separate 'view' button needed.",
      "Health status recalculates on the scheduled rollup job, not on every page load, so there can be a short delay after a change.",
    ],
    relatedIds: ["admin-service-peaks", "admin-feature-usage", "admin-tenant-detail"],
    pageIds: ["tenants-dashboard"],
  },
  {
    id: "admin-service-peaks",
    title: "Service Peaks — spotting load spikes across tenants",
    emoji: "📈",
    module: "Admin",
    requiredPermission: "view_admin",
    summary: "See when and where usage spikes happen, broken down by service and by tenant.",
    body: `## 📈 Service Peaks

Service Peaks shows platform-wide usage volume bucketed by hour or by day, broken down by service (e.g. sales imports, packing, storage uploads).

**Bucket selector**

Switch between hourly and daily buckets depending on whether you're investigating a short-lived spike or a longer trend.

**Platform-wide totals**

The bar chart totals usage events across every tenant, so you can see overall platform load at a glance.

**Per-tenant breakdown**

The table below the chart breaks the same window down by tenant, so a platform-wide spike can be traced back to the specific tenant driving it (useful when a single account starts hammering an integration or import).`,
    tips: [
      "A sudden platform-wide spike is often one tenant running a large import — check the per-tenant table first.",
      "This page reads from the persisted rollup tables, so historical buckets stay available even after the raw event data ages out.",
    ],
    relatedIds: ["admin-tenants-dashboard-overview", "admin-feature-usage"],
    pageIds: ["tenants-service-peaks"],
  },
  {
    id: "admin-feature-usage",
    title: "Feature Usage — which modules tenants actually use",
    emoji: "🧩",
    module: "Admin",
    requiredPermission: "view_admin",
    summary: "Module adoption across all tenants, with a per-tenant breakdown you can search.",
    body: `## 🧩 Feature Usage

Feature Usage tracks which modules each tenant is actively using, based on real activity rather than which features are simply enabled.

**Module adoption bars**

The top section shows adoption per module across the whole platform — useful for spotting a feature that's shipped but barely used.

**Per-tenant / module table**

Search by tenant name to see exactly which modules a specific account is using and how recently. Clicking a tenant name jumps straight to that tenant's detail page.`,
    tips: [
      "Low adoption on a recently-launched feature isn't necessarily a problem — check how long it's been enabled for those tenants first.",
      "Use this alongside the tenant detail page's feature usage panel when investigating a single account.",
    ],
    relatedIds: ["admin-tenants-dashboard-overview", "admin-tenant-detail"],
    pageIds: ["tenants-feature-usage"],
  },
  {
    id: "admin-tenant-detail",
    title: "Tenant detail page — usage, activity and admin notes",
    emoji: "🔍",
    module: "Admin",
    requiredPermission: "view_admin",
    summary: "The full picture for one tenant: usage against limits, feature adoption, recent events, and internal notes.",
    body: `## 🔍 Tenant detail

Opened by clicking a tenant from the Tenants Dashboard, this page combines everything platform operators need for one account.

**Usage & limits**

Bars show usage against each plan limit, colour-coded once a tenant approaches or exceeds a threshold.

**Feature usage**

A per-module activity count and last-used date, matching the wider Feature Usage page but scoped to this tenant only.

**Recent usage events**

The last 7 days of raw activity events, useful when investigating a specific incident or support ticket.

**Admin notes**

An internal-only notes thread for this tenant — record context like "upgraded plan after outreach call" or "flagged for repeated sync failures". Notes are visible to any platform operator with dashboard access, but only operators with note-management permission can add or delete them.`,
    tips: [
      "Admin notes are internal only — tenants never see them.",
      "Deleting a note is permanent; there's no recovery.",
    ],
    relatedIds: ["admin-tenants-dashboard-overview", "admin-service-peaks", "admin-feature-usage"],
    pageIds: ["tenants-dashboard-detail"],
  },

  // ── Channels ──────────────────────────────────────────────────────────────
  {
    id: "channels-overview",
    title: "Sales channels",
    emoji: "📡",
    module: "Channels",
    summary: "Define the channels your sales come through so reports and profit are correctly attributed.",
    body: `## 📡 Channels — where your sales come from

Channels represent the sales routes your business uses: eBay, Shopify, WooCommerce, TikTok Shop, In-person, Phone, etc.

**Why channels matter**

Every sale is tagged with a channel. This lets you:

- Filter sales and reports by channel
- See which platform generates the most profit
- Apply channel-specific rules (e.g. eBay postage hold applies only to eBay-tagged orders)

**Editing channels**

Go to **Channels** from the sidebar. You can rename default channels or add custom ones for niche platforms or one-off events.`,
    tips: [
      "Marketplace integrations (eBay, Shopify, etc.) auto-tag imported orders with the correct channel.",
      "For manual sales you pick the channel from a dropdown when creating the sale.",
    ],
    relatedIds: ["sales-overview", "reports-pl"],
    pageIds: ["channels"],
  },

  // ── Settings ──────────────────────────────────────────────────────────────
  {
    id: "settings-overview",
    title: "Settings and preferences",
    emoji: "⚙️",
    module: "Settings",
    requiredPermission: "edit_settings",
    summary: "Configure your business settings, branding, invoice templates, notifications and integrations.",
    body: `## ⚙️ Settings — make the system work your way

**General settings**

Set your business name, currency (default GBP), VAT number and company registration number — these appear on invoices.

**Branding**

Upload your logo and pick a brand colour. These are used on invoices, the customer-facing portal and notification emails.

**Invoice template**

Customise the invoice PDF: accent colour, footer text, whether to show payment details and company registration.

**Notifications**

Settings → Notifications Preferences lets each user choose how they want to be notified (email, push, in-app). Admins can set tenant-wide defaults under Notifications Defaults.

**eBay postage hold**

When enabled, eBay postage costs are held out of profit figures until orders ship. The hold window is configurable (default 4 hours, max 90 days).

**Live Stock CSV export columns**

Choose which columns appear when you export the inventory list to CSV from the Live view.`,
    tips: [
      "Changes to invoice branding take effect on the next invoice issued — existing invoices use a snapshot taken at finalisation time.",
      "Postage hold only applies to eBay orders — other channels always count postage immediately.",
    ],
    relatedIds: ["admin-overview", "integrations-overview"],
    pageIds: ["settings"],
  },

  // ── Client Area ──────────────────────────────────────────────────────────
  {
    id: "client-area-overview",
    title: "Using the MetalUI Client Area",
    emoji: "🏛️",
    module: "Client Area",
    requiredPermission: "tenant_account.view",
    summary: "Your account home — billing, invoices, usage, users, your plan, support and security all in one place.",
    body: `## 🏛️ Welcome to your Client Area

The Client Area is where you manage your *MetalUI account itself* — separate from the day-to-day inventory and sales work. It's your direct line to billing, your plan, your team and our support desk.

**What you'll find here**

| Section | What it's for |
|---------|---------------|
| Billing | Your outstanding balance, recent invoices and payments |
| Invoices | Every invoice — download a PDF or pay online |
| Usage & Limits | How much of your plan's allowances you've used |
| Users | Invite and manage the people who can access your account |
| Trial Status | Your trial countdown and what's included (trial accounts only) |
| Plan | Your current subscription and options to upgrade |
| Support | Raise and track support tickets with our team |
| Security | Your sign-in sessions, password and two-factor settings |

**Getting around**

Use the left-hand menu to move between sections. The overview page surfaces the headlines — outstanding balance, days left on a trial, anything that needs your attention — with quick links into the detail.

**Who can see what**

Sections respect your role. For example, only users with billing access see invoices, and only owners/admins can manage other users. If a section is missing from your menu, you don't currently have permission for it — ask an account owner to grant it.`,
    tips: [
      "The Client Area is about your MetalUI subscription — your inventory, sales and customers live in the main app menu.",
      "Owners and admins automatically have access to every Client Area section.",
      "Anything that needs action (an overdue invoice, an allowance nearing its limit, a trial ending soon) is flagged on the overview page.",
    ],
    relatedIds: [
      "client-area-billing",
      "client-area-plan-upgrade",
      "client-area-users",
      "client-area-support",
    ],
    pageIds: ["overview", "account-portal"],
  },

  {
    id: "client-area-billing",
    title: "Managing your billing",
    emoji: "💳",
    module: "Client Area",
    requiredPermission: "tenant_account.billing.view",
    summary: "See your outstanding balance, overdue amounts, next payment date, recent invoices and payments.",
    body: `## 💳 Your billing at a glance

The **Billing** page is your financial summary with MetalUI — what you owe, what's overdue, and what's been paid.

**The summary tiles**

- **Outstanding** — the total still to pay, plus how many invoices are open
- **Overdue** — anything past its due date (shown in red when there's a balance)
- **Next payment due** — the date your next invoice falls due
- **Account status** — your account standing and your payment terms (e.g. "30-day terms")

**Recent invoices & payments**

Below the tiles you'll see your most recent invoices and payments. Click any invoice number to open it, or **View all** to jump to the full Invoices or Payments list.

**Billing contacts**

If your account has billing contacts on file, they're listed here with the primary contact marked. These are the people we'll reach about anything billing-related.`,
    tips: [
      "An overdue balance shows in red — clear it from the Invoices page to keep your account in good standing.",
      "Need to change a billing contact? Raise a support ticket and we'll update it for you.",
      "Use 'View all' next to Recent invoices to reach the full list where you can pay or download PDFs.",
    ],
    relatedIds: [
      "client-area-invoices",
      "client-area-plan-upgrade",
      "client-area-overview",
    ],
    pageIds: ["billing"],
  },

  {
    id: "client-area-invoices",
    title: "Paying invoices",
    emoji: "🧾",
    module: "Client Area",
    requiredPermission: "tenant_account.billing.view",
    summary: "Download invoice PDFs and pay one or several outstanding invoices online with PayPal.",
    body: `## 🧾 Invoices — view, download and pay

The **Invoices** page lists every invoice on your account with its issue date, due date, total, balance and status.

**Opening an invoice**

Click an invoice number to open a detailed view — line items with quantity, unit price and VAT, plus subtotal, VAT, anything already paid and the balance due.

**Downloading a PDF**

Use the download icon on any row (or the **Download PDF** button in the detail view) to save an invoice for your records.

**Paying online**

1. Tick the checkbox next to each invoice you want to pay. Only invoices with an outstanding balance (issued, partially paid or overdue) can be selected.
2. Use the header checkbox to select **all** payable invoices at once.
3. The action bar shows how many you've selected and the total — click **Pay** to continue.
4. You're handed to **PayPal** to complete payment, then returned to your account where the payment is confirmed.

**If the Pay button is unavailable**

Online payment shows as disabled when it isn't set up for your account or you don't have permission to pay. You can still download the PDF and pay by your usual method, or contact support.`,
    tips: [
      "You can pay several invoices in a single PayPal checkout — just tick each one first.",
      "Only invoices with a remaining balance are selectable; fully-paid invoices are read-only.",
      "After paying, you're returned to your account automatically while we confirm the payment — no need to forward a receipt.",
    ],
    relatedIds: [
      "client-area-billing",
      "client-area-plan-upgrade",
      "client-area-overview",
    ],
    pageIds: ["invoices", "payments"],
  },

  {
    id: "client-area-usage-limits",
    title: "Understanding usage limits",
    emoji: "📈",
    module: "Client Area",
    requiredPermission: "tenant_account.view",
    summary: "Track how much of your plan's allowances you've used, with early warnings before you hit a cap.",
    body: `## 📈 Usage & Limits

The **Usage & Limits** page shows how much of your plan's allowances you've used so far — users, storage and other metered features — so there are no surprises.

**Reading each allowance**

Every allowance shows what you've used out of your limit, a percentage and a coloured progress bar:

| Status | Meaning |
|--------|---------|
| 🟢 Healthy | Comfortably within your limit |
| 🟠 Approaching limit | Getting close — worth keeping an eye on |
| 🟠 Limit reached | You've hit the cap for this allowance |
| 🔴 Over limit | You've exceeded the cap |

Allowances marked **Unlimited** have no cap and aren't tracked with a bar.

**When something needs attention**

If any allowance is near or over its limit, a banner appears at the top summarising which ones. From the **Need more headroom?** card you can:

- **Request a higher limit** — sends a request to our team (when available for your account)
- **Upgrade your plan** — move to a plan with bigger allowances
- **Contact support** — talk it through with us

The page also shows when usage was last calculated.`,
    tips: [
      "Approaching/over-limit allowances are summarised in a banner at the top so you can act early.",
      "'Request a higher limit' only appears when your account is allowed to ask — otherwise an account admin can request it.",
      "If an allowance shows 'Unlimited', there's no cap on it for your plan.",
    ],
    relatedIds: [
      "client-area-plan-upgrade",
      "client-area-trial",
      "client-area-overview",
    ],
    pageIds: ["usage"],
  },

  {
    id: "client-area-trial",
    title: "Managing your trial",
    emoji: "⏳",
    module: "Client Area",
    requiredPermission: "tenant_account.view",
    summary: "Track days left on your free trial, see what's included, request more time and convert to a paid plan.",
    body: `## ⏳ Trial Status

If your account is on a free trial, the **Trial Status** page shows how much time you have left and what happens next.

**The countdown**

A headline tells you how many days remain (or that your trial ends today / has ended), with a progress bar from your start date to your end date. The status badge reads **Trial active**, **Ending soon**, **Trial expired** or **Converted to a paid plan**.

**Keeping your access**

The **Keep your access** card shows the plan you'd move to, with monthly and yearly pricing. Click **Upgrade your plan** any time to convert and keep everything you're using.

**Need more time?**

Use the **Need more time?** card to request a trial extension — pick 7, 14 or 30 extra days and send it to our team. You'll see a "request pending" note while we review it, and only one request can be open at a time.

**What's included & usage so far**

Further down you'll see the features included in your trial and a snapshot of how much of your allowances you've used, with a link to the full Usage & Limits page.

If your account isn't on a trial, this page simply links you to your plan.`,
    tips: [
      "Upgrade before your trial ends to avoid any interruption to your access.",
      "You can only have one extension request open at a time — we'll be in touch about it.",
      "The usage snapshot here is a summary; open Usage & Limits for the full picture.",
    ],
    relatedIds: [
      "client-area-plan-upgrade",
      "client-area-usage-limits",
      "client-area-overview",
    ],
    pageIds: ["trial"],
  },

  {
    id: "client-area-users",
    title: "Creating and managing users",
    emoji: "👥",
    module: "Client Area",
    requiredPermission: "tenant_account.users.view",
    summary: "Invite, create and manage the people who can access your account — within your plan's seat limit.",
    body: `## 👥 Users

The **Users** page lets account owners and admins manage who can access your MetalUI account.

**Adding someone**

- **Invite by email** — they receive an invitation to set up their own sign-in.
- **Create directly** — set someone up straight away.

When you add a user you choose their **role**, which controls what they can see and do.

**Seat limits**

Your plan includes a set number of user seats. The page shows how many you've used and how many remain. If you're at your limit, you'll need to remove a user or upgrade your plan before adding another.

**Managing existing users**

From each user's menu you can:

- **Change role** — move someone between the available role presets
- **Suspend / reactivate** — temporarily block or restore access
- **Reset access** — force a sign-out everywhere (useful if a device is lost)
- **Remove** — revoke access entirely

**Pending invitations** appear in their own list until accepted, and you can resend or cancel them.`,
    tips: [
      "Watch the seat counter — if you're at your limit, upgrade your plan or remove a user before inviting another.",
      "Suspend keeps the user on your account but blocks access; remove revokes it completely.",
      "'Reset access' signs the person out of every device — handy if a laptop or phone goes missing.",
    ],
    relatedIds: [
      "client-area-security",
      "client-area-plan-upgrade",
      "client-area-overview",
    ],
    pageIds: ["users"],
  },

  {
    id: "client-area-support",
    title: "Submitting support tickets",
    emoji: "🛟",
    module: "Client Area",
    requiredPermission: "tenant_account.support.view",
    summary: "Raise tickets with the MetalUI team, track replies, attach files, and rate the help you got.",
    body: `## 🛟 Support

The **Support** page is your direct line to the MetalUI team. Raise a ticket, follow the conversation and attach anything that helps us help you.

**Raising a ticket**

1. Start a new ticket and give it a clear subject and description.
2. Pick a **category** (billing, technical issue, integration issue, login/access, bug report, training, account change, urgent outage, and more).
3. Choose a **priority** — Low, Normal, High or Urgent.
4. Attach screenshots or files if they'll help, then submit.

**Following a ticket**

Your tickets are listed and filtered by status. Open one to see the full thread, including replies from our team. You can reply, add more attachments, and download anything we send back.

**Closing, reopening and rating**

When your issue is sorted you can **close** the ticket — and **reopen** it later if it crops up again. After resolution you can leave a **rating** to tell us how we did.`,
    tips: [
      "Set priority to Urgent only for genuine outages — it helps us triage the most pressing issues first.",
      "Attach a screenshot whenever you can; it's often the fastest way for us to understand the problem.",
      "Closed a ticket too soon? You can reopen it rather than starting a brand-new one.",
    ],
    relatedIds: ["client-area-overview", "client-area-billing"],
    pageIds: ["support"],
  },

  {
    id: "client-area-plan-upgrade",
    title: "Upgrading your plan",
    emoji: "⬆️",
    module: "Client Area",
    requiredPermission: "tenant_account.view",
    summary: "See your current subscription and request an upgrade, downgrade, renewal or cancellation.",
    body: `## ⬆️ Plan

The **Plan** page shows your current subscription and the options available to you.

**Your current plan**

At the top you'll see your plan name, status (Active, Trial, Past due, Cancelled, Expired or No plan) and the allowances included with it.

**Switching plans**

Other plans are listed and labelled by how they relate to yours — **Your plan**, **Upgrade**, **Downgrade** or **Switch** — each with its pricing. Choose a plan and confirm to send a request.

**How changes are handled**

Most plan changes — downgrades, renewals and cancellations — are submitted to the MetalUI team as a **request**, and you can add a short note explaining what you need. Our team reviews and applies these for you.

When **instant online upgrades** are available for your account, an upgrade can be confirmed straight away rather than waiting for review — the page will tell you which applies before you confirm.

**Why upgrade**

Upgrading is the way to lift your usage allowances (more users, more storage) when you're approaching or over a limit, or to unlock additional features.`,
    tips: [
      "Some upgrades can be confirmed instantly; other changes are requests our team reviews and applies — the page tells you which applies.",
      "Add a note to your request if there's context that'll help us set you up correctly.",
      "Bumping into usage limits? Upgrading is usually the cleanest fix — check Usage & Limits first.",
    ],
    relatedIds: [
      "client-area-usage-limits",
      "client-area-billing",
      "client-area-trial",
    ],
    pageIds: ["plan"],
  },

  {
    id: "client-area-security",
    title: "Security and SSO",
    emoji: "🔒",
    module: "Client Area",
    requiredPermission: "tenant_account.view",
    summary: "Manage your sign-in, password and two-factor settings, review active sessions and security activity.",
    body: `## 🔒 Security

The **Security** page lets you manage the security of *your own* sign-in and see recent activity on your account.

**Password & two-factor authentication**

Your password and two-factor authentication (2FA) are managed in your account profile. The Security page links you straight there so you can update your password or turn on 2FA — the single strongest thing you can do to protect your account.

**Active sessions**

You'll see the devices and browsers currently signed in to your account, with location and last-active details where available. You can:

- **Sign out this session** — end a single device's access
- **Sign out everywhere else** — end every session except the one you're using now

Both actions are recorded.

**Security activity**

A log shows recent security events on your account — sign-ins, password changes, 2FA changes and session sign-outs — so you can spot anything unexpected.

**How sign-in works (SSO)**

Sign-in is handled by a secure identity provider rather than a password stored in the app itself. That means one trusted login protects your access across MetalUI — and if your organisation uses single sign-on (SSO), it applies through that same provider.`,
    tips: [
      "Turning on two-factor authentication is the single best step you can take to secure your account.",
      "See a session you don't recognise? Sign out 'everywhere else', then change your password.",
      "Password and 2FA changes are made in your account profile — the Security page links you straight there.",
    ],
    relatedIds: ["client-area-users", "client-area-overview"],
    pageIds: ["security"],
  },
  // AI Product Copy Generator (Task #1546)
  {
    id: "ai-product-copy-overview",
    emoji: "✨",
    module: "AI Product Copy",
    title: "AI Product Copy Generator — Overview",
    summary:
      "Use AI to generate accurate, brand-consistent product titles and descriptions directly from a product fact-sheet. Copy can be previewed, edited, then applied to the description editor in one click.",
    body: `## What is the AI Product Copy Generator?

The AI Product Copy Generator creates ready-to-use listing copy for your precious-metals inventory based on structured product facts you supply. It uses a language model (OpenAI, via Replit AI Integrations) tuned for UK bullion products — so it understands grams, fineness, mints, and UK English spelling conventions.

**It never invents facts.** If a fact (like weight or purity) is missing from the form, the model notes it in the output rather than guessing. Any missing facts are listed in the "Missing facts" badge section of the output.

## How to use it

1. Open an inventory item and go to the **Description** tab.
2. Click **AI Product Copy Generator** to expand the panel.
3. Fill in the product fact-sheet (metal type, weight, fineness, mint, condition, year, and any extra context).
4. Click **Generate copy**.
5. Review the output: recommended title, alternative title, short description, full description, SEO meta-title and meta-description, and suggested specifications.
6. Use the **Apply to editor** button to drop the full description into the description editor, then save normally.
7. Use the **Copy** icon next to any field to copy that field's text to the clipboard.

## Titles

The recommended title is up to 80 characters and optimised for marketplace search. An alternative title is also generated for variety or A/B testing. If you have the right permission, the **Apply title** button writes it directly to the product title field.

## Copy profiles

Administrators can create **Copy profiles** in Settings → AI Product Copy. A profile stores brand voice settings (tone, banned terms, required terms, extra instructions) and can be set as the default for all generations. Profiles can target a specific marketplace (e.g. eBay or Shopify).

## Approvals

If the tenant has **Require approval** enabled in settings, a generation must be approved by an editor before its copy can be applied. Approval is recorded in the audit log.

## SEO fields

Alongside the listing copy, the generator produces a meta title (≤60 chars) and meta description (≤160 chars) suitable for Shopify or WooCommerce SEO fields. These are shown in the output panel but are not applied automatically — copy them manually.

## Which model is used?

The default model is **gpt-4o-mini**. Administrators can change the default model in Settings → AI Product Copy → Model. All generations are logged with their token usage so you can monitor costs.`,
    tips: [
      "Add as many facts as possible — the model cites which facts it used and which were missing.",
      "Check the 'Warnings' section in the output for any editorial concerns the model flagged.",
      "For consistent brand voice, create a Copy Profile in Settings → AI Product Copy and make it the default.",
      "SEO meta fields are generated alongside the copy — copy them manually into your Shopify or WooCommerce SEO fields.",
      "The feature must be enabled in Feature Management before the panel appears.",
    ],
    relatedIds: ["ai-product-copy-profiles", "ai-product-copy-history"],
    pageIds: ["inventory"],
  },
  {
    id: "ai-product-copy-profiles",
    emoji: "🎨",
    module: "AI Product Copy",
    title: "AI Product Copy — Copy Profiles",
    summary:
      "Copy profiles store brand voice settings (tone, banned and required terms, extra instructions) and can be applied at generation time to produce consistent, on-brand product descriptions.",
    body: `## What is a copy profile?

A copy profile is a saved set of brand writing guidelines that the AI applies when generating product copy. One profile can be marked as the **default** — it is automatically used for every generation unless the operator overrides it at generate time.

## Profile fields

| Field | Description |
|---|---|
| Name | A label shown in the profile selector. |
| Tone | How the copy should sound — e.g. "professional and informative", "premium and exclusive". |
| Target marketplace | Optional: eBay, Shopify, WooCommerce, etc. Tailors SEO and wording to the platform. |
| Banned terms | Words or phrases the model must never use in the output. |
| Required terms | Words or phrases the model should include where relevant. |
| Extra instructions | Freeform brand guidelines — e.g. "Always mention that we are a UK-based LBMA dealer." |
| Default | When ticked, this profile is pre-selected for every generation. |

## Managing profiles

Go to **Settings → AI Product Copy → Profiles**. Owner and administrator roles can create, edit, and archive profiles. Archiving a profile removes it from the selector but does not delete any past generations that referenced it.

## Version history

Every save to a profile creates a version snapshot. If you need to see what a profile looked like at the time of an older generation, the profile version ID is stored on the generation record.`,
    tips: [
      "Marking a profile as Default avoids having to select it each time.",
      "Banned terms are enforced by the model prompt, not by post-processing — so very creative workarounds may still slip through. Review all copy before publishing.",
      "Archiving a profile is safer than deleting — it preserves the link from old generations.",
    ],
    relatedIds: ["ai-product-copy-overview", "ai-product-copy-history"],
    pageIds: ["settings"],
  },
  {
    id: "ai-product-copy-history",
    emoji: "📋",
    module: "AI Product Copy",
    title: "AI Product Copy — Generation History",
    summary:
      "Every generation is stored against the inventory item, including the fact-sheet inputs, all output fields, token usage, and the profile version used. Administrators can review and compare past generations.",
    body: `## Generation records

Each time the AI generates copy for an item, a **generation record** is created. It stores:

- The fact-sheet inputs supplied by the operator.
- The recommended title, alternative title, short description, full description, SEO fields, and specifications.
- The model used and token consumption.
- The time taken (duration).
- Any warnings or missing facts the model noted.
- The copy profile version, if one was selected.
- The operator who triggered the generation.

## Status lifecycle

| Status | Meaning |
|---|---|
| draft\_generated | Generated and awaiting review (default). |
| approved | An editor has approved the copy. |
| applied | The title or description has been applied to the product. |
| error | The AI call or JSON parse failed; the error message is stored. |

## Approval gate

If **Require approval** is enabled in settings, a generation cannot be applied until an editor marks it Approved. Both the approval and the apply actions are written to the audit log.

## Viewing history

Open the **AI Product Copy Generator** panel on any inventory item's Description tab, then click the **History** tab. The list shows every generation for that item, newest first.

- **Expand** any row to see the full output (titles, description, SEO, specifications).
- **Approve** a generation (requires the \`ai.product_copy.approve\` permission) to mark it editorially reviewed.
- **Apply title** or **Apply to editor** to push the copy directly into the item form.
- **Re-run with these inputs** pre-fills the Generate tab with the fact-sheet from that generation so you can tweak and regenerate without re-typing everything.`,
    tips: [
      "Every generation is stored — so even if you don't use the output immediately, it is there if you come back later.",
      "The 'Facts omitted' list tells you what extra detail would improve the copy on a re-run.",
      "Token usage is logged per generation — check Settings → AI Product Copy → Usage for a monthly summary.",
    ],
    relatedIds: ["ai-product-copy-overview", "ai-product-copy-profiles"],
    pageIds: ["settings", "inventory"],
  },

  // ── Dynamic Pricing & Margin Protection ──────────────────────────────────
  {
    id: "pricing-overview",
    title: "Introduction to Dynamic Pricing & Margin Protection",
    emoji: "⚖️",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "How the rules engine, margin floor, approvals and marketplace publishing fit together — keywords: repricing, automated pricing, price engine.",
    body: `## ⚖️ Prices that keep up — without losing your margin

Dynamic Pricing keeps your asking prices in step with the live metal price, your stock position and how fast items sell — using **rules you write** and a **margin floor that always holds**.

**The moving parts**

1. **Rules** — each rule computes a suggested price for the items in its scope (spot markup, stock level, sales velocity, marketplace adjustment, margin target, competitor price, customer group, dropship markup, or a manual override).
2. **Margin protection** — every suggestion is clamped so it can never fall below your minimum margin over landed cost, or below the item's own minimum acceptable price. This guard applies to *every* rule, including manual overrides.
3. **Approvals or auto-apply** — a suggested change either queues for a human to approve, or (if auto-apply is switched on for the rule or tenant) is written straight to the item.
4. **Publishing** — an approved or auto-applied price is written to the item's asking price and pushed to its connected marketplaces (eBay, Shopify, WooCommerce, TikTok Shop) through the same paths a manual edit uses.
5. **Run logs** — every pass (manual or scheduled) is recorded: rules evaluated, items scanned, changes proposed, auto-applied and queued.

**When it runs**

The engine runs automatically at most **once every 6 hours** while the master "Engine enabled" switch is on. You can also press **Run now** at any time — a manual run ignores the schedule throttle.`,
    tips: [
      "Nothing changes a price without leaving a trace: every proposal appears in Approvals and every pass appears in Run history.",
      "Start with auto-apply OFF — you'll see exactly what the engine would do before letting it act alone.",
      "The engine only reprices items in a rule's scope; items with no matching rule are never touched.",
    ],
    relatedIds: ["pricing-min-margin-rules", "pricing-recommendations", "pricing-approvals"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-cost-basis",
    title: "Cost basis: what 'cost' means in pricing",
    emoji: "🧮",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "The engine prices from landed unit cost — what you actually paid to get the item on the shelf, including freight and duty.",
    body: `## 🧮 Every margin starts with the right cost

Dynamic Pricing works from each item's **landed unit cost** — the figure recorded on the inventory item, which should include the purchase price plus any freight, import duty and other costs of getting it to you.

**Why it matters so much**

- The **margin floor** is calculated as a markup over landed cost. If the cost is wrong, the floor is wrong — a cost recorded too low lets the engine price dangerously close to a loss.
- **Margin target** and **dropship markup** rules compute their price directly from the cost.
- The margin % shown on each proposed change is measured against this cost.

**Items with no cost**

If an item has no landed cost recorded (£0), the cost-based part of the floor is zero — only the item's own **minimum acceptable price** protects it. Rules that need a cost (margin target, dropship markup) simply skip the item.

**Where the cost comes from**

Landed cost is set on the inventory item, ideally via a received purchase order so freight and duty are allocated correctly.`,
    tips: [
      "Give every precious-metal item a minimum acceptable price even when the cost is accurate — it's your independent backstop.",
      "Editing a landed cost changes the floor from the next repricing pass onwards.",
      "Consignment items: the 'cost' is what you'll owe the supplier — keep it realistic or the floor misleads.",
    ],
    relatedIds: ["pricing-true-profit", "pricing-min-margin-rules", "inventory-overview"],
    pageIds: ["pricing", "inventory"],
  },
  {
    id: "pricing-true-profit",
    title: "True profit — a worked example",
    emoji: "💰",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "True profit is what's left after every cost — item cost, marketplace fee, postage and affiliate payout — not just sale price minus cost.",
    body: `## 💰 What did you *really* make on that sale?

**True profit** counts every cost of the sale, not just what you paid for the item.

**Worked example**

| | |
|---|---|
| Sale price | £50.00 |
| Item cost (landed) | −£35.00 |
| Marketplace fee | −£5.00 |
| Postage | −£3.00 |
| Affiliate payout | −£0.60 |
| **True profit** | **£6.40** |

That's a **true margin of 12.8%** on the sale price — even though the raw markup over cost (£50 vs £35) looks like a healthy 43%.

**How this relates to the pricing engine**

The engine's margin floor works on **markup over landed cost** — a deliberately simple, always-available guard. Fees, postage and affiliate costs vary by channel and order, so they are reflected in your **reports** (which compute realised profit after fees and postage) rather than in the floor itself. Cover them by:

- setting your **minimum margin %** high enough to absorb typical channel fees, or
- using a **marketplace adjustment** rule to lift prices on high-fee channels.`,
    tips: [
      "Rule of thumb: minimum margin % ≥ your typical fee % + postage % + the profit you actually want.",
      "The margin shown on a proposed change is markup over landed cost — read it with your channel's fees in mind.",
      "Your P&L reports show realised profit after fees and postage, so you can check that pricing is delivering true profit.",
    ],
    relatedIds: ["pricing-margin-vs-markup", "pricing-marketplace-fees", "reports-pl"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-margin-vs-markup",
    title: "True margin vs markup vs gross margin",
    emoji: "📐",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Three different percentages that all get called 'margin' — and which one each screen in MetalUI uses.",
    body: `## 📐 Three percentages, one word

**Markup (over cost)** — profit relative to *cost*. £35 cost sold at £50 = (50−35)/35 = **43% markup**. This is what the pricing engine's margin floor and the margin column on approvals use.

**Gross margin** — profit relative to *sale price*, before fees. £15 profit on £50 = **30% gross margin**.

**True margin** — profit relative to sale price after configured costs such as marketplace fees, postage, affiliate payouts and consignment costs. In the worked example (£6.40 on £50) that's **12.8%**.

**Why the engine uses markup over cost**

Cost is known before the sale; fees and postage aren't (they depend on channel, service, order size). A markup-over-cost floor can therefore be enforced *before* a price ever goes live.

**Quick conversion**

- 25% markup ≈ 20% gross margin
- 50% markup ≈ 33% gross margin
- 100% markup = 50% gross margin`,
    tips: [
      "When someone says 'we need 20% margin', ask: of cost or of sale price? They differ more the higher they get.",
      "The margin % on the Approvals tab is markup over landed cost.",
      "Reports show realised margins after fees — that's where 'true margin' lives.",
    ],
    relatedIds: ["pricing-true-profit", "pricing-min-margin-rules", "pricing-glossary"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-min-margin-rules",
    title: "Minimum margin rules & the margin floor",
    emoji: "🛡️",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "The minimum-margin guard clamps every suggested price so it can never breach your margin floor or an item's minimum acceptable price.",
    body: `## 🛡️ The guard that never sleeps

Every price the engine suggests is clamped to the **margin floor** before anything else happens:

**Floor = the higher of**

1. **Landed cost + minimum margin %** — e.g. £35 cost with a 15% minimum margin gives a £40.25 floor.
2. **The item's minimum acceptable price** — a per-item hard limit you set on the inventory item.

**Where the minimum margin % comes from**

- The tenant-wide default lives in **Pricing Settings → Min margin %**.
- Each rule can set its own **Min margin %** that overrides the default for items it prices.

**It applies to everything**

Spot rules, competitor undercuts, slow-mover discounts, even **manual override rules** — all are clamped. If a rule computes £38 but the floor is £40.25, the proposed price becomes £40.25 (and the reason on the approval explains what happened).

**After rounding, too**

Rounding strategies like "end in .99" are re-checked against the floor, so rounding can never dip a price below it.`,
    tips: [
      "A proposed price sitting exactly at an odd figure like £40.25 is usually the floor doing its job — check the reason column.",
      "Rules pricing high-fee channels deserve a higher per-rule minimum margin than your default.",
      "The floor protects downwards only — it never pushes a price up on its own; a rule must propose the change.",
    ],
    relatedIds: ["pricing-hard-floor", "pricing-cost-basis", "pricing-settings"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-hard-floor",
    title: "Hard floor, approvals and safe prices",
    emoji: "🚧",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "The hard floor is the lowest price MetalUI allows; anything above it may still need approval before publishing. How the two safety layers differ.",
    body: `## 🚧 Two layers of protection, one clear rule

**Layer 1 — the hard floor (automatic, cannot be bypassed by rules)**

The hard floor is the **lowest price MetalUI allows** for an item: the higher of cost-plus-minimum-margin and the item's minimum acceptable price. The engine never proposes below it — a below-floor candidate is silently raised *to* the floor. There is no "override the floor" switch inside a rule; the only ways to move it are to change the minimum margin %, the item's minimum acceptable price, or its recorded cost — all permission-controlled, audited actions.

**Layer 2 — approval (human sign-off before publishing)**

A price that clears the floor still doesn't go live automatically unless auto-apply is on. With auto-apply off (the default), each change queues on the **Approvals** tab showing current price, proposed price, resulting margin and the rule's reasoning — an operator with the approve permission signs it off or rejects it.

**Minimum safe price**

Each proposed change records the **minimum acceptable price** it was clamped against, so an approver can see how close to the floor a change sits.

**In short**

- *Blocked outright*: below the hard floor — never proposed.
- *Needs sign-off*: everything else, until you enable auto-apply.
- *Automatic*: only when auto-apply is on for the rule or tenant.`,
    tips: [
      "If a discount rule seems to 'not work', it's probably hitting the floor — the reason on the proposal will show the clamped result.",
      "Keep auto-apply off for downward-moving rules (competitor, slow-mover) until you trust their behaviour.",
      "Raising the tenant minimum margin instantly raises every item's floor on the next run.",
    ],
    relatedIds: ["pricing-min-margin-rules", "pricing-approvals", "pricing-overrides"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-overrides",
    title: "Manual overrides and the audit trail",
    emoji: "✍️",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Manual override rules always beat computed rules — but even they can't cross the margin floor. Everything is recorded.",
    body: `## ✍️ When you know better than the maths

A **manual override** rule pins a fixed price on the items in its scope. Overrides sit at the top of the pecking order:

- If any enabled manual override matches an item, **it wins** over every computed rule, regardless of priority numbers.
- If several overrides match, the one with the **highest priority** wins.
- Even an override is **clamped to the margin floor** — you cannot use one to price below cost-plus-minimum-margin or below the item's minimum acceptable price.

**The audit trail**

- Every proposed change stores the rule that produced it, the reasoning, the before/after prices and the margin.
- Approvals record **who** approved or rejected, and when.
- Every engine pass is logged in **Run history** with counts and any errors.
- Price pushes to marketplaces reuse the standard sync paths, so channel-side history matches too.

**Overrides vs editing the item directly**

Editing the asking price on the inventory item is a one-off change the engine may later reprice. A manual override *rule* keeps re-asserting its price every run until you disable it.`,
    tips: [
      "Use an item-scoped override to freeze a price during a promotion — delete or disable it afterwards.",
      "An override that seems ignored is usually clamped by the floor — check the proposal's reason.",
      "Overrides still respect min-change: if the pinned price equals the current price, no proposal is raised.",
    ],
    relatedIds: ["pricing-rule-priority", "pricing-hard-floor", "pricing-history"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-marketplace-fees",
    title: "Marketplace fees and channel costs",
    emoji: "🏪",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Each channel takes a different bite — how to reflect eBay, Shopify, TikTok Shop and other fees in your pricing rules.",
    body: `## 🏪 Same item, different channel, different economics

A £50 sale is not worth the same everywhere: eBay final-value fees, TikTok Shop commission and affiliate payouts, payment processing on Shopify — each channel has its own cost profile.

**How to price for fees in MetalUI**

The engine doesn't maintain a named per-channel fee table; instead you encode fee differences into rules:

1. **Marketplace adjustment rules** — add a percentage on top of the base price for a specific channel (e.g. +8% for a high-fee marketplace). The channel is recorded on each proposed change.
2. **Per-rule minimum margin** — give rules for high-fee channels a higher Min margin % so the floor itself absorbs the fee.
3. **Channel-scoped rules** — any rule type can be scoped to a channel so its maths only applies there.

**Where actual fees show up**

Recorded fees on marketplace sales feed your P&L and profit reports — that's where you verify the adjustment percentages you chose actually preserve true profit.`,
    tips: [
      "Review your realised per-channel margins in Reports quarterly and re-tune the adjustment percentages.",
      "Remember postage: a 'free postage' channel effectively adds another 5–10% cost on small items.",
      "TikTok Shop affiliate commission is a real cost of sale — include it when sizing the channel uplift.",
    ],
    relatedIds: ["pricing-channel-pricing", "pricing-true-profit", "reports-pl"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-spot-pricing",
    title: "Spot-based pricing for precious metals",
    emoji: "🥇",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Spot price markup rules: price = live spot × item weight + your markup. What happens when the spot feed is stale or down.",
    body: `## 🥇 Prices that move with the metal

A **spot price markup** rule computes:

**price = spot (£/troy oz) × item weight (in troy oz) × (1 + markup %)**

- Spot prices for **gold, silver and platinum** are fetched live in GBP per troy ounce (palladium is not currently fed to the engine).
- The item's **metal** and **weight in grams** must be recorded — weight is converted to troy ounces (31.1035 g/oz) automatically.
- You can pin the rule to a specific metal or let it use each item's own metal field.

**Stale or missing spot prices**

The spot feed is cached briefly and fetched fresh for each run. If the feed is unavailable, the engine does **not** guess or reuse an old price for pricing decisions — spot rules simply skip that pass (logged as "no spot price"), leaving current prices untouched, and other rule types still run. Prices update again on the next successful run.

**Example**

1oz silver coin, spot £24.80/oz, markup 22% → £30.26 suggested, then floor-checked, change-capped and rounded like any other suggestion.`,
    tips: [
      "Items without a metal or weight are skipped by spot rules — fill both fields for anything you want spot-priced.",
      "Set the markup to cover your premium over spot INCLUDING channel fees — the rule prices the metal, not the fees.",
      "The engine runs at most every 6 hours on schedule; press Run now after a big spot move to reprice immediately.",
    ],
    relatedIds: ["pricing-premium-collectible", "pricing-change-limits", "pricing-overview"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-premium-collectible",
    title: "Pricing premium and collectible items",
    emoji: "💎",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Numismatic and collectible stock shouldn't track spot — how to keep the engine's hands off, or on, the right items.",
    body: `## 💎 When the metal is the least valuable part

A graded sovereign or a key-date coin is worth its collector premium, not its melt value. Spot-tracking such items would be a costly mistake.

**Strategies**

1. **Keep them out of spot rules** — scope spot rules to a "Bullion" category and keep collectibles in their own category with no spot rule.
2. **Minimum acceptable price** — set it at your researched collector value; even if a broad rule catches the item, the floor holds at that value.
3. **Manual override rules** — pin an exact price per item; the override beats every computed rule.
4. **Competitor price rules** — for items with a visible market price, match or undercut a researched competitor price instead of tracking spot.

**Mixed inventory tip**

Run bullion on spot rules with auto-apply, and collectibles on overrides/competitor rules with approval required — the same engine handles both safely because scope and priority keep them apart.`,
    tips: [
      "Category scoping is the cleanest separator — organise inventory into bullion vs collectible categories first.",
      "A minimum acceptable price on every collectible is cheap insurance against a mis-scoped rule.",
      "Slow-mover discount rules are dangerous on collectibles — rarity means slow sales are normal, not a pricing problem.",
    ],
    relatedIds: ["pricing-spot-pricing", "pricing-overrides", "pricing-rule-priority"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-channel-pricing",
    title: "Channel-specific pricing",
    emoji: "🛰️",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Scope rules to a channel so eBay, Shopify, WooCommerce and TikTok Shop each get pricing that fits their fees and buyers.",
    body: `## 🛰️ One catalogue, per-channel economics

Rules can be scoped **all / category / item / channel**. A channel-scoped rule computes against the item's base price like any other, and the channel is recorded on each proposed change so approvers can see which marketplace it targets.

**Typical setups**

- **Marketplace adjustment (+%) per channel** — the workhorse for fee differences.
- **Channel-scoped margin target** — enforce a higher target margin on a high-cost channel.
- **Channel-scoped competitor rules** — undercut where you compete on price, hold margin where you don't.

**How the price reaches the channel**

When a change is approved (or auto-applies), the new asking price is written to the item and pushed to **every marketplace the item is synced to** through the standard auto-push paths — eBay, Shopify, WooCommerce and TikTok Shop.

**Note on one-price-per-item**

The item's asking price is a single figure; channel rules shape *which proposal wins and why*, and the recorded channel documents the intent. If you need permanently different figures per channel, keep channel-specific listings/items.`,
    tips: [
      "The Channel column on Approvals tells you which marketplace a proposal was computed for.",
      "An item only pushes to channels it's actually synced to — check the item's channel toggles if a price didn't propagate.",
      "Pair each channel rule with a per-rule minimum margin that reflects that channel's fees.",
    ],
    relatedIds: ["pricing-marketplace-fees", "pricing-push-failures", "pricing-approvals"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-recommendations",
    title: "How price recommendations are produced",
    emoji: "💡",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "The pipeline behind every suggestion: rule maths → margin floor → change cap → rounding → minimum-change filter.",
    body: `## 💡 From rule to recommendation, step by step

For each item, the engine:

1. **Finds matching rules** — enabled rules whose scope covers the item.
2. **Picks the winner** — manual overrides first; otherwise highest priority rule that produces a usable price.
3. **Computes the raw candidate** — the rule's own maths (spot × weight, cost + target margin, base ± %, etc.).
4. **Clamps to the margin floor** — never below cost-plus-min-margin or the item's minimum acceptable price.
5. **Caps the move** — the price can't move more than Max change % from the current price in one run.
6. **Rounds** — per the rounding strategy (none, nearest £1, .99, .95), then re-checks the floor.
7. **Filters trivial changes** — if the result differs from the current price by less than Min change (£), no recommendation is raised and the next rule down gets a try.

**What you see**

Each recommendation shows the item, the rule, current vs proposed price, the resulting margin %, and a human-readable reason (e.g. "Spot gold £1,890.20/oz × 1.000oz +18%").

**Then what?**

Auto-apply on → applied immediately and logged. Auto-apply off → queued on the Approvals tab for sign-off.`,
    tips: [
      "The reason string is your friend — it shows the exact inputs the rule used.",
      "No recommendation for an item usually means: no matching rule, missing inputs (weight, cost, spot), or the change was smaller than Min change (£).",
      "A recommendation exactly at the floor means the rule wanted to go lower and was clamped.",
    ],
    relatedIds: ["pricing-rule-priority", "pricing-change-limits", "pricing-approvals"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-approvals",
    title: "The approval and publishing flow",
    emoji: "✅",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Approve or reject proposed price changes, and what each status — pending, approved, applied, auto-applied, rejected, failed, superseded — means.",
    body: `## ✅ Sign-off before prices go live

Unless auto-apply is on, every recommendation waits on the **Approvals** tab.

**Approving** (needs the approve permission) writes the new asking price to the item and pushes it to the item's connected marketplaces. **Rejecting** discards the proposal — the engine may propose again on a later run if conditions still hold.

**Statuses**

| Status | Meaning |
|--------|---------|
| pending | Awaiting a decision |
| approved | Signed off, being applied |
| applied | Price written and pushed |
| auto_applied | Applied automatically by an auto-apply rule |
| rejected | Declined by an operator |
| failed | The apply or marketplace push errored — see the reason column |
| superseded | A newer proposal for the same item replaced this one |

**Auto-apply**

Auto-apply can be enabled per **rule** or tenant-wide in Settings (a rule's own setting wins). Auto-applied changes still create a record with full reasoning, so the audit trail is identical.

**Who can do what**

Viewing needs pricing view access; approving/rejecting needs the approve permission; enabling auto-apply lives with settings/rule managers.`,
    tips: [
      "Old pending items are superseded automatically when the engine re-proposes for the same item — you only ever act on the latest.",
      "A failed status with an error mentioning a marketplace means the price saved locally but the channel push needs attention.",
      "Use the margin column to sanity-check a proposal before approving — it's markup over landed cost.",
    ],
    relatedIds: ["pricing-hard-floor", "pricing-push-failures", "pricing-history"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-change-limits",
    title: "Price-change limits: caps, minimum change and rounding",
    emoji: "🎚️",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Max change % stops runaway moves, Min change (£) stops penny-shuffling, and rounding keeps prices tidy — how the three interact.",
    body: `## 🎚️ Smooth moves only

**Max change %** — a proposal can't move the price more than this percentage from the current price in a single run (set 0 to disable the cap). A big spot move therefore reaches its target over several runs rather than in one jump. Each rule can override the tenant default.

**Min change (£)** — changes smaller than this are dropped entirely; nobody wants an approval queue full of 3p adjustments. If the winning rule's change is too small, the next rule in priority order gets a chance.

**Rounding strategy** — none (to the penny), nearest £1, end in .99, or end in .95. Rounding runs *after* the floor and cap, and the floor is re-asserted afterwards so .99/.95 rounding can never dip below it.

**Order of operations**

raw candidate → margin floor → max-change cap → rounding → floor re-check → min-change filter.

**Interplay to know about**

A tight max-change cap plus a large min-change threshold can leave prices stuck (each capped step is too small to clear the threshold). If prices stop moving, loosen one of the two.`,
    tips: [
      "During volatile weeks, a 10–15% max change keeps spot-tracking orderly while still converging within a day or two of runs.",
      "Min change (£) of 0.50–1.00 keeps the approvals queue meaningful on low-value stock.",
      "Rounding to .99 on bullion looks retail; nearest £1 reads cleaner for high-value pieces — it's per rule if you want both.",
    ],
    relatedIds: ["pricing-recommendations", "pricing-settings", "pricing-spot-pricing"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-ageing-demand",
    title: "Ageing stock and demand-based rules",
    emoji: "⏳",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Use sales-velocity and stock-level rules to discount slow movers, harvest margin on fast movers and react to stock depth.",
    body: `## ⏳ Let demand steer the price

**Sales velocity rules** watch units sold over a 7, 30 or 90-day window (cancelled and refunded orders don't count):

- **Fast movers** — at or above your fast threshold: apply an uplift (e.g. +5%) to harvest margin while demand is hot.
- **Slow movers** — at or below your slow threshold: apply a markdown (e.g. −8%) to get ageing stock moving. The 90-day window is the natural "ageing stock" detector: items with ~0 sales in 90 days get the nudge.

**Stock level rules** react to quantity on hand against a threshold:

- **Low stock** — scarce items can carry a premium (+%).
- **High stock** — deep positions can take a haircut (−%) to accelerate turnover.

**Safety still applies**

Markdowns are clamped to the margin floor, capped by Max change % per run and (unless auto-apply is on) queue for approval — a slow-mover rule can never discount into a loss.

**Combining them**

Give velocity rules lower priority than spot/margin rules for bullion, and higher priority for generic stock where turnover matters more than metal content.`,
    tips: [
      "Start markdowns gentle (−5%) — the rule re-fires each run, compounding until the floor or a sale stops it.",
      "Exclude collectibles from slow-mover rules; slow is normal for rare items.",
      "Watch the 30d vs 90d windows: 30d reacts faster, 90d is calmer and better for ageing-stock sweeps.",
    ],
    relatedIds: ["pricing-premium-collectible", "pricing-rule-priority", "pricing-change-limits"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-rule-priority",
    title: "Rule priority and conflict resolution",
    emoji: "🥇",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "When several rules match one item: overrides always win, then highest priority number, with fall-through when a rule yields nothing.",
    body: `## 🥇 One winner per item, decided fairly

When multiple enabled rules match an item, the engine picks exactly one winner per run:

1. **Manual overrides first** — if any manual override matches, only overrides are considered (highest priority among them wins). Computed rules never beat an override.
2. **Otherwise, highest priority number wins** — priority is the number on each rule (bigger = stronger; the default is 100).
3. **Fall-through** — if the winning rule yields no usable price (missing spot, no weight, "stock within band", or a change smaller than Min change £), the next rule down gets a try. So a specific rule can gracefully hand over to a general one.

**Scope isn't priority**

An item-scoped rule does NOT automatically beat a category or all-items rule — only the priority number (and override status) decides. Give your specific rules explicitly higher numbers.

**Suggested bands**

- 900+ manual overrides
- 500–800 item/category-specific rules
- 100–400 broad rules (spot markup, catch-all margin target)`,
    tips: [
      "Leave gaps between priority numbers (100, 200, 300…) so you can slot rules in later without renumbering.",
      "Two rules on the same priority have no guaranteed order — always differentiate the numbers when both can match one item.",
      "Disable (don't delete) a rule to take it out of contention while keeping its config for later.",
    ],
    relatedIds: ["pricing-overrides", "pricing-recommendations", "pricing-rules-config"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-simulation",
    title: "Testing rules safely before going live",
    emoji: "🧪",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Dry-run new rules with auto-apply off: every proposal queues for review instead of touching prices, and the CSV export lets you audit in bulk.",
    body: `## 🧪 See what would happen — before it happens

There's a built-in safe mode: **auto-apply off**. With auto-apply disabled (per rule, or tenant-wide), a run changes *nothing* — every computed change lands on the Approvals tab as a pending proposal for you to inspect.

**The safe-test workflow**

1. Create the rule with **Auto-apply off** (the default).
2. Press **Run now**.
3. Open **Approvals** and review what the rule proposed: prices, margins, reasons.
4. Not right? **Reject** the proposals (nothing was ever applied), adjust the rule, run again. Old proposals for the same items are superseded automatically.
5. Happy? Approve the batch — or now enable auto-apply on the rule for future runs.

**Bulk review**

Use **Export CSV** to pull the change list into a spreadsheet — handy for eyeballing hundreds of proposals, or sharing with a colleague before approving.

**Blast-radius control while testing**

Scope the rule to a single item or small category first, and keep Max change % tight — then widen once the maths checks out.`,
    tips: [
      "Rejected proposals cost nothing — reject freely while tuning.",
      "The Run history row for your test shows exactly how many items were evaluated and proposed.",
      "Test spot rules just after a Run now so the reasons show current spot inputs.",
    ],
    relatedIds: ["pricing-recommendations", "pricing-approvals", "pricing-history"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-history",
    title: "Run history and price history",
    emoji: "🕘",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Every pass is logged — trigger, counts, duration, errors — and every price change is traceable from proposal to applied.",
    body: `## 🕘 Nothing happens off the record

**Run history** (the Run history tab) logs every engine pass:

- **Trigger** — manual (someone pressed Run now) or scheduled.
- **Counts** — rules evaluated, items scanned, changes proposed, auto-applied, approvals created, alerts sent.
- **Status & duration** — success or error, with the error message when a pass failed.

**Price change history**

The Approvals tab is also the change ledger: every proposal ever made — applied, auto-applied, rejected, failed or superseded — stays queryable with its rule, reasoning, before/after prices and margin. **Export CSV** downloads the list for offline records.

**Reading a healthy run**

Lots of items evaluated with few changes proposed is normal — it means prices are already where the rules want them (or moves were under the Min change threshold). Zero rules evaluated means no rules are enabled.

**Scheduled cadence**

Automatic passes run at most every 6 hours while the engine is enabled. A run log with trigger "scheduled" appearing regularly is your sign the automation is alive.`,
    tips: [
      "No scheduled runs appearing? Check Engine enabled in Settings — the master switch gates the schedule.",
      "The alerts count shows how many notifications a run fired (capped per pass to avoid flooding).",
      "Sort Approvals by status to audit everything auto-applied last week.",
    ],
    relatedIds: ["pricing-approvals", "pricing-troubleshooting", "pricing-settings"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-push-failures",
    title: "Why marketplace price updates fail",
    emoji: "📡",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "A price applied in MetalUI but not on the marketplace: expired connections, unlinked listings, channel rejections — and how to fix each.",
    body: `## 📡 The price saved — so why isn't it live on the channel?

When a change is approved, MetalUI writes the new asking price to the item, then pushes it to every marketplace the item is synced to via the standard sync paths. The local save and the pushes are separate steps — a proposal marked **failed** (or a price that didn't propagate) usually means a push problem.

**Common causes**

1. **Expired or disconnected marketplace connection** — eBay/TikTok tokens lapse; reconnect under Integrations.
2. **Item not linked to a live listing** — the channel toggle is on but no active listing mapping exists (e.g. the listing was ended or deleted on the marketplace).
3. **Channel rejected the price** — marketplace-side validation (price limits, listing state, category rules).
4. **The item isn't synced to that channel** — pushes only go to channels enabled on the item.
5. **Temporary marketplace outage** — rare; the next sync usually heals it.

**How to diagnose**

- The **reason column** on a failed approval carries the apply/push error.
- Check the item's own channel sync status for listing-level errors.
- Re-approve after fixing the connection, or edit-and-save the item to trigger a fresh push.`,
    tips: [
      "Fix the connection first, then re-trigger: a re-approved change or a manual item save both re-push the price.",
      "A failed push doesn't undo the local price — MetalUI and the marketplace can briefly disagree until the push succeeds.",
      "Repeated failures on one channel across many items almost always mean an expired connection, not item problems.",
    ],
    relatedIds: ["pricing-approvals", "pricing-troubleshooting", "pricing-channel-pricing"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-troubleshooting",
    title: "Troubleshooting Dynamic Pricing",
    emoji: "🔧",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "No proposals, prices stuck, unexpected prices, runs skipped — the quick diagnosis table for the pricing engine.",
    body: `## 🔧 When pricing doesn't do what you expected

**"Run now says skipped"** — the engine is disabled in Settings, or (for scheduled passes) the 6-hour cadence hasn't elapsed. Manual runs only skip when the engine is off or no rules are enabled.

**"No changes proposed at all"** — check, in order: rules enabled? scopes actually match items? required inputs present (weight & metal for spot, landed cost for margin/dropship)? changes bigger than Min change (£)?

**"A spot rule skipped items"** — the item lacks weight or metal, or the spot feed had no price this pass (the reason will say "no spot price or item weight"). Spot rules never guess when the feed is down.

**"Prices seem stuck below where they should be"** — Max change % caps each run's move; large corrections take several runs. Also check whether an old manual override rule is still enabled and winning.

**"The proposed price is higher than my rule computes"** — the margin floor clamped it. The floor is the higher of cost-plus-min-margin and the item's minimum acceptable price.

**"A change shows failed"** — the marketplace push errored; see *Why marketplace price updates fail*.

**"Approvals pile up faster than we clear them"** — raise Min change (£), reduce rule overlap, or enable auto-apply on the rules you already trust (upward-moving spot rules are the usual first candidates).`,
    tips: [
      "The reason string on every proposal and the message column in Run history are the two fastest diagnostic surfaces.",
      "One variable at a time: disable all rules but the one you're debugging, Run now, read the results.",
      "Items are processed in bounded pages; enormous catalogues drain over successive runs rather than one giant pass.",
    ],
    relatedIds: ["pricing-push-failures", "pricing-history", "pricing-common-mistakes"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-common-mistakes",
    title: "Common Dynamic Pricing mistakes",
    emoji: "⚠️",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "The traps teams fall into: wrong costs, forgotten overrides, spot-tracking collectibles, auto-apply too early, fee-blind margins.",
    body: `## ⚠️ Learn from everyone else's bruises

**1. Pricing from a wrong or missing landed cost.** The floor is only as good as the cost under it. £0-cost items are protected only by their minimum acceptable price.

**2. Enabling auto-apply on day one.** Run with approvals for a week or two first; you'll catch scope and threshold mistakes while they're still proposals.

**3. Forgetting a manual override.** Overrides beat everything and keep re-asserting their price. A promotion override left enabled quietly pins the price forever. Review your override rules monthly.

**4. Spot-tracking collectible stock.** Melt-value pricing on numismatic items destroys premium. Separate categories, and scope spot rules to bullion only.

**5. Setting minimum margin below your real costs.** A 5% floor on a channel with 13% fees plus postage is a guaranteed loss that the engine will happily "protect".

**6. Same priority on overlapping rules.** The winner is then not guaranteed — always differentiate priorities when scopes overlap.

**7. Tight max-change + big min-change.** Each capped step is smaller than the change threshold, so nothing ever moves. If prices freeze, loosen one.

**8. Ignoring failed pushes.** The local price updated but the marketplace still shows the old one — margin looks protected while the channel undersells. Watch the failed column.`,
    tips: [
      "Do a monthly hygiene pass: costs sampled, override rules reviewed, failed pushes cleared, per-channel margins vs fees sanity-checked.",
      "When in doubt, tighten the floor rather than the rules — the floor is the layer that can't be out-prioritised.",
      "Keep a small test category for trying new rule ideas before widening scope.",
    ],
    relatedIds: ["pricing-troubleshooting", "pricing-min-margin-rules", "pricing-simulation"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-glossary",
    title: "Dynamic Pricing glossary",
    emoji: "📖",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Definitions: hard floor, margin floor, true margin, markup, spot price, auto-apply, superseded, minimum acceptable price, pricing floor and more.",
    body: `## 📖 The words, precisely

**Asking price** — the item's current listed price; the engine's input and output.

**Auto-apply** — a rule or tenant setting that lets changes go live without human approval. All changes are still recorded.

**Hard floor / pricing floor / margin floor** — the lowest price MetalUI allows for an item: the higher of landed cost + minimum margin %, and the item's minimum acceptable price. Applied to every suggestion, including manual overrides.

**Landed cost** — the full cost of getting an item on the shelf (purchase + freight + duty). The cost basis for all margin maths.

**Markup** — profit as a % of cost. The engine's margin guard and margin column both use markup over landed cost.

**Gross margin** — profit as a % of sale price, before fees.

**True margin** — profit as a % of sale price after configured costs such as marketplace fees, postage, affiliate payouts and consignment costs.

**Minimum acceptable price** — a per-item hard limit set on the inventory item; the floor can never go below it.

**Minimum margin %** — the tenant (or per-rule) markup the floor enforces over landed cost.

**Max change %** — the largest move a single run may make from the current price.

**Min change (£)** — changes smaller than this are ignored entirely.

**Priority** — the number deciding which rule wins when several match (higher wins; manual overrides always beat computed rules).

**Rounding strategy** — none, nearest £1, end .99 or end .95; applied after floor and cap, floor re-checked afterwards.

**Rule scope** — which items a rule covers: all, a category, one item, or a channel.

**Run / pass** — one execution of the engine over the catalogue; logged in Run history.

**Spot price** — the live metal price in GBP per troy ounce (gold, silver, platinum).

**Superseded** — a pending proposal replaced by a newer one for the same item.

**Troy ounce** — 31.1035 g; the unit spot prices are quoted in.`,
    tips: [
      "Search this glossary from the Help Centre — terms like 'pricing floor' and 'superseded' resolve here.",
      "If a colleague says 'margin', confirm whether they mean markup over cost or margin on sale price before comparing numbers.",
    ],
    relatedIds: ["pricing-margin-vs-markup", "pricing-overview", "pricing-faq"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-faq",
    title: "Dynamic Pricing FAQ",
    emoji: "❓",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "Quick answers: can the engine price below cost? how often does it run? can I undo a change? does it touch items with no rules?",
    body: `## ❓ Straight answers to the usual questions

**Can the engine ever price below cost?** No — every suggestion is clamped to the margin floor (cost + minimum margin %, or the item's minimum acceptable price if higher). Even manual override rules are clamped.

**How often does it run?** Automatically at most every 6 hours while the engine is enabled; on demand whenever someone with run permission presses Run now.

**Does it change items that no rule matches?** Never. No matching enabled rule → no proposal.

**Can I undo an applied change?** There's no one-click revert, but every change records the previous price — create a manual override (or edit the item) to restore it, and the audit trail keeps the full story.

**What happens if the spot feed is down?** Spot rules skip that pass without touching prices; other rule types still run. Nothing is ever priced off a guessed spot.

**Do rejected proposals come back?** The engine may re-propose on a later run if the rule still computes the same change. To stop it permanently, adjust or disable the rule.

**Why did my 20% discount only move the price 10%?** Max change % caps each run's move. The remainder arrives over subsequent runs — unless the floor stops it first.

**Who can approve changes?** Only users with the pricing approve permission; viewing and approving are separate rights.

**Does approving push to the marketplace immediately?** Yes — the price is written to the item and pushed through the standard marketplace sync paths straight away. A push failure shows as a failed status with the error.

**Is there a sandbox?** Effectively yes: with auto-apply off, runs only ever create pending proposals — reject them freely while testing.`,
    tips: [
      "Most 'why didn't it…' questions are answered by the reason column on the proposal itself.",
      "Bookmark the troubleshooting article — it's the diagnosis-first version of this FAQ.",
    ],
    relatedIds: ["pricing-troubleshooting", "pricing-glossary", "pricing-overview"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-settings",
    title: "Pricing Settings explained (admins)",
    emoji: "⚙️",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.settings.manage",
    summary:
      "The engine-wide guards: engine switch, auto-apply, minimum margin, max change, min change and rounding — defaults, effects and safe values.",
    body: `## ⚙️ The engine's control panel

Settings (visible to pricing-settings managers only) hold the tenant-wide defaults; each rule can override the numeric guards for its own items.

**Engine enabled** — the master switch for scheduled passes. Off = no automatic repricing (Run now also requires the engine to be on). Default: off.

**Allow auto-apply** — the tenant-wide default for whether changes go live without approval. Individual rules can set their own auto-apply flag, which wins over this default. Default: off.

**Min margin %** — the default margin floor markup over landed cost. Default 5%. Set it to at least your typical channel fees + desired profit — 5% is a bare-metal safety net, not a business target.

**Max change %** — the per-run price move cap. Default 25%. 0 disables the cap entirely (not recommended while auto-apply is on).

**Min change (£)** — ignore moves smaller than this. Default £0.50.

**Rounding strategy** — none / nearest £1 / end .99 / end .95. Default: none (round to the penny).

**Permissions around this page**

Managing settings, managing rules, running the engine, approving changes and exporting the CSV are five separate permissions — you can give a colleague run+approve without settings access.`,
    tips: [
      "Change one guard at a time and watch a run's proposals before the next tweak.",
      "Per-rule overrides beat these defaults — if behaviour doesn't match settings, check the rule's own min-margin/max-change/rounding fields.",
      "Turning the engine off doesn't touch existing pending approvals; they stay actionable.",
    ],
    relatedIds: ["pricing-min-margin-rules", "pricing-change-limits", "pricing-rules-config"],
    pageIds: ["pricing"],
  },
  {
    id: "pricing-rules-config",
    title: "Configuring price rules (admins)",
    emoji: "🛠️",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.rules.manage",
    summary:
      "Field-by-field reference for the rule editor: types, scopes, config keys, per-rule guards and auto-apply.",
    body: `## 🛠️ Building rules that behave

**Shared fields**

- **Name / description** — shown on proposals; make the name say what it does ("Silver bullion spot +22%").
- **Rule type** — the pricing maths (below).
- **Scope** — all items, a category, a single item, or a channel (with the matching category/item/channel field).
- **Priority** — higher number wins among matching rules (manual overrides always beat computed rules).
- **Enabled** — disabled rules are skipped everywhere.
- **Auto-apply** — this rule's changes go live without approval (overrides the tenant default).
- **Min margin % / Max change % / Rounding** — per-rule overrides of the Settings defaults; leave blank to inherit.

**Config keys by rule type** (the Config JSON field)

- *spot_price_markup*: \`markupPercent\`, optional \`metal\` (else the item's own metal is used). Needs item weight.
- *stock_level*: \`thresholdQty\`, \`lowStockAdjustPercent\`, \`highStockAdjustPercent\`.
- *sales_velocity*: \`windowDays\` (7/30/90), \`fastThreshold\`, \`fastAdjustPercent\`, \`slowThreshold\`, \`slowAdjustPercent\`.
- *marketplace_adjustment*: \`adjustPercent\`, \`channel\`.
- *margin_target*: \`targetMarginPercent\` (markup over landed cost).
- *competitor_price*: \`competitorPrice\`, plus \`matchOnly\` or \`undercutAmount\` / \`undercutPercent\`.
- *customer_group*: \`adjustPercent\`, \`customerGroup\`.
- *dropship_markup*: \`markupPercent\` (over landed cost).
- *manual_override*: \`price\` (the pinned price).

**Relative rules need a base price** — stock, velocity, marketplace and customer-group rules adjust the item's current asking price (falling back to its resale price). Items with neither are skipped by those rule types.`,
    tips: [
      "Blank per-rule guard fields inherit the Settings defaults — set them only where a rule genuinely needs different limits.",
      "Invalid config JSON is rejected on save — keep a working rule's config handy as a template.",
      "After editing a rule, Run now and read the proposals it generates before walking away.",
    ],
    relatedIds: ["pricing-rule-priority", "pricing-settings", "pricing-simulation"],
    pageIds: ["pricing"],
  },
  // ── Operations Inbox / Exception Management ─────────────────────────────
  {
    id: "ops-inbox-overview",
    title: "What is the Operations Inbox?",
    emoji: "🚨",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "One prioritised inbox for operational failures — what an exception is, how it differs from a notification, and how work flows through it.",
    body: `## 🚨 One inbox for everything that's gone wrong

The Operations Inbox collects **exceptions** — operational failures that need a human decision — into a single, prioritised list. Instead of scanning six modules for problems, you triage one queue sorted by severity and urgency.

**Exception vs notification**

- A **notification** tells you something *happened* ("Payment received", "Sync finished"). You read it and move on — there's nothing to fix.
- An **exception** tells you something is *wrong and needs action* ("TikTok authorisation expires in 3 days", "12 orders overdue for payment"). It stays open, tracks who owns it, carries deadlines, and escalates if ignored.

Every exception carries:

- A **severity** (P0 Critical → P5 Info) — how bad it is.
- An **urgency score** (0–100) — how quickly it should be worked, computed from financial impact, customer impact, time sensitivity, blockage, risk and repetition.
- **SLA deadlines** — acknowledge-by, action-by and resolve-by timestamps.
- **Affected records** — direct links to the orders, items or integrations involved.
- An **occurrence history** — every time the underlying problem was re-detected.

**How exceptions arrive**

Automatic **detectors** watch the system (expiring marketplace tokens, oversold stock, overdue unpaid orders, failed accounting syncs, packing evidence gaps, queue backlogs) and raise an exception when a condition trips. Duplicate detections fold into the existing exception rather than creating noise.

**The triage flow**

1. Open the inbox — the list is ordered severity first, then urgency, then last seen.
2. Click a row to open the detail drawer: description, SLA deadlines, affected records, occurrences.
3. **Assign** it (to yourself), **Acknowledge** it, take the action, then **Resolve** with a note — or **Snooze** / mark it **Waiting** if it can't move yet.

Many exceptions also **auto-resolve**: when a detector sees the underlying condition has cleared (e.g. the token was reconnected), the exception closes itself with resolution kind "auto".`,
    tips: [
      "The status filter defaults to 'Active (any open state)' — resolved and dismissed exceptions are hidden but never deleted; switch the filter to review history.",
      "The severity tabs show live counts per level, so you can see at a glance whether anything critical is open.",
      "If the same problem keeps recurring, its occurrence count (×N) climbs and its urgency score rises — repetition is a scored factor.",
    ],
    relatedIds: ["ops-inbox-severity-levels", "ops-inbox-urgency-score", "ops-inbox-resolution-states"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-severity-levels",
    title: "Severity levels P0–P5 explained",
    emoji: "🎚️",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "What each severity level means, examples of each, the expected response and how deadlines and escalation differ per level.",
    body: `## 🎚️ Six levels, from drop-everything to for-your-information

Every exception has a severity set by its detector (or overridden by one of your rules). Severity drives the SLA clock and the escalation behaviour.

| Level | Label | Meaning | Example | Expected response |
|-------|-------|---------|---------|-------------------|
| P0 | Critical | Revenue or operations are actively broken | A marketplace integration fully down during trading | Drop everything — acknowledge within 1 hour, resolve within 24 hours |
| P1 | Urgent | Serious problem causing real damage now | Oversold stock with recent sales; an expired marketplace token; 500+ jobs backed up | Same working day — acknowledge within 4 hours |
| P2 | High | Significant problem that will hurt soon | Token expiring within 7 days; accounting sync failures; overdue unpaid orders | Within the day — acknowledge within 8 hours, resolve within 3 days |
| P3 | Medium | Needs attention but not burning | Packing jobs stuck in progress over 24 hours; a growing queue backlog | Within the working day — resolve inside a week |
| P4 | Low | Worth fixing when convenient | Minor data quality issues | Within 2 days ack, 2 weeks to resolve |
| P5 | Info | For awareness | Informational conditions worth a record | Within the week; a month to close |

**How severity is set**

1. The **detector** assigns a sensible default (e.g. oversold stock raises at P1; an expiring token at P2, bumped to P1 once actually expired).
2. An **exception rule** can override the default when the exception is first raised — for example, "any marketplace exception mentioning TikTok → P1".
3. **Escalation** can bump severity one level at a time if the exception breaches its SLA unacknowledged or unresolved (P0 can't go higher).

**Severity vs urgency**

Severity is the *band* (how bad); the urgency score is the *rank within and across bands* (how quickly). Two P2 exceptions can carry very different urgency scores — the inbox sorts by severity first, then urgency.`,
    tips: [
      "Use the P0/P1 severity tabs as your morning check — if both counts are zero, nothing is on fire.",
      "Escalation raises severity a maximum of two steps, so a neglected P3 tops out at P1 — it never fabricates a P0.",
      "If a detector's default severity doesn't fit your business, don't ignore the exceptions — set a rule that re-maps them.",
    ],
    relatedIds: ["ops-inbox-critical-exceptions", "ops-inbox-deadlines-slas", "ops-inbox-escalation"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-critical-exceptions",
    title: "Understanding Critical (P0) exceptions",
    emoji: "🔴",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "What P0 means, the 1-hour acknowledgement window, how urgency and escalation behave at the top level, and how to run a P0 to resolution.",
    body: `## 🔴 P0 — the alarm you never leave ringing

A **P0 Critical** exception means revenue or core operations are actively broken. It is the only level with a 1-hour acknowledgement SLA.

**The P0 clock**

| Deadline | Window |
|----------|--------|
| Acknowledge by | 1 hour from raise |
| Action by | 4 hours |
| Resolve by | 24 hours |

Deadlines are calendar time from when the exception was raised (reopening resets them).

**Running a P0**

1. **Acknowledge immediately.** Acknowledging stops the first escalation trigger and tells the team someone owns it. Unacknowledged P0s past the 1-hour window escalate and re-alert admins.
2. **Assign it** — to yourself if you're taking it. An assigned P0 with no progress is still overdue, so acknowledge as well.
3. **Open the affected records** from the detail drawer and fix the underlying cause.
4. **Resolve with a note** describing what you did — the note is kept on the exception permanently.

**What escalation does at P0**

Severity can't go above P0, but a P0 past its SLA still records an escalation level and sends escalation alerts — it doesn't go quiet just because it's already at the top.

**When a P0 can wait**

Almost never — but if you're genuinely blocked on a third party, use **Waiting (external)**. Waiting states pause the escalation clock so you aren't punished for a courier's or marketplace's outage. Don't use snooze to hide a P0 you simply haven't started.`,
    tips: [
      "Acknowledge first, investigate second — acknowledgement is the only action with a 1-hour deadline.",
      "The urgency score on a P0 tells you which of several P0s to work first.",
      "A resolved P0 that recurs is raised as a fresh exception with fresh SLAs — check the occurrence history of past incidents when diagnosing repeats.",
    ],
    relatedIds: ["ops-inbox-severity-levels", "ops-inbox-deadlines-slas", "ops-inbox-escalation"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-urgency-score",
    title: "How the urgency score is calculated",
    emoji: "🧮",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "The 0–100 urgency score: the six weighted factors, how each is normalised, and two worked example calculations.",
    body: `## 🧮 A 0–100 score that ranks your queue for you

Every exception carries an urgency score from 0 to 100. It's a weighted blend of six factors:

| Factor | Weight | How it's measured |
|--------|--------|-------------------|
| Financial impact | 25% | Estimated £ at stake, scaled against £5,000 (anything ≥ £5,000 scores the full 25) |
| Customer impact | 20% | How directly customers are affected (0–1) |
| Time sensitivity | 20% | How soon it bites — within 1 hour scores full marks, tapering on a log scale to zero at 7 days |
| Blockage | 15% | How much downstream work is blocked (0–1) |
| Risk | 10% | Chance of knock-on damage (0–1) |
| Repetition | 10% | How often it has recurred — 1st occurrence scores 0, saturating at 10 occurrences |

**Worked example 1 — a mid-size problem**

£1,000 at stake, moderate customer impact (0.5), bites in about 24 hours, blocks some work (0.6), some risk (0.4), first occurrence:

- Financial: (1,000 ÷ 5,000) × 25 = **5.0**
- Customer: 0.5 × 20 = **10.0**
- Time (24h on the log curve ≈ 0.31): × 20 ≈ **6.2**
- Blockage: 0.6 × 15 = **9.0**
- Risk: 0.4 × 10 = **4.0**
- Repeat (first time): **0**

Total ≈ **24** — real, but not the top of the queue.

**Worked example 2 — an expired marketplace token**

£5,000+ of daily channel revenue at stake (full 25), high customer impact 0.8, bites within the hour (full 20), blocks all syncs (1.0 → 15), high risk 0.7 (→ 7), fifth occurrence ((5−1)÷9 ≈ 0.44 → 4.4):

Total ≈ 25 + 16 + 20 + 15 + 7 + 4.4 ≈ **87** — work this one now.

**When the score changes**

Each re-detection of the same problem merges into the existing exception, refreshes the factors and recomputes the score — so a worsening or repeating problem climbs the queue automatically.`,
    tips: [
      "The inbox sorts by severity first, urgency second — use urgency to pick between exceptions at the same level.",
      "Repetition alone can add up to 10 points — a 'minor' recurring problem gradually stops looking minor.",
      "You can use 'Min urgency' in exception rules to only re-prioritise or notify above a threshold.",
    ],
    relatedIds: ["ops-inbox-severity-levels", "ops-inbox-deduplication", "ops-inbox-rules"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-deadlines-slas",
    title: "Deadlines and SLAs",
    emoji: "⏱️",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "The acknowledge / action / resolve deadlines per severity level, how they're displayed, what pauses them and what happens on breach.",
    body: `## ⏱️ Three clocks on every exception

Every exception deadline is set the moment the exception is raised, based on its severity — three per exception:

| Severity | Acknowledge by | Action by | Resolve by |
|----------|----------------|-----------|------------|
| P0 Critical | 1 hour | 4 hours | 24 hours |
| P1 Urgent | 4 hours | 12 hours | 48 hours |
| P2 High | 8 hours | 24 hours | 72 hours |
| P3 Medium | 24 hours | 72 hours | 7 days |
| P4 Low | 48 hours | 7 days | 14 days |
| P5 Info | 7 days | 14 days | 30 days |

All three are **calendar time from the moment the exception is raised** (not working hours). Reopening a closed exception restarts fresh deadlines from the reopen.

**What each deadline means**

- **Acknowledge by** — someone has clicked Acknowledge, signalling ownership.
- **Action by** — meaningful work should have started.
- **Resolve by** — the exception should be closed.

**Where you see them**

The detail drawer lists all three with timestamps; any deadline in the past shows in red with an "(overdue)" marker.

**What happens on breach**

- Past **acknowledge-by** while still Open and unacknowledged → automatic escalation: severity bumps one level and admins are re-alerted.
- Past **resolve-by** while still active → a second (final) escalation bump.

**What pauses the clock**

- **Waiting (external)** and **Waiting (customer)** — escalation is paused while you're legitimately blocked on someone else.
- **Snoozed** — no escalation until the snooze expires (it then returns to Open with its original deadlines).
- **Resolved / Dismissed** — clocks stop entirely.`,
    tips: [
      "Acknowledging is cheap and stops the first escalation — do it as soon as you've seen the exception, even before you start work.",
      "Snoozing doesn't extend deadlines — a snoozed exception that wakes up past its resolve-by date is immediately escalation-eligible.",
      "If a deadline pattern consistently doesn't fit a detector's output, use a rule to change that detector's severity — the SLA follows the severity.",
    ],
    relatedIds: ["ops-inbox-escalation", "ops-inbox-resolution-states", "ops-inbox-severity-levels"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-assigning",
    title: "Assigning and owning exceptions",
    emoji: "🙋",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "How assignment works, assign-to-me and unassign, filtering by assignee, and auto-assignment via rules.",
    body: `## 🙋 Every open exception should have a name on it

Unowned problems drift. Assignment puts a name against each exception so the team knows who's on it.

**Assigning**

Open an exception and click **Assign to me** in the Actions section (requires the manage-exceptions permission). Click **Unassign** to release it back to the pool.

**Finding your work**

The assignee filter at the top of the inbox has three views:

- **Any assignee** — everything.
- **Assigned to me** — your personal queue.
- **Unassigned** — the pool nobody has picked up yet. A healthy team keeps this near zero for P0–P2.

**Auto-assignment via rules**

Exception rules can assign new exceptions automatically the moment they're raised — for example, route every *finance* exception to your bookkeeper. The first matching enabled rule wins.

**Assignment vs acknowledgement**

They're different signals: assignment says *who* owns it; acknowledgement says *work has been seen and accepted* — and only acknowledgement stops the first escalation. Best practice: assign **and** acknowledge in the same visit.`,
    tips: [
      "Start your day on 'Assigned to me' + status Active — that's your personal to-do list.",
      "Assignment survives status changes — a snoozed exception wakes up still assigned to you.",
      "There's no limit on how many exceptions one person holds — team leads should watch the Unassigned view AND spot overload.",
    ],
    relatedIds: ["ops-inbox-resolution-states", "ops-inbox-rules", "ops-inbox-best-practices"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-resolution-states",
    title: "Resolution states — resolve, dismiss, snooze, waiting, reopen",
    emoji: "🔄",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "Every exception status, what it means, when to use each action and how transitions (including reopen) behave.",
    body: `## 🔄 The exception lifecycle

**The statuses**

| Status | Meaning | Use when |
|--------|---------|----------|
| Open | Raised, nobody has engaged yet | Default on raise |
| Acknowledged | Someone has seen it and accepted the work | You've clicked Acknowledge |
| Snoozed | Hidden until a chosen time, then returns to Open | It genuinely can't be worked before a known time |
| Waiting (external) | Blocked on a third party (marketplace, courier, accountant) | You've done your part; the ball is elsewhere |
| Waiting (customer) | Blocked on the customer | You're waiting for the buyer to respond |
| Resolved | Fixed — closed with an optional note | The underlying cause is gone |
| Dismissed | Closed without fixing — not applicable / accepted risk | It's a false positive or you've decided not to act |

Open, Acknowledged, Snoozed and both Waiting states count as **active**; Resolved and Dismissed are **terminal**.

**Choosing between them**

- **Resolve** when the problem is actually fixed. Add a note — it's stored on the exception and shown in the drawer forever.
- **Dismiss** when no fix is needed. Also note *why*, so the next person doesn't re-investigate.
- **Snooze** takes an hours value and hides the exception until then; on expiry it returns to **Open** automatically. It does not extend SLA deadlines.
- **Waiting** states pause escalation — use them honestly; they're for genuine external blockage, not procrastination.

**Reopen**

- Reopening a **Resolved/Dismissed** exception returns it to Open with **fresh SLA deadlines**.
- Reopening a snoozed or waiting exception clears that state and returns it to Open (deadlines unchanged).

**Auto-resolution**

Detectors close their own exceptions when the condition clears (resolution kind "auto"), and rules can auto-resolve an exception after a configured quiet period with no recurrence — so stale noise cleans itself up.`,
    tips: [
      "Terminal states are safe: a resolved exception whose problem returns is raised again as a new exception with a full history of its own.",
      "Snooze requires a positive number of hours — the exception reappears in the Active view when the timer expires.",
      "Use the status filter set to Resolved or Dismissed to audit past decisions and their notes.",
    ],
    relatedIds: ["ops-inbox-deadlines-slas", "ops-inbox-deduplication", "ops-inbox-assigning"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-deduplication",
    title: "Deduplication, occurrences and root-cause exceptions",
    emoji: "🧩",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "Why the same problem never floods your inbox: dedupe keys, the occurrence history, aggregate root-cause exceptions and auto-resolution.",
    body: `## 🧩 One problem, one exception — however often it fires

Detectors run continuously, so the same failing condition can be detected dozens of times. The inbox folds all of that into a single exception.

**How dedupe works**

Each exception has a dedupe key built from its **detector** and the **entity it concerns** (e.g. a specific integration). While an exception for that key is still active, every re-detection:

- Increments the **occurrence count** (the ×N in the list),
- Updates **last seen**,
- Merges the latest impact factors and **recomputes the urgency score**,
- Appends an entry to the **occurrence history** in the drawer,
- Does **not** send another notification — you were told the first time.

Only once the exception is resolved or dismissed can a fresh detection open a new one.

**Root-cause (aggregate) exceptions**

Some detectors deliberately raise **one exception for the whole problem class** instead of one per record — for example, a single "oversold stock" exception listing every affected SKU, or one "overdue unpaid orders" exception covering all overdue orders. The affected-records list in the drawer links to each individual record. This keeps the inbox describing *root causes*, not symptoms.

**Auto-resolution**

When a detector re-checks and finds its condition has cleared (token reconnected, backlog drained), it resolves its own exception automatically with resolution kind "auto". You'll see that in the drawer footer next to the resolved timestamp.

**Reading the occurrence history**

The Occurrences section lists each detection with a timestamp and detail line. A long, dense history means a persistent condition; a sparse one means it's intermittent — useful diagnostic context before you dive in.`,
    tips: [
      "A rising occurrence count also raises the urgency score — repetition is one of the six scored factors.",
      "Don't dismiss an aggregate exception because one of its records is fixed — it represents all of them; it will auto-resolve when the whole condition clears.",
      "If you resolve an exception while the underlying problem still exists, the next detector pass simply raises it again — resolving is not silencing.",
    ],
    relatedIds: ["ops-inbox-urgency-score", "ops-inbox-resolution-states", "ops-inbox-troubleshooting"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-escalation",
    title: "Automatic escalation",
    emoji: "📢",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "How ignored exceptions escalate: the two triggers, severity bumps, escalation alerts, and what pauses escalation.",
    body: `## 📢 Ignored exceptions get louder, not lost

Escalation is the safety net that stops exceptions dying quietly in the queue.

**The two triggers**

1. **Unacknowledged past the acknowledge-by deadline** — an exception still Open with nobody having acknowledged it gets its severity bumped one level (e.g. P3 → P2), its escalation level set to 1, and an escalation alert sent to admins.
2. **Still active past the resolve-by deadline** — a second (final) bump: severity up one more level, escalation level 2.

Two bumps is the maximum, and P0 can't go higher — a P0 past its SLA still records the escalation and re-alerts, it just stays P0.

**What you see**

The detail drawer shows "Escalated ×N" in red with the escalation timestamp, and the exception now sits in a higher severity tab with tighter framing.

**What pauses escalation**

- **Waiting (external)** and **Waiting (customer)** — genuine external blockage isn't punished.
- **Snoozed** (until the snooze expires).
- **Resolved / Dismissed** — terminal states never escalate.

**How to stay ahead of it**

Acknowledging within the window defuses trigger 1 entirely; resolving (or honestly marking Waiting) within the resolve window defuses trigger 2. Escalation is only ever a symptom of an exception nobody engaged with.`,
    tips: [
      "Acknowledgement is the single cheapest way to prevent escalation — it's one click.",
      "An escalated exception keeps its bumped severity even after you acknowledge — resolve it to clear the noise.",
      "Frequent escalations in one category usually mean the team can't see the queue — check permissions and the Unassigned filter, not just workload.",
    ],
    relatedIds: ["ops-inbox-deadlines-slas", "ops-inbox-critical-exceptions", "ops-inbox-best-practices"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-rules",
    title: "Exception rules — step by step",
    emoji: "⚙️",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.manage_rules",
    summary:
      "Create rules that re-prioritise, auto-assign, silence or auto-resolve new exceptions — with the exact matching and precedence behaviour.",
    body: `## ⚙️ Shape how new exceptions land

Rules run when an exception is **first raised** and can override its severity, assign it, control notification and schedule auto-resolution. They do not reshape exceptions already in the inbox.

**Creating a rule**

1. Open **Operations Inbox → Rules** (requires the manage-rules permission) and click **New Rule**.
2. Name it clearly — the name is what teammates see in the list.
3. Set the **conditions** (all set conditions must match):
   - **Category** — e.g. only *finance* exceptions.
   - **Detector** — e.g. only *marketplace_token_expiry*.
   - **Title contains** — case-insensitive keyword, e.g. "TikTok".
   - **Min urgency (0–100)** — only exceptions scoring at or above this.
4. Set the **actions**:
   - **Set severity** — override the detector's default (or keep it).
   - **Notify admins on raise** — switch off for known-noisy conditions you still want tracked.
   - **Auto-resolve after (hours)** — close the exception automatically if the condition hasn't recurred for that many hours.
5. Click **Test against active exceptions** — a dry run showing how many of your current active exceptions the conditions would match. Nothing is changed.
6. **Save** (and leave **Enabled** on).

**Precedence**

Enabled rules are evaluated in order and the **first matching rule wins** — one rule applies per exception. Put your most specific rules first.

**What rules cannot do**

- They never modify existing exceptions — only new raises.
- They can't create exceptions; detectors do that.
- Disabling notifications doesn't hide the exception — it still appears in the inbox with full SLAs.`,
    tips: [
      "Use the Test button before saving — it's the fastest way to sanity-check a keyword or urgency threshold against reality.",
      "A rule with 'notify off' + a sensible auto-resolve window is the right way to keep tracking a known, tolerated condition without pinging anyone.",
      "If no rule matches, nothing changes: the detector's default severity applies and admins are notified.",
    ],
    relatedIds: ["ops-inbox-overview", "ops-inbox-assigning", "ops-inbox-severity-levels"],
    pageIds: ["exceptions", "exception-rules"],
  },
  {
    id: "ops-inbox-worked-example-tiktok",
    title: "Worked example: a TikTok Shop authorisation expires",
    emoji: "🎬",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "Follow one exception end-to-end: detection, urgency scoring, affected syncs, assignment, the reconnect fix and automatic resolution.",
    body: `## 🎬 One exception, start to finish

**1. Detection**

The *marketplace token expiry* detector checks every connected marketplace integration. It spots that your TikTok Shop refresh token expires within 7 days and raises a **P2 High** exception in the *marketplace* category ("TikTok Shop authorisation expires in 5 days"). If the token had already expired, it would raise at **P1 Urgent** instead.

**2. Urgency score**

The detector estimates the factors: significant channel revenue at stake, moderate customer impact (orders keep flowing until expiry), time sensitivity of a few days, syncs about to be blocked, first occurrence. Blended by the standard weights that might land around 55–65 — high in the P2 band, so it sits near the top of the queue.

**3. What's affected**

The detail drawer's **Affected records** section links straight to the TikTok Shop integration. Once the token actually expires, order imports, stock pushes and listing updates for that channel all stop — which is exactly why the detector warns a week early.

**4. Assignment and acknowledgement**

Your rule "Marketplace tokens → Sam" auto-assigned it on raise. Sam opens it, clicks **Acknowledge** (stopping the escalation clock) and follows the affected-record link.

**5. The fix**

On the integration page Sam clicks reconnect and completes the TikTok authorisation flow. A fresh token is stored; syncs are safe.

**6. Auto-resolution**

On its next pass the detector re-checks the token, finds it healthy, and **auto-resolves** the exception — the drawer footer shows Resolved with kind "auto". Nobody had to remember to close it.

**If it had been ignored**

Past the 8-hour acknowledge window it would have escalated to P1 and re-alerted admins; past the 72-hour resolve window it would have bumped again. And once the token actually expired, subsequent detections would have folded into the same exception, raising its occurrence count and urgency.`,
    tips: [
      "Expiry warnings a week out are your cheapest fixes — reconnecting takes two minutes before expiry and can mean lost orders after.",
      "The same pattern applies to QuickBooks and other marketplace/accounting authorisations — same detector family, same flow.",
      "After reconnecting, you don't need to resolve manually — the detector confirms the fix and closes it for you.",
    ],
    relatedIds: ["ops-inbox-category-marketplace", "ops-inbox-urgency-score", "ops-inbox-escalation"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-category-inventory",
    title: "Inventory exceptions (e.g. oversold stock)",
    emoji: "📦",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "What raises inventory exceptions, how the oversold-stock detector works, and how to clear them.",
    body: `## 📦 When the stock numbers stop adding up

**The oversold-stock detector**

Raises a **P1 Urgent** exception when items with a sale in the last 30 days show more units sold than were ever stocked. It's an **aggregate** exception — one exception for your whole account, with every affected SKU linked in the affected-records list — so a bad import doesn't fire fifty alarms.

**Why it matters**

Oversold stock means you may have taken money for units you can't ship, and your profit and stock-value reports are computing from wrong quantities.

**Clearing it**

1. Open the exception and work through the affected SKUs.
2. For each, compare the sales lines against actual intake — the usual causes are a missed purchase/intake, a duplicate sale import, or a pack quantity recorded against the wrong SKU.
3. Correct the stock record (add the missing intake, fix the sale line, or adjust the quantity with a note).
4. The detector re-checks on its next pass and auto-resolves once no SKU is oversold.

**Other inventory conditions**

The *inventory* category is also available to exception rules, so any future or custom inventory detectors route through the same triage flow.`,
    tips: [
      "Fix the data, not the exception — resolving it manually while SKUs are still oversold just means it's raised again on the next detector pass.",
      "Sort out the SKUs with recent sales first — those are the ones that can strand a live order.",
      "Recurring oversells on the same SKU usually point at a broken import mapping, not a warehouse problem.",
    ],
    relatedIds: ["ops-inbox-deduplication", "ops-inbox-overview", "inventory-overview"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-category-sales",
    title: "Sales exceptions (e.g. overdue unpaid orders)",
    emoji: "🛒",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "How the overdue-unpaid-orders detector rolls late payments into one exception and how to work it down.",
    body: `## 🛒 Money that should have arrived by now

**The overdue-unpaid-orders detector**

Raises a **P2 High** aggregate exception when orders pass their payment due date while still awaiting payment, not paid, or only partially paid (cancelled orders are excluded). One exception covers all overdue orders, with each order linked in the affected-records list.

**Working it**

1. Open the exception and follow the affected-record links to each order.
2. Chase, record the payment when it lands, write off what you'll never collect, or cancel dead orders — the same actions you'd use from the Sales page.
3. As orders are paid, written off or cancelled they drop out of the detector's condition; once none remain overdue, the exception **auto-resolves**.

**Alongside payment alerts**

You may already receive per-order "payment overdue" notifications. The exception is different: it's the *aggregate working item* — one owned, SLA-tracked task representing the whole overdue book, rather than a stream of pings.

**Useful pairings**

- Assign it permanently to whoever owns credit control via an exception rule.
- Reports → Payments gives you the same population sorted by days late and balance — a good companion view while you work the exception.`,
    tips: [
      "The exception tracks a moving population — its occurrence history shows the condition being re-detected as orders join and leave.",
      "Snooze is reasonable here if you chase payments on a fixed weekly cycle — set it to wake the morning of your credit-control run.",
      "Write-offs and cancellations count as clearing the condition — the detector isn't asking you to collect the uncollectable.",
    ],
    relatedIds: ["payments-outstanding", "payments-reports", "ops-inbox-resolution-states"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-category-marketplace",
    title: "Marketplace exceptions (tokens and channel health)",
    emoji: "🏪",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "Expiring or expired marketplace authorisations — what the token-expiry detector checks and how to reconnect safely.",
    body: `## 🏪 Keeping your sales channels authorised

**The marketplace token-expiry detector**

Checks every connected integration with a renewable authorisation (e.g. TikTok Shop, QuickBooks) and raises:

- **P2 High** when the refresh token expires **within 7 days** — your early warning.
- **P1 Urgent** when the token has **already expired** — syncs for that channel are failing now.

Each integration gets its own exception (dedupe is per integration), so two channels expiring gives you two clearly separate items.

**Why a week early?**

An expired marketplace authorisation silently stops order imports, stock pushes and listing updates. Orders keep being placed on the marketplace — you just stop seeing them, which is how oversells and late dispatches start. Fixing during the warning window costs two minutes; fixing after expiry can cost real orders.

**The fix**

Follow the affected-record link to the integration and run the reconnect / re-authorise flow for that channel. Once the detector sees a healthy token on its next pass, the exception **auto-resolves**.

**If reconnect fails**

Marketplace consoles occasionally require re-approval on their side. Mark the exception **Waiting (external)** while you wait on the marketplace — that pauses escalation — and add what you're waiting for as context in your resolution note when you close it.`,
    tips: [
      "Reconnect before expiry whenever possible — the warning exception exists precisely so you never operate a dead channel unknowingly.",
      "A rule matching detector 'marketplace_token_expiry' + keyword 'TikTok' can bump severity or route these to your channel manager automatically.",
      "See the worked example article for this exact scenario followed end-to-end.",
    ],
    relatedIds: ["ops-inbox-worked-example-tiktok", "ops-inbox-severity-levels", "ops-inbox-rules"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-category-packing",
    title: "Packing exceptions (evidence gaps and stuck jobs)",
    emoji: "📮",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "The packing-evidence-gap detector: jobs flagged with issues or stuck in progress for over 24 hours.",
    body: `## 📮 Parcels that stalled on the bench

**The packing-evidence-gap detector**

Raises a **P3 Medium** exception when packing jobs are either flagged with an **issue** or have sat **in progress for more than 24 hours**. Both patterns mean a paid order isn't moving and may lack the evidence (video / photos) you rely on in disputes.

**Working it**

1. Open the affected packing jobs from the exception.
2. For stuck jobs: find out why — missing stock, a question for the customer, or simply a job someone started and walked away from. Complete or cancel it.
3. For issue-flagged jobs: resolve the flagged problem (damaged item, short pick) and get the job back on track.
4. When no jobs match the condition, the exception auto-resolves.

**Why it's P3, not higher**

A single slow parcel is a customer-service risk rather than an operational outage. But don't let it age: past its SLA it escalates like any other exception, and chronic recurrences point at a workflow problem worth fixing at the source.`,
    tips: [
      "If the same station's jobs keep stalling, look at the workflow (batch sizes, missing stock at pick) rather than nudging individual jobs.",
      "Packing evidence matters most for high-value orders — prioritise those affected records first.",
      "Waiting (customer) is appropriate when a job is stalled on a buyer's answer — it pauses escalation honestly.",
    ],
    relatedIds: ["packing-overview", "ops-inbox-resolution-states", "ops-inbox-deadlines-slas"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-category-returns",
    title: "Returns exceptions",
    emoji: "↩️",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "How the returns category fits the exception model, and how to use rules for returns conditions.",
    body: `## ↩️ Returns in the exception model

The *returns* category is part of the exception framework: returns-related exceptions land in the inbox with the same severity, urgency, SLA and workflow treatment as every other category, and exception rules can match on the category to route or re-prioritise them.

**Today**

There is no always-on automatic returns detector yet — returns problems currently surface through the Returns module itself (open cases, evidence requests, refund decisions) and its notifications.

**What the category is for**

- **Rules-ready routing** — you can already create a rule matching category *returns* so that when returns detectors ship, they arrive pre-routed to the right person at the right severity.
- **A consistent home** — as detectors are added (e.g. return cases aging without a decision, refunds promised but not issued), they will raise exceptions here rather than inventing a new alerting channel.

**Meanwhile**

Keep working returns from the Returns module, and treat any *finance*-category exception that references refunds (e.g. accounting sync failures on refund documents) as the returns-adjacent signal it is.`,
    tips: [
      "Set up your returns routing rule now — rules apply to new exceptions the moment a matching detector ships.",
      "The Returns module's own audit trail remains the source of truth for individual cases.",
      "Refund-related accounting failures appear under the finance category — check there if a refund didn't reach your books.",
    ],
    relatedIds: ["returns-overview", "ops-inbox-rules", "ops-inbox-category-finance"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-category-finance",
    title: "Finance exceptions (accounting sync failures)",
    emoji: "💷",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "The accounting-sync-failure detector for Xero and QuickBooks, and how to troubleshoot a failing sync.",
    body: `## 💷 When your books stop receiving

**The accounting-sync-failure detector**

Raises a **P2 High** exception when Xero or QuickBooks sync attempts have **failed within the last 24 hours**. The exception aggregates the recent failures with the affected documents linked, so one flaky afternoon doesn't produce a wall of alerts.

**Troubleshooting a failing sync**

1. **Check the connection first.** An expired or revoked authorisation is the most common cause — if there's also a marketplace-token exception for the same provider, fix that one first; the sync failures are downstream of it.
2. **Read the failure detail** on the affected sync log entries — accounting providers return specific reasons (a missing account mapping, a locked period, a validation error on one document).
3. **Fix the cause**: reconnect the integration, correct the account/VAT mapping in the integration settings, or amend the document that's being rejected.
4. **Re-run the sync** for the affected documents from the integration page.
5. Once no failures occur in the detector's 24-hour window, the exception **auto-resolves**.

**Why it's urgent-ish but not P1**

Your operational flow (selling, packing) continues — but every day the books drift, the eventual reconciliation gets harder. Don't let these hit their 72-hour resolve deadline.`,
    tips: [
      "One bad document can fail repeatedly and dominate the exception — fix or exclude that document rather than retrying the whole batch forever.",
      "Locked accounting periods are a classic silent cause after year-end — check the period lock date in your accounting package.",
      "Repeated auth failures right after reconnecting usually mean the accounting user who authorised lacks permissions on the provider side.",
    ],
    relatedIds: ["ops-inbox-category-marketplace", "ops-inbox-troubleshooting", "ops-inbox-worked-example-tiktok"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-category-compliance",
    title: "Compliance exceptions",
    emoji: "📜",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "The compliance category: what belongs here and how to prepare routing rules ahead of detectors.",
    body: `## 📜 Compliance in the exception model

The *compliance* category exists for obligations with deadlines and consequences — the kind of problem that absolutely should live in an SLA-tracked inbox rather than someone's memory.

**Today**

There is no always-on automatic compliance detector yet. The category is live in the framework: rules can match it, and any compliance detectors added later raise exceptions straight into your existing triage flow.

**What will belong here**

Think of conditions like expiring regulatory documents, missing customer verification on qualifying transactions, or record-keeping gaps — anything where "we didn't notice in time" is itself the failure.

**Prepare now**

- Create a rule matching category *compliance* that assigns to whoever owns compliance and (if appropriate) raises severity — compliance items rarely deserve a quiet default.
- Agree internally that compliance exceptions are never dismissed without a note explaining the decision — the dismissal note is your audit trail.`,
    tips: [
      "Compliance exceptions are the strongest case for the Waiting (external) state — regulators and verifiers move on their own clocks.",
      "Use dismissal notes rigorously in this category; they're retained on the exception permanently.",
      "A P5 Info compliance exception is still an exception — it has a 30-day resolve SLA precisely so 'for awareness' items can't rot forever.",
    ],
    relatedIds: ["ops-inbox-rules", "ops-inbox-resolution-states", "ops-inbox-category-returns"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-category-infrastructure",
    title: "Infrastructure exceptions (queue backlogs)",
    emoji: "🖥️",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "The queue-backlog detector: processing queue depth thresholds, dead letters, and what to do about them.",
    body: `## 🖥️ When the machinery behind the scenes clogs up

**The queue-backlog detector**

Background work (imports, media processing, document generation) flows through processing queues. The detector raises:

- **P3 Medium** when a queue's depth reaches **100+** pending jobs, or when any **dead-lettered** jobs exist (jobs that failed repeatedly and were parked).
- **P1 Urgent** when depth reaches **500+** — work is now visibly delayed.

**What a backlog means for you**

Nothing is lost — jobs wait their turn — but the things those jobs produce (imported orders, generated documents, processed images) arrive late. Dead letters are different: those jobs have *stopped retrying* and the work they represent will not complete without attention.

**Working it**

1. Check whether the backlog is draining (occurrence history + refreshed detail) — a burst after a big import often clears itself, and the exception will auto-resolve when it does.
2. If it's growing, look for the cause upstream: a huge import just started, or a downstream dependency (an external API) failing and forcing retries.
3. Dead letters always deserve a look — they usually indicate one poison job or a systematic error worth reporting.

**Who handles these**

Infrastructure exceptions are typically for your admin/owner rather than day-to-day operators. A routing rule on category *infrastructure* keeps them off the operational team's plate.`,
    tips: [
      "A backlog that auto-resolved repeatedly (check past resolved exceptions) is a capacity signal, not a one-off.",
      "Dead letters never fix themselves — treat any dead-letter exception as real even when the queue depth looks healthy.",
      "Snooze a burst-related backlog exception for a couple of hours rather than dismissing it — if it hasn't drained by then, you'll want to know.",
    ],
    relatedIds: ["ops-inbox-deduplication", "ops-inbox-rules", "ops-inbox-troubleshooting"],
    pageIds: ["exceptions"],
  },
  {
    id: "ops-inbox-best-practices",
    title: "Operations Inbox best practices",
    emoji: "🏆",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "A daily routine, team conventions and rule hygiene that keep the inbox trustworthy.",
    body: `## 🏆 Habits that keep the inbox worth trusting

**A daily routine that works**

1. **Morning:** check the P0 and P1 tabs first. Acknowledge and assign anything new.
2. **Then:** switch the assignee filter to **Unassigned** — get every active P0–P2 owned by someone.
3. **During the day:** work from **Assigned to me** + Active.
4. **Weekly:** review Resolved and Dismissed for patterns — recurring exceptions are process bugs wearing a costume.

**Team conventions worth adopting**

- **Acknowledge means "I've got it."** Never acknowledge an exception you don't intend to own or hand off explicitly.
- **Always leave a note** on resolve and dismiss. Six months later, the note is the only memory anyone has.
- **Waiting states are honest states.** Use them for genuine external blockage — never as a place to park work you haven't started (that's what the queue itself is for).
- **Don't fight the detector.** If an exception keeps being raised after you resolve it, the condition still exists — fix the data, or if the condition is genuinely tolerated, use a rule (notify off / auto-resolve) instead of serial dismissal.

**Rule hygiene**

- Keep rules few and legible — a dozen precise rules beat fifty overlapping ones (remember: first match wins).
- Re-test rules after big workflow changes with the built-in Test button.
- Review any rule with notifications off quarterly: is the condition still tolerated, or has it become real risk?

**Watch these health signals**

- Escalations occurring at all → exceptions aren't being acknowledged in time.
- A growing Dismissed count in one category → either a noisy detector needing a rule, or risk being ignored.
- Unassigned P2+ exceptions older than a day → ownership gap.`,
    tips: [
      "The inbox works best as the single place operational problems live — resist tracking the same issues in a spreadsheet on the side.",
      "Urgency ties are broken by recency — trust the sort order rather than cherry-picking.",
      "New team member? Send them the walkthrough (Help drawer → Take the tour) before their first triage shift.",
    ],
    relatedIds: ["ops-inbox-assigning", "ops-inbox-rules", "ops-inbox-escalation"],
    pageIds: ["exceptions", "exception-rules"],
  },
  {
    id: "ops-inbox-troubleshooting",
    title: "Troubleshooting the Operations Inbox",
    emoji: "🔧",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "Fixes for the common puzzles: exceptions that keep coming back, actions you can't see, filters hiding items, and severities that look wrong.",
    body: `## 🔧 When the inbox itself is the puzzle

**"I resolved it but it came straight back"**

The detector re-checked and the underlying condition still exists — resolving an exception doesn't fix the problem it describes. Fix the data (reconnect the token, correct the stock, settle the orders). If the condition is genuinely acceptable, use a rule with notifications off and an auto-resolve window instead of resolving on repeat.

**"I can't see the Actions section"**

Workflow actions (assign, acknowledge, resolve…) need the *manage exceptions* permission; viewing needs only *view exceptions*. The Rules button needs *manage rules*. Ask an admin to review your role.

**"An exception disappeared"**

Check the status filter — the default view is **Active**. It was probably resolved (perhaps auto-resolved by its detector), dismissed by a teammate, or snoozed. Set status to **All statuses** and search; the drawer shows exactly what happened, when, and any note.

**"The severity looks wrong"**

Check three things in order: (1) has an exception **rule** overridden the detector default? (2) has it **escalated** (drawer shows "Escalated ×N")? (3) is the detector default simply not right for your business — in which case set a rule.

**"Why did nobody get notified?"**

A matching rule may have notifications switched off, and repeat occurrences of an already-active exception deliberately never re-notify. Check the Rules screen and the exception's occurrence history.

**"Snooze won't accept my value"**

Snooze needs a positive number of hours — the wake-up time must be in the future.

**"The counts on the severity tabs don't match my list"**

Tabs count within your current status filter; your list may additionally be narrowed by category, assignee or search text. Clear the filters to reconcile.`,
    tips: [
      "The occurrence history is your best diagnostic — it timestamps every detection so you can correlate with imports, syncs and deploys.",
      "Auto-resolved exceptions show resolution kind 'auto' in the drawer footer — that's the detector confirming the fix, not a person.",
      "Still stuck? Ask the AI assistant in the Help Centre — it can look at your live exception data.",
    ],
    relatedIds: ["ops-inbox-deduplication", "ops-inbox-resolution-states", "ops-inbox-faq"],
    pageIds: ["exceptions", "exception-rules"],
  },
  {
    id: "ops-inbox-glossary",
    title: "Operations Inbox glossary",
    emoji: "📖",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary: "Every term used in the Operations Inbox, defined in one place.",
    body: `## 📖 The vocabulary, A to Z

| Term | Definition |
|------|-----------|
| Acknowledge | One-click signal that a person has seen and accepted an exception; stops the first escalation trigger |
| Active | Any non-terminal status: Open, Acknowledged, Snoozed, Waiting (external), Waiting (customer) |
| Affected records | The orders, items, jobs or integrations an exception concerns — linked directly from the detail drawer |
| Aggregate (root-cause) exception | A single exception representing a whole problem class (e.g. all oversold SKUs) rather than one per record |
| Auto-resolve | An exception closed automatically — by its detector confirming the condition cleared, or by a rule's quiet-period timer |
| Category | The business area of an exception: inventory, sales, marketplace, packing, returns, finance, compliance or infrastructure |
| Deduplication | Folding repeat detections of the same problem into one exception (per detector + entity) instead of raising duplicates |
| Detector | An automatic check that watches for a failure condition and raises exceptions when it trips |
| Dismiss | Close an exception without fixing it — false positive or accepted risk; note recommended |
| Escalation | Automatic severity bump + admin re-alert when an exception breaches its acknowledge or resolve SLA (maximum two bumps) |
| Exception | An operational failure needing human action, tracked with severity, urgency, SLAs, ownership and history |
| Exception rule | An admin-defined rule applied when an exception is first raised: override severity, auto-assign, control notification, schedule auto-resolve |
| Occurrence | One detection event of an exception's underlying condition; the count shows as ×N |
| Reopen | Return a closed (or snoozed/waiting) exception to Open; from a terminal state this resets the SLA deadlines |
| Resolve | Close an exception because the underlying problem is fixed; note recommended |
| Severity | The P0 (Critical) → P5 (Info) band describing how bad an exception is; drives the SLA windows |
| SLA deadlines | The acknowledge-by / action-by / resolve-by timestamps derived from severity at raise time |
| Snooze | Hide an exception until a chosen time; it returns to Open automatically and SLAs are not extended |
| Terminal status | Resolved or Dismissed — the exception is closed; a recurrence opens a fresh exception |
| Urgency score | The 0–100 weighted blend of financial, customer, time, blockage, risk and repetition factors used to rank the queue |
| Waiting (customer) | Active status: blocked on the buyer; escalation paused |
| Waiting (external) | Active status: blocked on a third party; escalation paused |`,
    tips: [
      "Severity is the band, urgency is the rank — the two together decide list order.",
      "If a teammate uses a term differently, this table is the tie-breaker — it matches exactly how the system behaves.",
    ],
    relatedIds: ["ops-inbox-overview", "ops-inbox-faq"],
    pageIds: ["exceptions", "exception-rules"],
  },
  {
    id: "ops-inbox-faq",
    title: "Operations Inbox FAQ",
    emoji: "❓",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary: "Quick answers to the questions teams ask most about exceptions.",
    body: `## ❓ Quick answers

**Why is this critical? (Why is this exception P0?)**
Its detector (or one of your rules) judged the impact severe enough for the top band, or it escalated there after breaching SLAs unacknowledged. The drawer shows the severity, any "Escalated ×N" marker, and the urgency factors behind the score. See *Understanding Critical (P0) exceptions*.

**What's the deadline on an exception?**
Three deadlines are set from severity at raise time — acknowledge-by, action-by, resolve-by — all shown in the drawer, red when overdue. P0 is 1h/4h/24h; the full table is in *Deadlines and SLAs*.

**Do I have to acknowledge if I'm already assigned?**
Yes — they're separate signals. Only acknowledgement stops the first escalation.

**Does snoozing extend my deadlines?**
No. It hides the exception and pauses escalation until it wakes, but the original deadlines stand.

**Can I create an exception manually?**
No — detectors raise exceptions. For ad-hoc work items use Tasks; the inbox stays a trustworthy record of detected failures.

**Who gets notified when an exception is raised?**
Admins, by default, on first raise only (repeats never re-notify). A matching rule can switch notification off.

**Can I change what severity a detector uses?**
Yes — an exception rule matching that detector can set a different severity for all its future exceptions.

**What happens if I do nothing at all?**
It escalates: one severity bump past the acknowledge deadline, a second past the resolve deadline, each with an admin alert. It never silently disappears.

**Is dismissed data deleted?**
No. Resolved and dismissed exceptions are kept with their full history and notes — switch the status filter to see them.

**Why did an exception close on its own?**
Its detector confirmed the condition cleared (resolution kind "auto"), or a rule's auto-resolve window elapsed with no recurrence.

**We fixed the problem — must we resolve manually?**
Usually not: the detector auto-resolves on its next pass. Resolving manually with a note is still good practice when you want the fix documented.

**Why don't I see the Rules button?**
It requires the manage-rules permission. Viewing the inbox and managing exceptions are separate, lower permissions.`,
    tips: [
      "The severity tab counts answer 'is anything on fire?' faster than any report.",
      "Every answer above links back to a full article — use Related articles below to go deeper.",
    ],
    relatedIds: ["ops-inbox-overview", "ops-inbox-glossary", "ops-inbox-troubleshooting"],
    pageIds: ["exceptions", "exception-rules"],
  },

  // ── AI Agents / Autonomous Operations ─────────────────────────────────────
  {
    id: "agents-overview",
    title: "Introduction to AI Agents",
    emoji: "🤖",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "What AI Agents can and cannot do: they investigate, recommend and prepare — nothing executes without guardrails and (by default) your approval.",
    body: `## 🤖 Autonomous operations, with you in charge

AI Agents investigate your live data, explain what they find, and prepare actions for you to approve. They never get unrestricted access to your system.

**What an agent CAN do**

- Read your data through a fixed, per-agent whitelist of read-only tools (stuck orders, low stock, reorder candidates, low-margin items…).
- Write findings reports and recommendations.
- Prepare draft actions — a sync retry, a stock adjustment, a draft purchase order, a price-change draft — as **proposals** in the Approval Centre.
- Execute an approved proposal, with every deterministic guardrail checked first.

**What an agent CANNOT do**

- Touch anything outside its tool whitelist. The Operations Agent cannot read HR data; the Margin Agent cannot raise purchase orders.
- Execute a financial action (price change, purchase order, refund) without a human approval — ever, regardless of settings.
- Execute anything at all while Auto Execution is off (the default) — every proposal waits for you.
- Exceed your guardrails or cost limits — runs are refused before they start once a limit is hit.

**The four shipped agents**

| Agent | Watches | Can prepare |
|-------|---------|-------------|
| Operations | Stuck orders, failed marketplace syncs | Notes, task recommendations, sync retries |
| Inventory | Low / slow-moving / excess stock, SKU issues | Notes, tasks, mapping fixes, stock adjustments |
| Purchasing | Stock cover and sales velocity | Notes, tasks, draft purchase orders |
| Margin | Low-margin products | Notes, tasks, price-change drafts |

Every run, proposal, approval and execution is recorded in the audit trail.`,
    tips: [
      "Start every agent in Investigate mode for a week — read its findings before you let it prepare anything.",
      "The header of every proposal shows its risk level (0–5) and whether it's financial; financial always needs approval.",
      "Agents work only inside your own account — tenant isolation means they can never see another business's data.",
    ],
    relatedIds: ["agents-modes", "agents-risk-levels", "agents-approval-centre"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-modes",
    title: "Agent modes explained",
    emoji: "🎛️",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Ask, Investigate, Recommend, Prepare and Execute-with-approval — what each mode allows and when to use it.",
    body: `## 🎛️ Five modes, from read-only to (approved) action

You pick a mode every time you run an agent. The mode is a hard ceiling on what the run may produce.

**Ask (read-only answer)** — the agent answers your question from live data and stops. No proposals, nothing to approve. *"How many orders are stuck right now?"*

**Investigate (findings report)** — the agent digs into the data and writes a structured findings report. Still no proposals. *"Investigate why TikTok syncs failed this week."*

**Recommend (no prepared actions)** — the agent may propose only the lowest-risk items: notes (risk 0) and task recommendations (risk 1). Nothing that changes data. *"Recommend which slow movers to discount."*

**Prepare (draft proposed actions)** — the agent drafts real actions — stock adjustments, draft purchase orders, price-change drafts — as proposals in the Approval Centre. Nothing executes until approved. *"Prepare a draft PO for items running out within 14 days."*

**Execute with approval** — like Prepare, but if Auto Execution is switched on in Settings, low-risk **non-financial** proposals at or below your auto-execute risk ceiling may run immediately after passing every guardrail. Financial actions still queue for approval.

**Defaults per agent**

Operations and Inventory default to Investigate; Purchasing and Margin default to Prepare. You can change the mode on each run and on each schedule.`,
    tips: [
      "A schedule's mode works the same way — a daily Recommend schedule can never create a draft PO.",
      "Ask mode is free-form: use it as a live-data Q&A before committing to a deeper run.",
      "Execute-with-approval only differs from Prepare when Auto Execution is enabled in Settings — off by default.",
    ],
    relatedIds: ["agents-risk-levels", "agents-actions-vs-recommendations", "agents-schedules"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-risk-levels",
    title: "Risk levels 0–5 and approval defaults",
    emoji: "🚦",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Every proposed action carries a fixed risk level. Higher risk and anything financial always needs a human decision.",
    body: `## 🚦 A fixed risk score on every action

Risk levels are assigned by the system per action type — the agent cannot lower them.

| Risk | Action type | Financial? |
|------|-------------|-----------|
| 0 | Note | No |
| 1 | Task recommendation | No |
| 2 | Sync retry | No |
| 2 | Mapping fix | No |
| 3 | Stock adjustment | No |
| 4 | Price change draft | **Yes** |
| 4 | Draft purchase order | **Yes** |
| 5 | Refund draft | **Yes** |

**When approval is required**

- **Financial actions (risk 4–5) always require approval.** No setting changes this.
- With **Auto Execution off** (the default), *every* proposal requires approval — even a risk-0 note.
- With Auto Execution on, non-financial proposals at or below your **Auto-execute max risk** (0–3) may run automatically after guardrail checks; anything above it queues for approval.

**Reading the badges**

Risk badges are colour-coded: green (0–1), amber (2–3), red (4–5). A red "Financial" badge on a proposal means it can only ever execute through the Approval Centre.`,
    tips: [
      "Keep the auto-execute ceiling at 1 to start — notes and task recommendations are safe to automate first.",
      "Risk levels are per action type, not per run — one run can propose a risk-1 task and a risk-4 draft PO.",
      "Even auto-executed actions pass every guardrail first and land in the audit trail.",
    ],
    relatedIds: ["agents-modes", "agents-guardrails", "agents-approval-centre"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-permissions",
    title: "How agent permissions work",
    emoji: "🔐",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Agents inherit nothing beyond their tool whitelist; people need explicit AI Agents permissions; tenants are fully isolated.",
    body: `## 🔐 Two layers of permission — the agent's and yours

**The agent's own boundary**

Each agent has a fixed whitelist of read-only tools and allowed action types, set in the catalogue — not configurable per run. The Purchasing Agent can read reorder candidates and suppliers and prepare draft POs; it has no tool for prices, refunds or HR data. Restricted modules simply don't exist in an agent's toolbox.

**Your permissions**

Access to the module is split so you can give colleagues exactly what they need:

- **View AI Agents** — see the catalogue, runs and proposals.
- **Run agents** — start a run from the catalogue.
- **Approve** — approve, partially approve, reject, re-analyse and retry proposals in the Approval Centre.
- **Manage schedules** — create, edit, pause and delete recurring runs.
- **Manage settings** — change guardrails, cost limits and Auto Execution.

A user who can run agents but not approve can generate proposals that then wait for an approver.

**Tenant isolation**

Agents run entirely inside your own account's database. They cannot read, reference or affect any other business on the platform, and the AI Agents feature itself is switched on per account.`,
    tips: [
      "Give Run to analysts and Approve to managers — the separation is deliberate.",
      "If a colleague sees 'You don't have permission to view AI Agents', they need the view permission granted in user management.",
      "An agent's action-type badges on its catalogue card are its complete capability list — nothing hidden.",
    ],
    relatedIds: ["agents-overview", "agents-guardrails", "agents-admin-settings"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-guardrails",
    title: "Understanding AI Guardrails",
    emoji: "🛡️",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Deterministic, server-side limits checked before any action executes — max refund, max price change, max PO value, minimum margin, affected-record cap and marketplace allow-list.",
    body: `## 🛡️ Hard limits the AI cannot talk its way past

Guardrails are deterministic checks enforced on the server immediately before execution. They are not suggestions to the model — a failed guardrail blocks the action outright.

**The rules**

- **Max affected records** (default 50) — applies to every action; a proposal touching more records than this is blocked.
- **Allowed marketplaces** — when set, actions may only touch the listed channels; empty means all.
- **Max price change (%)** (default 15) — caps how far a price-change draft can move any price; 0 = no limit.
- **Minimum margin (%)** (default 5) — a price change may not push an item's margin below this floor.
- **Max PO value (£)** (default £5,000) — caps the total value of a draft purchase order; 0 = no limit.
- **Max refund (£)** (default £0) — caps refund drafts; the £0 default means refunds are never executable until you raise it.

**What "blocked" looks like**

A blocked action shows the exact failure, e.g. *Guardrail "max_po_value" failed*, plus the full per-rule checklist (✓/✗) on the proposal detail. Blocked actions never partially run.

**Guardrails vs approval**

Approval is a human decision; guardrails run even after you approve. Approving a proposal that breaches a guardrail results in "Approved — execution blocked", not a bypass.`,
    tips: [
      "Guardrails apply identically to auto-executed and manually approved actions.",
      "Tighten guardrails first, loosen later — a blocked action can always be re-run after you adjust the limit.",
      "The guardrail checklist on each proposal shows every rule evaluated, including the ones that passed.",
    ],
    relatedIds: ["agents-risk-levels", "agents-when-ai-cannot-act", "agents-admin-settings"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-approval-centre",
    title: "The Approval Centre, step by step",
    emoji: "✅",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Review a proposal's evidence, then approve (fully or partially), reject, or request a re-analysis — execution happens on approval.",
    body: `## ✅ Where proposals become decisions

Open **AI Agents → Approval Centre**. The filter defaults to **Awaiting approval**; other views show approved/executing, executed, failed/blocked and closed proposals.

**Reviewing a proposal**

Click any row. The detail shows:

- The agent's **reason** and **confidence**, the **data sources** it used, its **assumptions** and any **warnings**.
- The **affected records** — exactly what will change.
- The **guardrail checklist** — every rule already evaluated against the proposal.
- The **risk level** and a **Financial** badge where money is involved.

**Your options**

1. **Approve & execute** — the action runs immediately, guardrails checked one final time. The result comes straight back: executed, partial success, blocked or failed.
2. **Partial approval** — when a proposal covers several records, untick the ones you don't want; only ticked records execute.
3. **Reject** — closes the proposal without executing. Add a review note so the reasoning is on record.
4. **Request re-analysis** — sends the proposal back to the agent for a fresh run against current data; useful when things have changed since it was drafted.
5. **Retry execution** — available on failed or partially executed proposals; retries only what didn't complete.

Every decision records who made it, when, and any note — permanently.`,
    tips: [
      "Use partial approval instead of rejecting a mostly-good proposal — untick the outliers and execute the rest.",
      "A review note on a rejection teaches your team (and your future self) why — it's kept in the audit trail.",
      "Executed and partially-executed proposals show a per-record result list, so you can see exactly what happened to each item.",
    ],
    relatedIds: ["agents-evidence", "agents-failed-actions", "agents-risk-levels"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-evidence",
    title: "Agent evidence: reading a proposal like an auditor",
    emoji: "🔎",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Confidence, data sources, assumptions, warnings and affected records — what each evidence field means and how to weigh it.",
    body: `## 🔎 Never approve on trust — approve on evidence

Every proposal carries a structured evidence block. Here's how to read it.

**Confidence** — the agent's own estimate of how sure it is, as a percentage. Treat low confidence as an invitation to check the underlying data yourself, not as a reason to auto-reject.

**Data sources** — which datasets the agent read (e.g. reorder candidates, sales velocity, supplier list). If a source you'd expect is missing — say a proposal about pricing that never looked at margins — be sceptical.

**Assumptions** — what the agent had to guess: lead times it estimated, demand it extrapolated. Each assumption is a place reality can diverge.

**Warnings** — the agent flagging its own doubts: thin data, unusual outliers, borderline guardrails. A proposal with warnings deserves a slower read.

**Affected records** — the definitive list of what will change if you approve. This is also where partial approval operates: untick any record to exclude it.

**Tool calls (run detail)** — the full log of every data query the run made, with arguments and results, is on the run in Run history. When evidence seems off, the tool-call log shows exactly what the agent saw.`,
    tips: [
      "Cross-check one or two affected records against the live page (Inventory, Sales) before your first few approvals — calibrate your trust.",
      "Assumptions about lead times and demand are the most common source of over-ordering in draft POs.",
      "The raw payload on every proposal shows the exact data that would be written — nothing is hidden behind the summary.",
    ],
    relatedIds: ["agents-approval-centre", "agents-audit-trail", "agents-safety-best-practices"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-actions-vs-recommendations",
    title: "Actions vs recommendations",
    emoji: "⚖️",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Notes and task recommendations inform; sync retries, mapping fixes, stock adjustments and financial drafts change data. Know which is which.",
    body: `## ⚖️ Some proposals change nothing; some change your books

**Recommendations (risk 0–1)**

- **Note** — a written observation attached to the run. Executing it just files the note.
- **Task recommendation** — a suggested to-do for your team. Executing it creates the task; nobody is forced to do it.

Approving these carries essentially no operational risk — they inform humans.

**Operational actions (risk 2–3)**

- **Sync retry** — re-attempts a failed marketplace sync. Reversible by nature (it retries an operation that already should have happened).
- **Mapping fix** — corrects a SKU/listing mapping. Changes data; wrong mappings misroute future orders, so check the affected records.
- **Stock adjustment** — changes quantity on hand. Directly affects availability and stock value; approve only when you understand *why* the number is wrong.

**Financial drafts (risk 4–5)**

- **Price change draft** — feeds the Dynamic Pricing approval flow.
- **Draft purchase order** — creates a draft PO; it commits money once you progress it with the supplier.
- **Refund draft** — prepares a customer refund (blocked entirely until the max-refund guardrail is raised above £0).

Financial drafts always require approval and are also constrained by the money guardrails.`,
    tips: [
      "The action-type column in the Approval Centre tells you instantly which category you're deciding on.",
      "A 'draft' financial action still needs its normal downstream flow — a draft PO doesn't email your supplier by itself.",
      "Reversibility varies: a note is trivially reversible, a stock adjustment needs a counter-adjustment, an executed refund is real money.",
    ],
    relatedIds: ["agents-risk-levels", "agents-approval-centre", "agents-failed-actions"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-operations-agent",
    title: "The Operations Agent",
    emoji: "🧭",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Investigates stuck orders and failed marketplace syncs; proposes notes, tasks and sync retries.",
    body: `## 🧭 Your first responder for order-flow problems

**What it watches** — stuck orders, failed marketplace syncs and orders that need attention.

**Its tools** — read stuck orders, read sync failures, read a single order in detail, and propose actions. Nothing else.

**What it can prepare**

- **Notes** (risk 0) — e.g. "3 eBay orders stuck since Tuesday share the same courier error".
- **Task recommendations** (risk 1) — e.g. "Reconnect the TikTok Shop token".
- **Sync retries** (risk 2) — re-attempt a failed push once the underlying cause looks resolved.

**Default mode: Investigate** — out of the box it reports rather than proposes.

**Good instructions**

- "Investigate all orders that haven't moved status in 48 hours and group them by cause."
- "Find this week's failed syncs, identify which are transient, and prepare retries for those."

**What it will never do** — touch stock, prices, purchase orders or refunds; those aren't in its action list.`,
    tips: [
      "Run it after any marketplace outage — it separates transient failures (worth a retry) from real breakage (worth a task).",
      "Pair it with a daily morning schedule in Recommend mode for a standing triage report.",
      "A sync retry that keeps failing is a signal to fix the integration, not to keep approving retries.",
    ],
    relatedIds: ["agents-inventory-agent", "agents-schedules", "agents-modes"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-inventory-agent",
    title: "The Inventory Agent",
    emoji: "📦",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Watches stock health — low, slow-moving and excess stock, SKU issues — and can prepare mapping fixes and stock adjustments.",
    body: `## 📦 A stock-health analyst on demand

**What it watches** — low stock, slow-moving and excess stock, and SKU/mapping issues.

**Its tools** — read low-stock items, slow movers, items needing SKU review, a single inventory item, and propose actions.

**What it can prepare**

- **Notes and task recommendations** (risk 0–1).
- **Mapping fixes** (risk 2) — correct SKU/listing mappings flagged for review.
- **Stock adjustments** (risk 3) — align recorded quantity with reality when the evidence supports it.

**Default mode: Investigate.**

**Good instructions**

- "Investigate items with less than 7 days of stock cover at current sales velocity."
- "Find SKUs whose marketplace mappings look wrong and prepare fixes."
- "Which items have had no sales in 90 days and more than 20 units on hand?"

**A caution on stock adjustments** — an adjustment changes your stock valuation and availability everywhere. Read the affected records and the agent's assumptions before approving; if you're unsure, reject and count the shelf.`,
    tips: [
      "Use it before reordering — its excess-stock view stops you buying more of what isn't selling.",
      "Mapping fixes are high-leverage: one wrong mapping can misroute every future order for that listing.",
      "The max-affected-records guardrail caps a runaway adjustment — keep it modest for this agent.",
    ],
    relatedIds: ["agents-purchasing-agent", "agents-guardrails", "agents-actions-vs-recommendations"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-purchasing-agent",
    title: "The Purchasing Agent",
    emoji: "🧾",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Builds reorder suggestions from stock cover and sales velocity, and prepares draft purchase orders for approval.",
    body: `## 🧾 From "we're running low" to a draft PO

**What it watches** — stock cover and sales velocity, to build reorder suggestions.

**Its tools** — read reorder candidates, read suppliers, read a single inventory item, and propose actions.

**What it can prepare**

- **Notes and task recommendations** (risk 0–1).
- **Draft purchase orders** (risk 4, financial) — a complete draft PO with supplier, lines, quantities and estimated cost.

**Default mode: Prepare** — it's built to draft POs, but every draft still queues in the Approval Centre because POs are financial.

**Guardrails that apply** — the **Max PO value (£)** cap (default £5,000) blocks oversized drafts; **Max affected records** caps the line count.

**Good instructions**

- "Prepare a draft PO for products likely to run out in the next 14 days."
- "Which reorder candidates have a supplier on file, and what would restocking 30 days of cover cost?"

See the worked example article for a full end-to-end walkthrough of the 14-day run-out scenario.`,
    tips: [
      "Approving a draft PO creates the draft — you still review, send and receive it through the normal Purchase Orders flow.",
      "Check the agent's lead-time assumptions in the evidence block; they drive the quantities.",
      "Split huge restocks into multiple runs rather than raising the Max PO value guardrail in one jump.",
    ],
    relatedIds: ["agents-worked-example-po", "agents-margin-agent", "agents-guardrails"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-margin-agent",
    title: "The Margin Agent",
    emoji: "📉",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Finds low-margin products and prepares price-change drafts that feed the Dynamic Pricing approval flow.",
    body: `## 📉 Margin leaks, found and drafted for repair

**What it watches** — low-margin products across your catalogue.

**Its tools** — read low-margin items, read a single inventory item, and propose actions.

**What it can prepare**

- **Notes and task recommendations** (risk 0–1).
- **Price-change drafts** (risk 4, financial) — proposed new prices that feed into the Dynamic Pricing approval flow.

**Default mode: Prepare.**

**Guardrails that apply** — **Max price change (%)** (default 15) caps how far any single price can move; **Minimum margin (%)** stops a change dropping an item below your floor; **Allowed marketplaces** limits which channels can be touched.

**Two layers of approval for prices** — a price-change draft needs approval here, and the resulting change flows through Dynamic Pricing's own approval/margin-floor machinery. A price can't sneak through either gate.

**Good instructions**

- "Find items selling below 10% margin and prepare corrections up to the guardrail cap."
- "Which products lost margin after the last cost update?"`,
    tips: [
      "If costs are stale, fix landed costs first — the agent's margin maths is only as good as your cost data.",
      "Its drafts respect both the AI guardrails and the Dynamic Pricing hard floor — the tighter one wins.",
      "Run it after any bulk cost import to catch items whose prices no longer make sense.",
    ],
    relatedIds: ["agents-purchasing-agent", "pricing-overview", "agents-guardrails"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-schedules",
    title: "Scheduled agents",
    emoji: "⏰",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Run agents on a recurring hourly, daily or weekly cadence — with the same mode ceilings, guardrails and cost limits as manual runs.",
    body: `## ⏰ Standing instructions, on a clock

A schedule runs an agent automatically with a fixed instruction and mode.

**Setting one up** (requires the manage-schedules permission)

1. Open **AI Agents → Schedules → New schedule**.
2. Pick the agent, name it, and write the instruction exactly as you would for a manual run.
3. Choose the mode — Investigate, Recommend, Prepare or Execute with approval.
4. Pick the frequency: **hourly**, **daily** (at an hour you choose, UTC) or **weekly** (day + hour, UTC).

The next-run time is computed immediately and shown in the list.

**How scheduled runs behave**

- Identical rules to manual runs: same mode ceiling, same guardrails, same approval requirements. A scheduled Prepare run queues proposals in the Approval Centre like any other.
- **Cost limits pause schedules.** When your daily or monthly cost limit is reached, runs are refused and schedules are paused with a reason shown on the row. Re-enable the schedule (or wait for the window to reset) to resume.
- The master **Scheduled runs enabled** switch in Settings turns all scheduling off at once.

**Times are UTC** — a "Daily 06:00" schedule runs at 6am UTC, not local time.`,
    tips: [
      "A daily Recommend-mode schedule per agent gives you a standing morning briefing without any execution risk.",
      "Check the Paused badge if a schedule seems dead — the reason (usually a cost limit) is printed on it.",
      "Weekly schedules use day-of-week 0 = Sunday; the picker handles this for you.",
    ],
    relatedIds: ["agents-cost-controls", "agents-modes", "agents-admin-schedules"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-cost-controls",
    title: "Cost controls and budgets",
    emoji: "💰",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Daily and monthly spend caps, per-run token budgets and a concurrency limit keep AI costs predictable — runs are refused, never overdrawn.",
    body: `## 💰 The AI can never spend more than you allow

Four independent controls cap agent costs (defaults in brackets):

- **Daily cost limit** (£2) — total agent spend per day; once reached, new runs are refused and schedules pause until tomorrow.
- **Monthly cost limit** (£25) — the same, per calendar month.
- **Per-run token budget** (60,000 tokens) — caps how much work a single run can do; a run that hits its budget stops cleanly with whatever it found.
- **Max concurrent runs** (2) — stops a pile-up of simultaneous runs.

Setting a cost limit to 0 removes that limit.

**Refusal, not surprise bills** — limits are checked *before* a run starts, reserving its worst-case cost. If the reservation would breach a limit, the run is refused with a clear message. You can never wake up to an overdrawn budget.

**Watching spend** — the catalogue header shows today's and this month's spend against the limits, with a red badge when a limit is exhausted. Every run's token count and cost appear per-run in Run history.`,
    tips: [
      "The £2/day default is deliberately tight — raise it once you know your typical per-run cost from Run history.",
      "Cost-paused schedules resume when you re-enable them after the window resets — they don't silently restart.",
      "Per-run cost scales with how much data the instruction makes the agent read — narrow instructions are cheaper.",
    ],
    relatedIds: ["agents-schedules", "agents-audit-trail", "agents-admin-settings"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-audit-trail",
    title: "The agent audit trail",
    emoji: "📜",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Every run, tool call, proposal, decision and execution is permanently recorded — with who, when and what.",
    body: `## 📜 Everything the AI does is on the record

**Run history** (AI Agents → Runs) keeps every run: agent, trigger (manual or scheduled), mode, instruction, status, token count and cost. Open a run to see:

- The full **summary/findings** it produced.
- Every **tool call** it made — which dataset it read, with what arguments, and what came back.
- Every **proposal** the run generated, with status and risk.

**Decision records** — every approve, partial approve, reject, re-analyse and retry is logged with the acting user, timestamp and any review note. So are settings changes and schedule create/edit/delete.

**Execution results** — executed and partially executed proposals carry a per-record result list (which records succeeded, which failed and why).

**Why this matters** — when someone asks "why did stock change on this SKU?" or "who approved that PO draft?", the answer is a click away: run → proposal → decision → execution, each step attributed and timestamped.`,
    tips: [
      "The tool-call log is the ground truth for 'what did the AI actually look at' — use it when a proposal seems oddly reasoned.",
      "Filter the Approval Centre to Executed to review recent AI-driven changes as a batch.",
      "Blocked and failed runs are kept too — absence of action is also on the record.",
    ],
    relatedIds: ["agents-evidence", "agents-approval-centre", "agents-safety-best-practices"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-when-ai-cannot-act",
    title: "When AI cannot act",
    emoji: "🚫",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "The four reasons an agent is stopped — feature off, missing permission, guardrail failure, or pending approval — and what each looks like.",
    body: `## 🚫 Four different "no"s, four different fixes

**1. The feature is off** — AI Agents is enabled per account. When it's off, the module is hidden entirely: no pages, no schedules, no runs. Fix: an administrator enables the feature.

**2. You lack a permission** — you can see the module but a button is missing or disabled: no Run buttons without the run permission, no Approve/Reject without approve, read-only Settings without manage-settings. Fix: ask an admin to grant the specific permission.

**3. A guardrail failed** — the run or action was refused by a deterministic limit. You'll see the exact reason: a cost limit reached ("Run blocked"), *Guardrail "max_po_value" failed*, too many affected records, a disallowed marketplace. Fix: adjust the guardrail in Settings (if appropriate!) or narrow the proposal, then re-run or retry.

**4. Approval is pending** — the action exists but is waiting in the Approval Centre. This is the system working as designed: financial actions always wait, and with Auto Execution off everything waits. Fix: an approver decides.

**Reading the message** — blocked runs and actions always carry a specific reason on their detail view. There is no silent failure mode; if nothing happened, one of these four reasons is recorded somewhere you can see.`,
    tips: [
      "'Run blocked' at start time is almost always a cost limit or concurrency cap — check the usage header on the catalogue.",
      "A blocked action after approval means a guardrail — the checklist on the proposal shows which rule and why.",
      "Proposals superseded by a re-analysis close automatically — that's housekeeping, not an error.",
    ],
    relatedIds: ["agents-guardrails", "agents-cost-controls", "agents-permissions"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-failed-actions",
    title: "Recovering from failed actions",
    emoji: "🩹",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Partial success, retries and idempotency — what happens when an approved action doesn't fully execute, and how to recover safely.",
    body: `## 🩹 When execution doesn't go to plan

**Partial success** — a proposal covering many records can succeed on some and fail on others. Its status becomes **partially executed**, and the per-record results list shows exactly which records completed and which failed, with the error for each.

**Retrying** — failed and partially executed proposals show a **Retry execution** button in the Approval Centre. Retry re-attempts only the records that didn't complete — already-executed records are not run twice. That idempotency means retrying is safe: you cannot double-adjust stock or double-create a draft PO by retrying.

**Blocked ≠ failed** — a *blocked* action was stopped by a guardrail before doing anything; nothing partial happened, and there's nothing to roll back. A *failed* action attempted execution and hit an error.

**A sensible recovery sequence**

1. Open the proposal and read the per-record results and execution error.
2. If the cause was transient (a marketplace hiccup, a timeout) — **Retry**.
3. If the cause was real (record deleted, mapping gone) — fix the underlying data, then Retry, or Reject the remainder with a note.
4. If the world has moved on since the proposal was drafted — **Request re-analysis** for a fresh proposal against current data.

Everything you do here is captured in the audit trail.`,
    tips: [
      "Retry is per-remaining-record, so a 48/50 partial success retries just the 2 stragglers.",
      "Repeated failures on the same record point at the record, not the agent — check it in its own module.",
      "Re-analysis supersedes the old proposal rather than editing it — the original stays on the record.",
    ],
    relatedIds: ["agents-approval-centre", "agents-when-ai-cannot-act", "agents-audit-trail"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-safety-best-practices",
    title: "AI safety best practices",
    emoji: "🦺",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "A practical checklist for adopting agents safely: start read-only, tighten guardrails, review evidence, expand trust gradually.",
    body: `## 🦺 Trust is earned in stages

**Week one: read-only** — run every agent in Investigate mode. Read its findings against what you know. You're calibrating the agent's judgement — and your own instructions.

**Week two: recommendations** — move to Recommend mode. Approve notes and task recommendations. Still nothing changes data.

**Then: prepared actions, reviewed hard** — allow Prepare mode. For every proposal: read the confidence, the assumptions, the warnings, and spot-check affected records against the live pages. Use partial approval liberally.

**Only later, maybe: auto-execution** — if you enable Auto Execution, start with the max-risk ceiling at 1 (notes and tasks). Raise it one level at a time, weeks apart, and only for action types whose proposals you've been approving unchanged.

**Standing hygiene**

- Keep cost limits tight enough that a runaway schedule is an annoyance, not a bill.
- Keep **Max refund at £0** until you have a real, reviewed refund workflow.
- Revisit guardrails after business changes — a £5,000 PO cap set in quiet season may be wrong in peak.
- Audit the Executed filter weekly: review what the AI changed as a batch.
- Separate duties: the person who writes instructions shouldn't be the only approver.

**The golden rule** — never approve a proposal you couldn't explain to a colleague. The evidence block exists so you can.`,
    tips: [
      "If you keep editing an agent's proposals the same way, change the instruction — encode the correction upstream.",
      "Re-analysis is cheap; a bad execution is not. When in doubt, re-analyse.",
      "Auto Execution can be switched off at any time without losing pending proposals.",
    ],
    relatedIds: ["agents-guardrails", "agents-evidence", "agents-cost-controls"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-worked-example-po",
    title: "Worked example: a draft PO for 14-day run-outs",
    emoji: "🧪",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary:
      "Follow one instruction — 'Create a PO for products likely to run out in 14 days' — from investigation to executed draft PO.",
    body: `## 🧪 One instruction, end to end

**The instruction** — you open the Purchasing Agent, keep its default **Prepare** mode, and run: *"Create a PO for products likely to run out in the next 14 days."*

**1. Investigation** — the agent reads your reorder candidates: current quantity on hand versus sales velocity. Its tool calls (visible later in Run history) fetch the candidate list and drill into individual items.

**2. Product selection** — it keeps items whose stock cover is under 14 days and drops items with adequate cover or no meaningful sales history, noting any borderline calls as assumptions.

**3. Lead times** — it reads your supplier records and factors expected lead time: an item with 12 days of cover and a 10-day lead time is more urgent than one with 8 days of cover and next-day supply. Estimated lead times appear in the assumptions.

**4. Quantities** — it sizes order quantities to restore a sensible cover window, based on the same velocity data, and lists per-item reasoning.

**5. Cost** — it prices the lines from your cost data and totals the PO. If the total exceeds your **Max PO value** guardrail, the proposal will be blocked at execution — the guardrail checklist on the proposal shows the check.

**6. Draft PO proposal** — the run finishes with a **Draft purchase order** proposal (risk 4, Financial) in the Approval Centre: supplier, lines, quantities, unit costs, total, plus confidence, data sources, assumptions and warnings.

**7. Approval** — you review the evidence, untick any line you disagree with (partial approval), add a note, and click **Approve & execute**.

**8. Execution** — the draft PO is created in your Purchase Orders module. From there it follows the normal PO flow: review, send to the supplier, receive stock. The run, proposal, your decision and the created draft are all in the audit trail.`,
    tips: [
      "Sanity-check the two or three biggest lines against the Inventory page before approving — velocity spikes inflate quantities.",
      "If the proposal is blocked on max_po_value, prefer splitting the order over raising the cap in one jump.",
      "Approving creates a *draft* PO — money moves only when you progress it with the supplier.",
    ],
    relatedIds: ["agents-purchasing-agent", "agents-approval-centre", "purchase-orders-overview"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-glossary",
    title: "AI Agents glossary",
    emoji: "📚",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary: "Every term the module uses — run, proposal, mode, guardrail, evidence, partial execution — defined in one place.",
    body: `## 📚 The vocabulary, in one place

**Agent** — a specialised AI worker with a fixed tool whitelist and allowed action types (Operations, Inventory, Purchasing, Margin).

**Run** — one execution of an agent against one instruction, in one mode. Manual or scheduled.

**Mode** — the ceiling on what a run may produce: Ask, Investigate, Recommend, Prepare, Execute with approval.

**Instruction** — the plain-English brief you give the run.

**Tool** — a read-only data query an agent is allowed to make (e.g. get low-stock items). Tool calls are logged per run.

**Proposal / action** — a prepared change created by a run, waiting in the Approval Centre. Statuses: proposed, approved, rejected, re-analysis requested, executing, executed, partially executed, failed, blocked, superseded.

**Risk level** — the fixed 0–5 score of an action type. 4–5 are financial.

**Financial action** — price change draft, draft purchase order, refund draft. Always requires human approval.

**Guardrail** — a deterministic server-side limit checked before execution (max refund, max price change, max PO value, minimum margin, max affected records, allowed marketplaces).

**Auto Execution** — the optional setting allowing non-financial actions at or below a risk ceiling to execute without approval. Off by default.

**Evidence** — a proposal's confidence, data sources, assumptions, warnings and affected records.

**Partial approval** — approving only a subset of a proposal's affected records.

**Partial execution** — an execution where some records succeeded and some failed; retry covers only the remainder.

**Re-analysis** — sending a proposal back for a fresh run against current data; the old proposal is superseded.

**Blocked** — stopped by a guardrail or cost limit before anything changed.

**Schedule** — a recurring run (hourly/daily/weekly, UTC) with a fixed instruction and mode.

**Cost limits** — daily and monthly spend caps plus a per-run token budget and a concurrency cap; runs are refused, never overdrawn.`,
    tips: [
      "Blocked = nothing happened; failed = an attempt errored; partially executed = some records completed.",
      "Superseded proposals were replaced by a re-analysis — they're closed, not lost.",
    ],
    relatedIds: ["agents-overview", "agents-faq"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-faq",
    title: "AI Agents — frequently asked questions",
    emoji: "❓",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    summary: "Quick answers: can it spend money by itself, can I undo, why was my run refused, is my data shared?",
    body: `## ❓ Straight answers

**Can an agent spend money without me?** No. Price changes, purchase orders and refunds are financial actions and always require approval, whatever your settings. Refunds are additionally capped at £0 by default, which blocks them entirely.

**Can I undo an executed action?** Depends on the type. Notes and tasks — trivially. A sync retry — nothing to undo. A stock adjustment — make a counter-adjustment. A draft PO — delete the draft. A price change — reprice via Dynamic Pricing. The audit trail tells you exactly what was changed and by which run.

**Why was my run refused before it started?** A cost limit (daily/monthly) is exhausted or the max-concurrent-runs cap is hit. The catalogue header shows your spend against limits.

**Why is a proposal blocked after I approved it?** A guardrail failed at execution time — the proposal's guardrail checklist shows which rule. Approval never bypasses guardrails.

**Do agents see other businesses' data?** No. Runs are confined to your own account's data. Tenant isolation is structural, not a setting.

**Why can't I see the AI Agents pages at all?** The feature is off for your account, or you lack the view permission.

**Do scheduled runs behave differently from manual ones?** No — same modes, guardrails, approvals and costs. The only difference is the trigger.

**What happens if a run hits its token budget mid-way?** It stops cleanly and keeps whatever findings and proposals it completed; nothing half-written executes.

**Who can approve?** Only users with the approve permission. Running and approving are separate permissions on purpose.

**Does re-analysis change the original proposal?** No — it supersedes it. The original stays in the record with its status.`,
    tips: [
      "Most 'the AI did nothing' reports are a cost limit or a pending approval — check the usage header and the Awaiting approval filter first.",
      "Search this Help Centre for 'agent approval', 'guardrail' or 'risk level' to jump to the detailed articles.",
    ],
    relatedIds: ["agents-glossary", "agents-when-ai-cannot-act", "agents-overview"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-admin-settings",
    title: "Configuring guardrails & Auto Execution (admins)",
    emoji: "⚙️",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.settings.manage",
    summary:
      "Field-by-field reference for the Settings tab: every guardrail, cost limit and the Auto Execution switch, with defaults and safe values.",
    body: `## ⚙️ The settings reference (managers only)

All values live under **AI Agents → Settings** and apply account-wide, to manual and scheduled runs alike. Changes are audit-logged.

**Guardrails**

- **Max refund (£)** — default 0. 0 means refund drafts can never execute. Raise only with a reviewed refund workflow.
- **Max price change (%)** — default 15. Caps any single price move; 0 = no limit.
- **Max PO value (£)** — default 5,000. Caps a draft PO's total; 0 = no limit.
- **Minimum margin (%)** — default 5. Price changes may not push an item below this floor.
- **Max affected records** — default 50. Applies to every action type.
- **Allowed marketplaces** — comma-separated channel list (e.g. ebay, shopify, tiktok_shop). Empty = all allowed.

**Auto Execution**

- **Auto Execution switch** — default off. Off = every proposal requires approval. On = non-financial proposals at or below the ceiling may run automatically after guardrail checks.
- **Auto-execute max risk (0–3)** — the ceiling. Financial actions (risk 4–5) are outside it by construction.

**Cost controls**

- **Daily cost limit (£)** — default 2. **Monthly cost limit (£)** — default 25. 0 = no limit. Reaching a limit refuses new runs and pauses schedules.
- **Per-run token budget** — default 60,000.
- **Max concurrent runs** — default 2.
- **Scheduled runs enabled** — master switch for all schedules.

**Change discipline** — adjust one value at a time and watch the next few runs. Loosening a guardrail applies to every subsequent execution, including auto-executed ones.`,
    tips: [
      "Settings changes take effect immediately — including for proposals approved after the change.",
      "0 means 'no limit' for the money caps but 'never' for max refund context — read each hint carefully.",
      "Every settings save is written to the audit log with who and when.",
    ],
    relatedIds: ["agents-guardrails", "agents-cost-controls", "agents-admin-schedules"],
    pageIds: ["ai-agents"],
  },
  {
    id: "agents-admin-schedules",
    title: "Managing agent schedules (admins)",
    emoji: "🗓️",
    module: "AI Agents",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.schedules.manage",
    summary:
      "Create, edit, pause and delete recurring runs; understand UTC timing, pause reasons and safe scheduling patterns.",
    body: `## 🗓️ Running the recurring fleet (schedule managers only)

**Creating** — Schedules → New schedule: agent, name, instruction, mode, frequency (hourly / daily / weekly), hour (UTC) and, for weekly, day of week. New schedules default to Recommend mode, daily at 06:00 UTC — deliberately conservative.

**Editing & pausing** — the Enabled switch on each row starts/stops it; editing timing recomputes the next run. A schedule the system paused (cost limit) shows a **Paused** badge with the reason; flipping it back on clears the pause once the limit window has reset.

**Deleting** — removes the schedule; its past runs stay in Run history.

**Safe patterns**

- Stagger hours so multiple schedules don't collide with the max-concurrent-runs cap.
- Prefer daily over hourly unless the data genuinely changes hourly — hourly schedules multiply cost 24×.
- Keep standing schedules in Investigate/Recommend mode; use Prepare-mode schedules only for flows with an active approver (a Prepare schedule with nobody approving just accumulates proposals).
- Name schedules for their intent ("Morning ops triage"), not their mechanics.

**Auditability** — every create, edit and delete is logged with the acting user; scheduled runs are marked with the scheduled trigger in Run history.`,
    tips: [
      "All times are UTC — during British Summer Time a 06:00 UTC schedule runs at 07:00 UK time.",
      "The 'Scheduled runs enabled' master switch in Settings overrides every individual schedule at once.",
      "Review the proposals backlog before adding another Prepare-mode schedule — approvals should keep pace with proposals.",
    ],
    relatedIds: ["agents-schedules", "agents-admin-settings", "agents-cost-controls"],
    pageIds: ["ai-agents"],
  },

  // ── Cost layers / Lots (Task #2175) ────────────────────────────────────────
  // Gated on inventory_cost_layers_v2 + lots.view so these only surface for
  // tenants running the lot engine and users allowed to see lots.
  {
    id: "lots-how-cost-layers-work",
    title: "How cost layers (lots) work",
    emoji: "🧱",
    module: "Inventory",
    featureKey: FeatureKey.inventory_cost_layers_v2,
    requiredPermission: "lots.view",
    summary:
      "What a lot is, how receipts create them, and how FIFO, weighted average and specific identification decide which cost a sale draws down.",
    body: `## 🧱 Every unit remembers what it cost

A **cost layer** — we call it a **lot** — is a batch of stock recorded at the exact cost you paid for it. Instead of one blended "average cost" per SKU, each receipt keeps its own price, quantity and provenance, so you always know which pounds are still sitting on the shelf.

**How lots are created**

- **Receiving a purchase order** creates a lot for each received line, stamped with the landed cost (purchase + freight + duty) at receipt time.
- **Adding stock outside a PO** (manual stock-in, stock takes, returns to stock) creates a lot too, so nothing enters inventory without a cost attached.
- Each lot records where it came from — the PO, the supplier and the date — which is its **provenance**.

**How a sale draws down cost**

When you sell, the system consumes stock from your lots and takes their cost as the cost of goods sold. Which lot it consumes depends on your **costing method**:

| Method | Which lot is consumed | Best for |
|--------|-----------------------|----------|
| **FIFO** (first-in, first-out) | The oldest lot still holding stock | Most businesses — matches the physical flow of goods |
| **Weighted average** | A blend of every open lot's cost | Fungible stock where individual batches don't matter (e.g. bullion coins) |
| **Specific identification** | The exact lot you nominate | High-value, serialised or individually tracked items |

**Why it matters**

Because each sale is matched to a real lot cost, your profit, COGS and stock valuation reflect what you actually paid — not a rolling average that hides a cheap old batch behind an expensive new one. It also powers the lot reports, per-PO profitability and the Dynamic Pricing cost-basis choices.`,
    tips: [
      "A single SKU can hold several open lots at once — that's normal, and it's exactly what lets FIFO and specific identification work.",
      "Landed cost (not the PO estimate) is stamped on the lot when you receive — enter freight and duty accurately so every downstream report is right.",
      "Cancelled orders are excluded from COGS and profit, so voiding a sale returns its lot draw-down to stock.",
    ],
    relatedIds: ["lots-reading-reports", "lots-profitability", "lots-pricing-cost-basis"],
    pageIds: ["inventory"],
  },
  {
    id: "lots-reading-reports",
    title: "Reading the lot reports",
    emoji: "📑",
    module: "Reports",
    featureKey: FeatureKey.inventory_cost_layers_v2,
    requiredPermission: "lots.view",
    summary:
      "A tour of every lot report: valuation, value by lot/product/supplier, purchase history, cost movement, supplier cost comparison, stock ageing, sell-through and margin.",
    body: `## 📑 What each lot report tells you

The lot reports turn your cost layers into answers. Open them from **Reports** (each respects your date filter and only shows lots you're permitted to see).

**Valuation**

- **Inventory valuation by lot** — every open lot with its remaining quantity and remaining cost value; the true worth of what's on the shelf, batch by batch.
- **Value by lot / product / supplier** — the same value rolled up whichever way you need it: per individual lot, totalled per SKU, or totalled per supplier.

**Buying & cost trends**

- **Purchase history** — every lot you've received, with cost, quantity, PO and supplier, so you can see exactly what you bought and when.
- **Cost movement** — how your unit cost for a product has moved over time as newer lots arrived; spot creeping supplier prices early.
- **Supplier cost comparison** — the same product bought from different suppliers, side by side, so you can see who's really cheapest once landed cost is counted.

**Stock health**

- **Stock ageing** — open lots bucketed by how long they've been sitting, so slow movers and ageing capital surface before they become a problem.
- **Lot sell-through** — how much of each lot has sold versus how much remains; a fast-selling lot and a stagnant one look very different here.

**Profitability**

- **Margin by product / lot / PO / supplier** — realised profit and margin sliced whichever way you need. Revenue, COGS and profit all **exclude cancelled orders**, so the numbers reflect money actually taken.

**Reading them right**

Valuation and ageing views show the **current** position of open lots. The buying, sell-through and margin views respect the **date range** you pick, so widen the window if a report looks emptier than expected.`,
    tips: [
      "Cost movement and supplier cost comparison are the fastest way to justify switching supplier — they compare landed cost, not the sticker price.",
      "Stock ageing plus sell-through together tell you what to discount: old and barely-sold is your clearance list.",
      "If a report is empty, check the date filter and that the lot feature is enabled for your tenant — reports tolerate tenants with no lot data yet and simply show nothing.",
    ],
    relatedIds: ["lots-how-cost-layers-work", "lots-profitability", "lots-supplier-analytics"],
    pageIds: ["reports", "inventory"],
  },
  {
    id: "lots-profitability",
    title: "Per-PO and per-lot profitability, and comparing purchases",
    emoji: "💰",
    module: "Reports",
    featureKey: FeatureKey.inventory_cost_layers_v2,
    requiredPermission: "lots.view",
    summary:
      "See exactly how much each purchase order or lot earned, and put two lots or POs side by side to learn which buy was the better deal.",
    body: `## 💰 Did that buy actually pay off?

Because every sale is matched to the lot it drew down, MetalUI can trace profit all the way back to the batch — and the purchase order — it came from.

**Per-PO profitability**

For any purchase order you can see what it cost you to buy, what its stock has sold for, the COGS realised, and the resulting profit and margin. It answers the real question: *"was this order a good decision?"* — not just *"did it arrive?"*.

**Per-lot profitability**

Drill one level deeper to a single lot: revenue from units sold out of that lot, its share of COGS, remaining unsold value, and its realised margin so far. A lot that's half-sold shows both what it's earned and what's still tied up in it.

**Comparing two lots or two purchases**

Put two lots — or two POs — side by side to compare cost per unit, sell-through, days to sell and realised margin. It's how you decide which supplier, batch size or buying pattern to repeat, and which to avoid.

**The golden rule on the numbers**

Revenue, COGS and profit everywhere here **exclude cancelled orders** (status ≠ Cancelled), so a voided sale never inflates a lot's or a PO's performance. Consignment lots show your share, not the whole ticket.`,
    tips: [
      "A profitable-looking PO with poor sell-through still has cash locked in unsold lots — read profit and sell-through together.",
      "Comparing two lots of the same product is the cleanest A/B test of two suppliers or two buying moments.",
      "Per-lot profit updates as the lot sells down — a young lot's margin is provisional until it's cleared.",
    ],
    relatedIds: ["lots-reading-reports", "lots-supplier-analytics", "lots-how-cost-layers-work"],
    pageIds: ["reports", "inventory"],
  },
  {
    id: "lots-supplier-analytics",
    title: "Supplier analytics from your lots",
    emoji: "🏷️",
    module: "Suppliers",
    featureKey: FeatureKey.inventory_cost_layers_v2,
    requiredPermission: "lots.view",
    summary:
      "Use lot-level cost data to compare suppliers on landed cost, price stability, sell-through and the profit their stock actually delivers.",
    body: `## 🏷️ Which suppliers really earn their place?

Every lot remembers which supplier it came from, so your cost layers double as a scorecard for who you buy from.

**What you can compare across suppliers**

- **Landed cost per product** — the true cost once freight and duty are counted, so a "cheaper" quote that ships expensively is exposed.
- **Cost stability** — whether a supplier's prices are creeping up over time (cross-reference the Cost movement report).
- **Sell-through** — whose stock actually moves versus whose sits ageing on the shelf.
- **Realised profit and margin** — the profit their lots have delivered, cancelled orders excluded.

**Where to look**

- **Supplier cost comparison** and **Value by supplier** reports for the buying and valuation view.
- **Margin by supplier** for the profit view.
- The supplier's own page ties this back to their record and any consignment balance you owe them.

**Why it beats gut feel**

A supplier you "like" may be quietly your least profitable once ageing stock and landed cost are counted. The lot data settles the argument with your own numbers.`,
    tips: [
      "Pair Margin by supplier with Stock ageing — high margin but slow-moving stock ties up cash even from a 'good' supplier.",
      "Consignment lots credit the supplier their share automatically, so supplier margin here already nets off what you owe them.",
      "Supplier analytics only cover suppliers that appear on your lots — a supplier you've never received stock from won't show here.",
    ],
    relatedIds: ["lots-profitability", "lots-reading-reports", "suppliers-overview"],
    pageIds: ["suppliers", "reports"],
  },
  {
    id: "lots-cost-anomaly-exceptions",
    title: "Cost anomaly exceptions in the Operations Inbox",
    emoji: "🚨",
    module: "Operations Inbox",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    summary:
      "How MetalUI flags a lot received at a surprising cost, where the exception appears, and how to set the thresholds that trigger it.",
    body: `## 🚨 Catching a wrong cost before it ships

When a lot is received at a cost that looks off — far higher or lower than what you normally pay for that product — MetalUI raises a **cost anomaly** exception so it's caught before that price poisons your valuation, margin and pricing.

**What triggers it**

A new lot's landed cost is compared against the recent cost history for the same product. If it deviates beyond your configured threshold — a percentage swing, an absolute swing, or both — an exception is raised against the affected lot, PO and product.

**Where it appears**

In the **Operations Inbox** under the inventory category, with the affected lot and PO linked from the detail drawer. Like every exception it carries a severity, SLA deadlines and a full occurrence history, and it deduplicates so a re-check of the same lot won't spam you.

**How to configure the thresholds**

Set the tolerances in the exception rules for cost anomalies:

- **Percentage threshold** — how big a swing from the usual cost is acceptable (e.g. 25%).
- **Absolute threshold** — an optional £ swing floor, so trivial percentage jumps on cheap items don't cry wolf.
- **Direction** — flag increases, decreases, or both.

An exception rule can also override the severity, auto-assign the exception to your buyer, or set an auto-resolve window.

**What to do when one fires**

Open the drawer, check the lot's cost against the PO. If it's a genuine data error, correct the lot cost (with the right permission) and the exception auto-resolves on the next check. If the price really did move, resolve it with a note — that's your audit trail for the change.`,
    tips: [
      "Set the absolute threshold as well as the percentage one so low-value SKUs don't trigger a flood of false positives.",
      "A cost anomaly that keeps returning means the underlying lot cost is still wrong — fix the lot, don't just resolve the exception.",
      "Route cost anomalies to your buyer with an auto-assign rule so they land with the person who placed the PO.",
    ],
    relatedIds: ["ops-inbox-rules", "lots-how-cost-layers-work", "lots-reading-reports"],
    pageIds: ["exceptions", "exception-rules"],
  },
  {
    id: "lots-pricing-cost-basis",
    title: "Choosing a Dynamic Pricing cost basis",
    emoji: "🎯",
    module: "Dynamic Pricing",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    requiredPermission: "pricing.view",
    summary:
      "What each cost basis means — next FIFO, weighted average available, latest acquisition, replacement, manual and landed — and why provenance matters for your prices.",
    body: `## 🎯 Which cost should your prices be built on?

Dynamic Pricing works out the margin floor and every suggestion from a **cost basis** — the cost figure it treats as "what this item cost me". With cost layers enabled you can choose which cost the engine uses, and each choice tells a different story.

**The options**

- **Next FIFO** — the cost of the next lot that would be sold under FIFO (your oldest open lot). Prices reflect the actual cost you're about to draw down.
- **Weighted average available** — the blended cost across all open lots for the item. Smooths out lot-to-lot swings.
- **Latest acquisition** — the cost of your most recently received lot. Prices track the newest buy price — useful when costs are rising fast.
- **Replacement cost** — what it would cost you to buy the item again today (e.g. current spot plus your premium). Best when you intend to restock at the market rate rather than the historic one.
- **Manual** — a cost you set by hand on the item. Full control, but it's on you to keep it current.
- **Landed** — the item's stored landed cost, the classic single-cost figure. The safe default when you're not yet running lots.

**Provenance — knowing where the cost came from**

Whichever basis the engine uses, it records the **provenance** of that cost: which lot, PO or manual entry it was drawn from, and when. So when a price looks surprising you can trace it straight back to the exact cost figure — and the exact batch — behind it, rather than guessing.

**Choosing well**

- Costs rising steadily? *Latest acquisition* or *replacement* protects your margin fastest.
- Fungible stock with many small lots? *Weighted average available* avoids jumpy prices.
- Want prices to mirror the goods actually leaving the shelf? *Next FIFO*.
- Not on lots yet? Stay on *landed* until you are.`,
    tips: [
      "Every suggestion is still clamped to the margin floor regardless of cost basis — a low basis can't push a price below cost.",
      "Provenance is your 'why is this price this?' trail — the recommendation shows which lot or entry the cost came from.",
      "Switching cost basis changes future runs only; it never rewrites the price history of past changes.",
    ],
    relatedIds: ["lots-how-cost-layers-work", "pricing-glossary", "pricing-overview"],
    pageIds: ["pricing", "inventory"],
  },
  {
    id: "lots-glossary",
    title: "Cost layers & lots glossary",
    emoji: "📖",
    module: "Inventory",
    featureKey: FeatureKey.inventory_cost_layers_v2,
    requiredPermission: "lots.view",
    summary:
      "Definitions for every term the lot engine uses: FIFO, LIFO, COGS, landed cost, weighted average, specific identification, sell-through, ageing, true profit and more.",
    body: `## 📖 The vocabulary of cost layers

| Term | Definition |
|------|-----------|
| **Cost layer / Lot** | A batch of stock recorded at the exact cost you paid, kept separate from other batches so each unit remembers what it cost |
| **FIFO** (first-in, first-out) | Costing method that consumes your oldest open lot first — the default, matching how goods physically flow |
| **LIFO** (last-in, first-out) | Costing method that consumes your newest lot first; rarely used in the UK and not the MetalUI default |
| **Weighted average cost** | A single blended cost across all open lots of an item, re-weighted by quantity as lots come and go |
| **Specific identification** | Costing method where you nominate the exact lot a sale draws from — for high-value or serialised items |
| **COGS** (cost of goods sold) | The lot cost consumed by the units you've sold; revenue minus COGS is gross profit (cancelled orders excluded) |
| **Landed cost** | The full cost of getting an item on the shelf — purchase price plus freight and duty — stamped on the lot at receipt |
| **Replacement cost** | What it would cost to buy the item again today (e.g. current spot plus premium), rather than what you historically paid |
| **Cost basis** | The cost figure prices and margins are built from; with lots you choose which (next FIFO, weighted average, latest acquisition, replacement, manual, landed) |
| **Provenance** | The traceable origin of a cost — which lot, PO or manual entry it came from, and when |
| **Sell-through** | How much of a lot has sold versus how much remains — a measure of how fast a batch is clearing |
| **Stock ageing** | How long open lots have been sitting in stock, bucketed to surface slow movers and tied-up capital |
| **Consignment liability** | The balance you owe a consignment supplier for their stock that has sold — accrued automatically as lots sell down |
| **True profit** | Profit after configured costs such as marketplace fees, postage, affiliate payouts and consignment costs — not just revenue minus COGS |
| **True margin** | True profit expressed as a percentage of the sale price |`,
    tips: [
      "FIFO is the MetalUI default — reach for specific identification only when individual batches genuinely matter.",
      "True profit and true margin count fees and payouts that plain gross profit ignores — always compare like with like.",
      "If a teammate says 'cost', confirm which cost basis they mean — landed, replacement and next-FIFO can be very different numbers.",
    ],
    relatedIds: ["lots-how-cost-layers-work", "lots-pricing-cost-basis", "lots-reading-reports"],
    pageIds: ["inventory", "reports"],
  },

  // ── Customer Commerce Portal (Task #2251) ──────────────────────────────────
  // Every article here is gated on the single `customer_commerce_portal`
  // feature key plus a per-surface commerce permission, so help for a
  // capability a tenant hasn't been granted is never shown. The finer commerce
  // sub-entitlements (boxes, checkout, portal_editor, analytics, …) are
  // metadata on that one feature — not feature keys — so we gate the drawer by
  // the operator PERMISSION that guards the matching route, which is what an
  // operator actually needs to reach the surface the article describes.
  {
    id: "commerce-overview",
    title: "What the Customer Commerce Portal is",
    emoji: "🏬",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "Your own branded, customer-facing storefront on top of the stock you already hold — catalogue, Boxes, checkout, pricing and analytics in one place.",
    body: `## 🏬 Your own storefront, powered by your stock

The Customer Commerce Portal is a branded shopfront your customers browse and buy from directly. It sits on top of the same inventory, pricing and sales you already run — nothing is duplicated or synced from a third party.

**What your customers get**

- A **catalogue** of the products you choose to publish, with categories and curated collections.
- Their own **account**, so returning customers see prices and history that belong to them.
- A persistent **Box** — a basket that holds items (and can lock their price) across visits rather than emptying the moment they leave.
- A **checkout** that turns a submitted Box into a real sales order once payment is taken.

**What you get on the operator side**

- A no-code **portal page editor** to merchandise the home page and any other portal page from reusable blocks.
- **Commerce analytics** — product performance, the buying funnel, and per-customer activity.
- A **customer-facing FAQ** you control, including a prominent "How does my Box work?" explanation.

**How it's switched on**

The whole portal is one feature. Within it, individual capabilities — Boxes, checkout, pre-orders, the page editor, analytics and more — are entitlements your platform administrator can turn off one at a time without disabling the module. If you can't see a surface described in these articles, that capability is switched off for your tenant.`,
    tips: [
      "The commerce portal is separate from the anonymous order-tracking portal — turning one on doesn't affect the other.",
      "Publishing a product to the portal never moves stock; it only decides what shoppers can see and buy.",
      "If a whole section of the Commerce area is missing, it's an entitlement your platform admin controls, not a bug.",
    ],
    relatedIds: [
      "commerce-catalogue",
      "commerce-boxes",
      "commerce-checkout",
      "commerce-portal-editor",
      "commerce-analytics",
    ],
    pageIds: ["commerce", "commerce-dashboard"],
  },
  {
    id: "commerce-catalogue",
    title: "Publishing products to the storefront",
    emoji: "🛍️",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "Choose which items customers can see and buy, and merchandise them with images, descriptions and ordering.",
    body: `## 🛍️ Deciding what customers can see and buy

Your inventory and your storefront are deliberately separate. Holding stock does **not** put it on the portal — you publish the products you want customers to browse, and everything else stays internal.

**Publishing a product**

Open a product in the Commerce area and merchandise it:

- **Title, description and images** shown to shoppers (these can differ from your internal SKU notes).
- The **categories and collections** it belongs to.
- Whether it is **published** — the switch that makes it visible in the catalogue.

Publishing a product needs a stronger permission than merely editing its merchandising, so a staff member can tidy copy and images without being able to push something live.

**Availability follows your real stock**

A published product's buy state reflects live inventory: sellable quantity, what's reserved in other customers' Boxes, and any pre-order stock still on its way. You don't maintain a second stock number for the portal.

**Unpublishing**

Unpublish a product to pull it from the catalogue without deleting anything. Items already sitting in customers' Boxes are handled by your Box policy — unpublishing hides it from new shoppers, it doesn't silently empty existing Boxes.`,
    tips: [
      "Merchandising copy is customer-facing — write it for shoppers, not for your stock room.",
      "Only holders of the publish permission can change a product's live/hidden state; merchandisers can still prepare it.",
      "Unpublishing removes a product from browsing without touching stock, orders or Boxes it already sits in.",
    ],
    relatedIds: ["commerce-overview", "commerce-categories-collections", "commerce-pricing"],
    pageIds: ["commerce-products"],
  },
  {
    id: "commerce-categories-collections",
    title: "Categories and collections",
    emoji: "🗂️",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "Organise the catalogue into browsable categories and hand-picked collections shoppers can navigate.",
    body: `## 🗂️ Giving the catalogue a shape

Two tools group your products for shoppers, and they do different jobs.

**Categories — the structure**

Categories are the backbone of navigation, like aisles in a shop. A product's category decides where it appears when a customer browses, and categories drive the category-grid blocks on your portal pages. Keep them broad and stable — "Gold coins", "Silver bars" — rather than one per promotion.

**Collections — the curation**

Collections are hand-picked groupings you assemble for a reason: "This month's new arrivals", "Staff picks", "Sovereigns". A product can sit in several collections at once, and collections are what you feature in collection-grid blocks and promotions.

> Rule-based automatic collections aren't part of this — collections are curated by hand for now.

**Where they show up**

Both feed the portal page editor: a **category grid** block lets shoppers jump into a category, and a **collection grid** block showcases a curated set. Rearranging them on the page never changes the underlying catalogue.`,
    tips: [
      "Use categories for permanent structure and collections for anything seasonal or promotional.",
      "A product can belong to many collections but reads most naturally in a single primary category.",
      "Collections are manual — there are no automatic membership rules, so review them when stock changes.",
    ],
    relatedIds: ["commerce-catalogue", "commerce-portal-editor"],
    pageIds: ["commerce-categories", "commerce-collections"],
  },
  {
    id: "commerce-customers-groups",
    title: "Customer accounts and groups",
    emoji: "👥",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "How portal customers get accounts, and how customer groups let you treat trade and retail buyers differently.",
    body: `## 👥 Knowing who's buying

**Customer accounts**

Portal shoppers have their own accounts. An account ties a person to their Box, their order history, any agreed prices, and — for trade buyers — the group they belong to. Because a returning customer is recognised, the prices and content they see can be theirs specifically rather than a generic shop-window view.

**Customer groups**

Groups are how you separate audiences — most commonly **trade (B2B)** from **retail (B2C)**. A group can carry its own group pricing, so a wholesale customer sees wholesale prices automatically without you quoting by hand each time.

- A customer's group is set on their record and can be changed by staff with the right permission.
- Group pricing (where enabled) applies on top of your list prices for everyone in that group.
- Analytics can split B2B versus B2C conversion so you can see how each audience behaves.

**Privacy of internal detail**

Sensitive internal B2B fields on a customer are only shown to operators who hold the analytics permission — a general operator sees the customer-facing view without those internal blocks.`,
    tips: [
      "Set a customer's group before quoting — group pricing then applies automatically at checkout.",
      "B2B vs B2C conversion in analytics is only meaningful once customers are actually assigned to groups.",
      "Internal-only customer detail is hidden from operators without the commerce analytics permission by design.",
    ],
    relatedIds: ["commerce-pricing", "commerce-analytics", "commerce-overview"],
    pageIds: ["commerce-customers", "commerce-customer-groups"],
  },
  {
    id: "commerce-pricing",
    title: "Portal pricing and agreed prices",
    emoji: "💰",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.pricing.view",
    summary:
      "How a shopper's price is worked out — list price, group pricing and per-customer agreed prices — and who can change them.",
    body: `## 💰 Working out the price a customer sees

The price on the portal is resolved for the specific shopper, in a clear order of precedence.

**The layers**

1. **List price** — the product's standard portal price.
2. **Group pricing** — a price adjustment that applies to everyone in a customer group (e.g. your trade tier). Applied on top of the list price where the customer belongs to such a group.
3. **Agreed price** — a price negotiated with an individual customer for a specific product. The most specific layer, it overrides the group and list price for that customer only.

**Who can change what**

- Viewing prices needs the pricing view permission.
- Editing list, group or agreed prices needs the pricing edit permission — so a staff member can see what a customer is being charged without being able to move the number.

**Price locks and Boxes**

When a customer adds an item to their Box, your Box policy may **lock** that price for a period, so the figure they were promised holds even if your list price moves before they check out. That lock is separate from the pricing layers above — it freezes whatever price applied at the moment of adding. See the Boxes article for how long a lock lasts.`,
    tips: [
      "Agreed price beats group price beats list price — the most specific layer always wins.",
      "Viewing and editing prices are separate permissions, so staff can audit a price without changing it.",
      "A Box price lock freezes the price at add-time; it's not a fourth pricing layer, it's a snapshot.",
    ],
    relatedIds: ["commerce-customers-groups", "commerce-boxes", "commerce-checkout"],
    pageIds: ["commerce-pricing"],
  },
  {
    id: "commerce-boxes",
    title: "How Boxes work",
    emoji: "📦",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.box.view",
    summary:
      "A Box is a persistent basket that can reserve stock and lock a price across visits — the heart of the commerce portal.",
    body: `## 📦 The Box — a basket that remembers

A **Box** is a customer's persistent basket. Unlike an ordinary web cart that empties when the tab closes, a Box holds its contents across visits, and — depending on your policy — can **reserve the stock** and **lock the price** while the customer decides.

**What a Box can hold onto**

- **Stock reservation** — items in a Box can be held so another shopper can't take them out from under the customer. Reserved quantity is subtracted from what's sellable elsewhere.
- **Price lock** — the price at the moment of adding can be frozen for a set window, so a customer isn't penalised if your list price moves while they think it over.

**Box states**

| State | Meaning |
|-------|---------|
| Open | The customer is actively building it |
| Reserved | Stock is being held for the customer |
| Locked | Contents and/or prices are fixed pending checkout |
| Checked out | Submitted to checkout, awaiting or taken payment |

**Reservations and locks expire**

This is the point operators most need to understand: a Box is **intent, not a sale**. Reservations and price locks are time-limited by your Box policy, and a Box can be abandoned. That's why Box contents are treated as pipeline demand everywhere in reporting and forecasting — never as confirmed sales — because a large share of Boxes expire without ever being paid.

**Operator controls**

With the right permissions you can view any Box, see abandoned Boxes, override a price or apply a lock, extend or release a reservation, and reprice a Box. Price overrides and locks need a stronger permission than ordinary Box editing.`,
    tips: [
      "A Box is intent, not a sale — reserved stock and locked prices expire on your policy's timetable.",
      "Reserved quantity is deducted from sellable stock elsewhere, so stale Boxes can quietly starve the shop of stock.",
      "Overriding a Box price or applying a lock needs the price-override permission, above ordinary Box editing.",
    ],
    relatedIds: ["commerce-pricing", "commerce-checkout", "commerce-analytics", "commerce-faq"],
    pageIds: ["commerce-boxes", "commerce-dashboard"],
  },
  {
    id: "commerce-checkout",
    title: "Checkout, payment and awaiting payment",
    emoji: "💳",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "How a submitted Box becomes a paid sales order, and what the awaiting-payment state means for your stock.",
    body: `## 💳 Turning a Box into a real order

Checkout is the bridge from a customer's Box to a genuine sales order in your system.

**The flow**

1. The customer **submits** their Box to checkout.
2. A **checkout session** is created, and the Box moves to a checked-out state.
3. **Payment is taken.** Only once payment succeeds does a real sales order exist and stock is committed as sold.

**Awaiting payment**

Between submission and payment a checkout can sit in **awaiting payment** — the customer has committed but the money hasn't landed yet (e.g. a bank transfer in flight). This is a distinct, watched state:

- The stock stays held for the customer while payment is pending.
- It appears on the Awaiting payment screen so staff can chase or reconcile it.
- It counts as a **pipeline** demand signal with higher confidence than an open Box, because a submitted checkout usually converts — but it is still **not** a confirmed sale until paid.

**Why paid is the only real line**

Nothing becomes confirmed sales history until payment is confirmed. An awaiting-payment checkout can still fall through, so folding it into sales or revenue would overstate both. Reporting keeps it as pipeline right up to the moment it's paid.`,
    tips: [
      "A sales order only exists once payment succeeds — a submitted checkout on its own is still pipeline.",
      "Awaiting payment holds stock for the customer; reconcile or release these promptly so stock isn't stranded.",
      "Awaiting payment carries more forecasting confidence than an open Box, but still never counts as a sale.",
    ],
    relatedIds: ["commerce-boxes", "commerce-pricing", "commerce-analytics"],
    pageIds: ["commerce-checkouts"],
  },
  {
    id: "commerce-portal-editor",
    title: "Using the portal page editor",
    emoji: "🎨",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.portal.design",
    summary:
      "Compose your portal home and other pages from reusable blocks, then publish — no developer required.",
    body: `## 🎨 Merchandising your storefront without code

The portal page editor lets you build the customer-facing pages from a palette of reusable **blocks**, arrange them, and publish — without a developer.

**The blocks you can use**

Hero, text, image, product grid, product carousel, category grid, collection grid, banner, video, call-to-action, testimonials, FAQ, recently viewed, new arrivals, featured products, pre-orders, spacer and divider. Content blocks (product/category/collection grids, recently viewed, new arrivals) pull live data, so a "featured products" block reflects your current catalogue rather than a frozen list.

**Draft versus published**

This is the most important thing to understand about the editor:

- Your edits are a **draft**. Working on a page changes what *you* see in the editor, never what customers see.
- Customers only see a page once you **publish** it. Publishing swaps the live version for your draft in one deliberate step.

Because of that split you can rework the home page across several sessions without a half-finished layout ever reaching a shopper.

**Publishing takes effect immediately**

When you publish, the change reaches customers without them (or you) having to hard-reload — the shared configuration is refreshed so a publish is never a silent no-op. If a brand-new block type were ever missing from the publish pipeline the system refuses to skip it silently, so a page can't quietly drop a section you placed.

**The home page**

The portal home ships with a sensible default layout using these blocks. It's data-driven, so you can reorder and swap sections rather than starting from a blank page.`,
    tips: [
      "Your draft is private — nothing you change reaches customers until you press Publish.",
      "Content blocks show live catalogue data, so a featured or new-arrivals block updates itself as stock changes.",
      "Publishing is immediate and safe: it refreshes the live config rather than waiting for a reload.",
    ],
    relatedIds: ["commerce-catalogue", "commerce-categories-collections", "commerce-faq"],
    pageIds: ["commerce-portal-editor"],
  },
  {
    id: "commerce-analytics",
    title: "Reading Commerce Analytics",
    emoji: "📈",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.analytics.view",
    summary:
      "Product metrics, the buying funnel and per-customer activity — with the crucial pipeline-versus-sales distinction.",
    body: `## 📈 What customers actually do

Commerce Analytics turns first-party portal events into three views. All of it is built from your own portal activity — there's no cross-site behavioural tracking.

**Product analytics**

Per product: views, unique viewers, add-to-box count and conversion, quantity currently sitting in Boxes, potential box revenue, pre-order demand, completed box sales, abandoned quantity, average achieved price and group demand. Events are deduplicated, so a customer refreshing a page doesn't inflate the view count. These metrics come from a scheduled rollup rather than being recomputed live, so the screen stays fast — the figures are as fresh as the last refresh.

**The funnel**

The commerce funnel reports the journey **view → added to Box → revisit → checkout started → submitted → paid → order → fulfilled**, with the conversion rate between each step, average Box build duration, average Box value and item count, B2B versus B2C conversion, and how much revenue originated through Boxes.

**Per-customer activity**

In Customer 360, staff with the analytics permission can see a customer's own commerce activity — what they've viewed, boxed and bought.

**Pipeline is not sales — the golden rule**

Analytics deliberately separates **pipeline** (quantity in open Boxes, awaiting-payment checkouts, pre-order demand, views) from **confirmed sales** (paid orders). Pipeline is intent that can still evaporate — Boxes expire, checkouts fall through — so it is labelled and reported apart from realised revenue. Reading "potential box revenue" as money in the bank is the single most common misreading; treat it as demand you could convert, not sales you've made.`,
    tips: [
      "Pipeline figures (Boxes, awaiting payment, pre-orders, views) are demand you might convert — never treat them as revenue earned.",
      "Product metrics refresh on a schedule, so a just-happened event may not show until the next rollup.",
      "Event deduplication means refreshes don't inflate views — a rising view count is real interest, not reloads.",
    ],
    relatedIds: ["commerce-boxes", "commerce-checkout", "commerce-customers-groups", "commerce-overview"],
    pageIds: ["commerce-analytics", "commerce-dashboard"],
  },
  {
    id: "commerce-faq",
    title: "Editing the customer-facing FAQ",
    emoji: "❓",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.portal.design",
    summary:
      "Configure the FAQ shoppers read on the portal, including the prominent 'How does my Box work?' explanation.",
    body: `## ❓ Answering shoppers before they ask

The portal carries a **customer-facing FAQ** you control. It's distinct from this internal Help Centre — the FAQ is what your shoppers read, written in your voice, for their questions.

**How it works**

- You author FAQ entries — a question and its answer — and order them.
- They appear on the portal, and can be surfaced in an **FAQ block** on any portal page via the editor.
- Editing FAQ content uses the same portal-design permission as the page editor.

**The "How does my Box work?" answer**

Boxes are the part of the portal customers most often misunderstand, so the FAQ is expected to carry a prominent **"How does my Box work?"** entry. Explain that a Box is a persistent basket, then describe only the behaviours enabled for this tenant: whether stock is reserved, whether a price is locked, how long each window lasts, what happens at expiry and which payment or delivery choices are actually offered. If a policy is disabled or has no configured window, say that plainly rather than implying protection.

**Keep it in step with your policy**

Because the FAQ describes Boxes and pricing, review it whenever you change the tenant's reservation or price-lock settings. The customer copy must follow the resolved settings; it must not introduce a universal reservation, price-lock, payment or delivery promise.`,
    tips: [
      "The customer FAQ is separate from this internal Help Centre — write it for shoppers, not staff.",
      "Give 'How does my Box work?' a prominent slot; it's the question that prevents the most confusion and abandoned Boxes.",
      "Revisit the FAQ whenever you change reservation or price-lock windows so it never over-promises.",
    ],
    relatedIds: ["commerce-boxes", "commerce-portal-editor", "commerce-overview"],
    pageIds: ["commerce-settings", "commerce-portal-editor"],
  },
  {
    id: "commerce-troubleshooting",
    title: "Commerce troubleshooting",
    emoji: "🛠️",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "A source-first checklist for missing products, held stock, unpaid checkouts, delayed analytics and missing entitlements.",
    body: `## 🛠️ Fixing a Commerce problem without guessing

Commerce screens intentionally separate live source records from derived pipeline metrics. Start with the source record below, then refresh the derived view.

**A product is missing**

Open **Commerce → Products** and check publication state, B2C/B2B audience, category or collection visibility and sellable stock. Publishing does not move stock; it only makes an existing inventory item available to the portal.

**Stock looks held**

Open **Commerce → Boxes** and inspect active and expired reservations. A reservation can reduce sellable stock while the Box is still pipeline intent. Do not adjust inventory to compensate for an unreviewed reservation.

**A checkout is not in Sales**

Check **Commerce → Awaiting payment**. A submitted checkout can hold stock while payment is pending, but it is not a recognised sale until the payment provider confirms it. After confirmation, verify the resulting order in **Sales**.

**Analytics is behind or shows no data**

Commerce Analytics uses scheduled rollups. Check the generated timestamp and the report-preparing state before treating a recent event as missing. A tenant whose rollup has not run has no measurements yet — it must not be represented as a grid of zeros.

**A route or article is missing**

Commerce is gated twice: the tenant needs the \`customer_commerce_portal\` feature and the operator needs the permission for that surface. For example, Boxes require \`commerce.box.view\`, while Analytics requires \`commerce.analytics.view\`. Ask an administrator to check both rather than sharing a deep link that the operator cannot use.

**Everyday Bullion Box terminology**

“Everyday Bullion Box” is tenant-specific terminology for this tenant's persistent Box capability. It is not a hard-coded platform object or a universal behaviour for every tenant; keep customer-facing wording aligned with the tenant's configured reservation and price-lock policy.`,
    tips: [
      "Intent is not sales: views, Boxes, reservations, pre-orders and awaiting payment remain pipeline until payment is confirmed.",
      "A feature can be enabled while a particular operator still lacks that surface's permission.",
      "Use the generated timestamp in Analytics to distinguish delayed rollups from a genuine source-data issue.",
    ],
    relatedIds: ["commerce-overview", "commerce-boxes", "commerce-checkout", "commerce-analytics"],
    pageIds: ["commerce", "commerce-products", "commerce-analytics"],
  },
  {
    id: "commerce-dashboard",
    title: "Reading the Commerce Dashboard",
    emoji: "📊",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "Use the generated Commerce snapshot to monitor portal customers, Boxes, checkouts and payment intent.",
    body: `## 📊 The dashboard is an operational snapshot

Commerce Dashboard combines tenant-scoped customer, Box and checkout facts into a generated snapshot. It is a starting point for attention, not a second accounting ledger. The generated time and tenant timezone tell you how fresh the snapshot is.

**What the signals mean**

- **Portal customers** are customer accounts known to Commerce; they are not a measure of revenue.
- **Active or cooling Boxes** are persistent baskets and reservation state. They represent intent and may expire.
- **Checkouts started** include attempts that have not become paid orders.
- **Awaiting payment** is the value of submitted checkout intent, not money received.

**Normal workflow**

Start with an alert or unusual count, open its source surface, and inspect the customer, Box or checkout record before changing data. Use Products for publication and sellable stock, Boxes for reservations, Awaiting Payment for pending payment, and Sales for recognised orders and revenue.

**Permissions and safe boundaries**

Viewing the dashboard requires the \`customer_commerce_portal\` feature and \`commerce.view\`. Financial values elsewhere may be redacted when the caller lacks the relevant price permission. Never infer a customer identity from an aggregate count or share customer activity outside the tenant.`,
    tips: [
      "A fresh dashboard snapshot can still contain pipeline intent; freshness does not make an unpaid checkout a sale.",
      "If a count looks stale, compare its generated time with the source surface before editing records.",
      "The dashboard may link into Sales, but Sales remains authoritative for recognised orders and revenue.",
    ],
    relatedIds: ["commerce-overview", "commerce-boxes", "commerce-checkout", "commerce-analytics"],
    pageIds: ["commerce-dashboard"],
  },
  {
    id: "commerce-awaiting-payment",
    title: "Reconciling Awaiting Payment checkouts",
    emoji: "⏳",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.payment.view",
    summary:
      "Understand pending payment, held stock, expiry and the hand-off to the paid Sales record.",
    body: `## ⏳ Awaiting Payment is still pipeline

The Awaiting Payment screen lists submitted checkouts whose payment provider has not yet confirmed a successful payment. A submitted checkout may continue to hold a reservation according to the tenant's Box policy, but its amount is not recognised revenue.

**Normal reconciliation**

1. Search by checkout, customer or payment reference and check the checkout's status, payment method and last activity.
2. Compare the provider or bank reference with the payment record. Do not mark a payment received from a screenshot or customer statement alone.
3. For a supported bank transfer, use the controlled **Mark Received** action only after the bank record has been verified.
4. Confirm the idempotent payment result and reload the checkout. Then verify the resulting paid order in Sales before treating it as a sale.
5. If it has expired or failed, follow the displayed failure reason and the tenant's customer-contact policy; do not recreate a payment by hand.

Payment processing is idempotent: retries with the same provider reference must resolve to the existing payment rather than create a duplicate. A pending provider response is not proof of failure or success.

**Permissions and boundaries**

Viewing requires \`commerce.payment.view\`; payment mutations require the stronger payment or management permission. Customer email, addresses and payment references are sensitive operational data. Only use the minimum record needed to reconcile and never paste payment credentials into notes.`,
    tips: [
      "An awaiting amount is a pipeline value, not a receivable or recognised sale.",
      "Never compensate for a pending checkout by manually reducing inventory; review the Box reservation and expiry instead.",
      "If the provider and checkout disagree, preserve both references and escalate for reconciliation rather than double-posting.",
    ],
    relatedIds: ["commerce-checkout", "commerce-boxes", "commerce-payments", "commerce-overview"],
    pageIds: ["commerce-awaiting-payment"],
  },
  {
    id: "commerce-pre-orders",
    title: "Managing future-stock pre-orders",
    emoji: "🗓️",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredFeatures: [FeatureKey.pre_orders_enabled],
    requiredPermission: "pre_orders.view",
    summary:
      "Review future-stock demand, capacity and allocation without presenting expected stock as available.",
    body: `## 🗓️ Pre-orders are demand against future stock

The Commerce Pre-orders route reuses the canonical Pre-orders batch workspace. Its batches, purchase-order links, capacity, expected dates and allocation state are the source of truth; Commerce does not maintain a parallel future-stock quantity.

**Normal workflow**

1. Open a batch and confirm its product, expected supply, capacity and customer-facing availability.
2. Review pre-order demand and customer allocation against the batch, including any outstanding customer choices.
3. Update expected dates or capacity only from authoritative purchase-order and batch information.
4. When stock arrives, receive it through the normal purchase-order and inventory workflow, then let the batch allocation process move eligible demand forward.
5. Hand paid or fulfilled orders to Sales and Packing. A pre-order or deposit is not automatically recognised revenue.

**Statuses and boundaries**

Statuses such as open, full, allocated, fulfilled or cancelled describe the batch pipeline. They do not promise a delivery date unless the tenant has confirmed one. The \`pre_orders_enabled\` feature and \`pre_orders.view\` permission are required to read this surface; management actions require the stronger pre-order permission.

Do not promise a price, reservation, allocation or delivery window that is not present in the batch and tenant policy. Customer demand and future supply must remain distinguishable from sellable on-hand stock.`,
    tips: [
      "Use the purchase order and receipt records to verify future supply; do not make an expected quantity look sellable early.",
      "A full batch is a capacity state, not proof that every customer has paid.",
      "When allocation changes, re-check the customer-facing status and the resulting Sales/Packing hand-off.",
    ],
    relatedIds: ["commerce-pre-sales", "commerce-boxes", "commerce-checkout", "commerce-overview"],
    pageIds: ["commerce-pre-orders"],
  },
  {
    id: "commerce-pre-sales",
    title: "Running Commerce pre-sales",
    emoji: "📣",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredFeatures: [FeatureKey.pre_orders_enabled],
    requiredPermission: "pre_orders.view",
    summary:
      "Operate pre-sale campaigns backed by the canonical pre-order batch, with clear customer and revenue boundaries.",
    body: `## 📣 Pre-sales use the pre-order pipeline

Pre-sales are the customer-facing campaign view over the same future-stock batches used by Pre-orders. There is no separate pre-sale inventory or revenue model. Product copy, capacity, expected supply and allocation are controlled by the batch and its linked purchase-order facts.

**Before publishing a campaign**

- Confirm the product and customer audience.
- Confirm capacity, expected supply and the tenant's rules for payment, deposits, reservation and price locking.
- Make sure the copy distinguishes an expression of demand from a paid order and does not promise universal delivery.
- Check that the customer-facing status and FAQ match the current tenant policy.

**After interest arrives**

Review demand in Pre-orders, keep future stock separate from on-hand stock, and use Checkout/Awaiting Payment for payment state. Only confirmed payment creates the Sales hand-off; Packing and Postage own dispatch and delivery status.

Access requires the portal feature, \`pre_orders_enabled\` and \`pre_orders.view\`. Use the stronger management permission for changes. Customer-facing copy must not expose private customer data or internal supplier details.`,
    tips: [
      "Pre-sale demand is not a forecast and is not recognised revenue.",
      "Do not edit inventory quantities to make a pre-sale look available; update the canonical batch or receipt record.",
      "If supply or timing changes, update the source batch and review the campaign copy before contacting customers.",
    ],
    relatedIds: ["commerce-pre-orders", "commerce-portal-editor", "commerce-faq", "commerce-checkout"],
    pageIds: ["commerce-pre-sales"],
  },
  {
    id: "commerce-quotes",
    title: "Quotes, trade requests and credit",
    emoji: "🧾",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "Turn B2B requests into controlled quotes and credit decisions without bypassing pricing or payment records.",
    body: `## 🧾 Quotes are proposals, not orders

Commerce Quotes is the trade workspace for customer requests, quote versions, terms and credit context. A quote can begin from a customer's Box or request, but it does not reserve stock, create a Sale or recognise revenue merely because it was prepared or accepted.

**Normal workflow**

1. Confirm the customer identity and whether the request is B2B or B2C before reviewing any private account or agreed-price information.
2. Inspect the Box lines, current catalogue pricing, group or agreed price basis and available stock.
3. Set expiry, payment terms, delivery terms and notes from tenant-approved policy. Never invent a discount or credit limit in the quote.
4. Save the quote and use its status to distinguish draft, sent, accepted, expired and rejected. Keep superseded versions for audit.
5. Convert only through the supported checkout, pre-order or Sale hand-off. Re-check price, stock and customer authority at conversion time.

Prices and credit are sensitive financial data. Viewers may see redacted values, and any customer-supplied audience or group claim must not override the customer's own account. A quote's acceptance is customer intent until the authoritative payment and Sales records confirm the transaction.`,
    tips: [
      "Quote expiry protects against stale price and stock assumptions; it is not a delivery promise.",
      "Use customer and group records as the pricing authority; do not copy a price from an old quote into a new one.",
      "For a disputed quote, retain the version and source pricing explanation instead of overwriting history.",
    ],
    relatedIds: ["commerce-customers-groups", "commerce-pricing", "commerce-boxes", "commerce-payments"],
    pageIds: ["commerce-quotes"],
  },
  {
    id: "commerce-payments",
    title: "Commerce payment records and reconciliation",
    emoji: "💳",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.payment.view",
    summary:
      "Read provider payment records, safely mark verified transfers and preserve an idempotent audit trail.",
    body: `## 💳 One payment record, one reconciliation trail

Commerce Payments lists the payment records created by checkout and provider callbacks. The payment record and provider reference are authoritative for payment state; a customer message, a checkout total or an operator's memory is not.

**Statuses and transitions**

- **Pending** means the provider or bank has not been confirmed.
- **Completed** means the payment has been accepted and can feed the paid-order hand-off.
- **Failed** means the attempt did not complete; investigate the provider reason before retrying.
- A reversal or refund must remain linked to the original payment and order.

**Normal workflow**

Filter by method or status, open the payment, compare its reference with the external provider or bank record, and use the permitted reconciliation action. The server's idempotency key and provider reference prevent callback retries from creating duplicate payment rows. After a successful reconciliation, reload and verify the related checkout and Sales order.

Viewing requires \`commerce.payment.view\`; marking a transfer received requires \`commerce.payment.mark_received\` or the management permission. Never store full card data, credentials or sensitive bank details in Commerce notes.`,
    tips: [
      "Retrying a provider callback should resolve to the existing payment, not create a second completed payment.",
      "A completed payment still needs the resulting order checked in Sales before fulfilment.",
      "If the provider reference is missing or ambiguous, stop and escalate rather than guessing which customer to credit.",
    ],
    relatedIds: ["commerce-awaiting-payment", "commerce-checkout", "commerce-postage", "commerce-overview"],
    pageIds: ["commerce-payments"],
  },
  {
    id: "commerce-postage",
    title: "Commerce Postage and delivery options",
    emoji: "🚚",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredFeatures: [FeatureKey.postage_module_enabled],
    requiredPermission: "postage.view",
    summary:
      "Configure customer-facing delivery choices, zones, packaging and collection without bypassing fulfilment.",
    body: `## 🚚 Postage is the checkout delivery authority

Commerce Postage configures the services, rules, zones, parcel and insurance requirements, packaging, B2B rules and collection choices that checkout can offer. The checkout planner reads these records; legacy delivery switches elsewhere do not override them.

**Normal workflow**

1. Maintain active services and customer-facing names, charges and audience.
2. Define destination zones, parcel and insurance rules, packaging and free-postage thresholds.
3. Keep B2B delivery terms separate from retail choices and verify product overrides deliberately.
4. Use the simulator with representative customer, basket and destination data before publishing a change.
5. After payment, hand the order to Packing and the canonical Postage shipment/label flow. Tracking and dispatch status belong to those fulfilment records.

**Boundaries**

The \`postage_module_enabled\` feature and \`postage.view\` permission gate this surface; edits require the postage management permission. A delivery option being visible is not proof that a label was purchased or a parcel delivered. Do not promise a courier, charge or delivery date that the active rule and service do not support.`,
    tips: [
      "Run the simulator after changing zones, rules or packaging; a saved rule is not evidence that every basket receives the intended option.",
      "Collection is a fulfilment choice, not a postage label.",
      "Use Packing and shipment records for dispatch evidence and tracking; do not infer delivery from checkout selection.",
    ],
    relatedIds: ["commerce-checkout", "commerce-payments", "commerce-portal-editor", "commerce-overview"],
    pageIds: ["commerce-postage"],
  },
  {
    id: "commerce-settings",
    title: "Tenant Commerce Settings",
    emoji: "⚙️",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.portal.settings",
    summary:
      "Change tenant-owned Box, reservation and price-lock policy while keeping checkout and delivery authorities clear.",
    body: `## ⚙️ Settings describe this tenant's policy

Commerce Settings stores tenant-owned behaviour such as maximum open Boxes, reservation enablement and duration, and price-lock enablement and duration. These values are read by the canonical Box policy; they are not universal Commerce promises.

**Before changing a setting**

1. Confirm the relevant entitlement is available and review the current value and last update time.
2. Check the impact on open Boxes, active reservations, customer-facing FAQ copy and support instructions.
3. Change only the section you intend to change and save. Verify the response after the reload.
4. Review a representative customer Box and the portal FAQ. A policy change should not leave copy claiming a reservation or price lock that is no longer active.

Checkout validation and payment availability are owned by their runtime services. Future stock is owned by Pre-orders. Delivery options are owned by Commerce Postage. Notifications are owned by the notification centre. Do not use this page to edit a setting that the authoritative surface does not read.

Viewing the settings document requires \`commerce.view\`; editing requires \`commerce.portal.settings\`. Financial, customer and policy changes are tenant-scoped and should be made by authorised operators with a clear reason.`,
    tips: [
      "A disabled reservation or price-lock entitlement is different from a setting that is enabled but expired for one Box.",
      "Changing a window does not rewrite a customer's historical payment or Sales record.",
      "Update the customer FAQ only after the saved policy is confirmed, and avoid promising universal payment or delivery behaviour.",
    ],
    relatedIds: ["commerce-boxes", "commerce-faq", "commerce-portal-editor", "commerce-postage"],
    pageIds: ["commerce-settings"],
  },
  {
    id: "commerce-help",
    title: "Using Commerce Help",
    emoji: "📚",
    module: "Commerce Portal",
    featureKey: FeatureKey.customer_commerce_portal,
    requiredPermission: "commerce.view",
    summary:
      "Find the right Commerce screen and gated operator article for each catalogue, customer, payment and fulfilment state.",
    body: `## 📚 A permission-aware Commerce map

Commerce Help is an operator index, not a customer FAQ. Each topic links to the live Commerce surface and to the full Help Centre article. The map hides topics whose feature or permission is unavailable, so a deep link cannot be mistaken for access.

Use the journey in order: publish catalogue products, organise categories and collections, resolve customer/group pricing, review Boxes and reservations, follow checkout and payment, then hand paid orders to Sales, Packing and Postage. Use Analytics for labelled pipeline and recognised-sales measures, and Settings for tenant policy.

**When a state looks wrong**

Start with the smallest authoritative record: product publication and inventory for catalogue visibility, Box policy for reservations, checkout and payment records for unpaid states, pre-order batches for future stock, customer/group records for audience and pricing, and generated timestamps for analytics. Treat views, Boxes, reservations, pre-orders and unpaid checkouts as intent until payment creates the Sales record.

The internal article and the portal's customer-facing FAQ are separate. The FAQ must use tenant-configured reservation and price-lock windows and must not promise payment, price protection or delivery behaviour that the current policy does not provide.`,
    tips: [
      "If a topic is missing, check both the parent portal feature and the topic's operator permission.",
      "Use the related links in an article to hand off to the next authoritative surface rather than duplicating data.",
      "Do not share customer-specific help or payment details with a caller who cannot access that Commerce capability.",
    ],
    relatedIds: ["commerce-overview", "commerce-troubleshooting", "commerce-settings", "commerce-analytics"],
    pageIds: ["commerce-help"],
  },
  {
    id: "markets-alerts-content",
    title: "Live prices, market alerts, overlays and widgets",
    emoji: "📈",
    module: "Markets",
    featureKey: FeatureKey.market_tools_enabled,
    requiredPermission: "markets.view",
    summary: "Understand market data, create quiet price alerts, export branded graphics, and configure overlays and embeddable widgets.",
    body: `## Live market data

**Spot** is the indicative wholesale price for a metal. **Bid** is the indicative buying side and **ask** is the selling side. Values are normalised to the selected currency and unit. Purity and scrap calculations multiply gross weight by fineness before applying spot; percentage adjustments are explicit premiums or discounts. Quality badges distinguish live, delayed, stale and unavailable data. Stale or unavailable data never triggers a market alert.

Charts and historical views use time-stamped observations. OHLC means open, high, low and close for a period. Ratios divide one canonical metal quote by another in the same currency and unit. The converter changes units or currency without changing the underlying observation. Dealer offers are commercial comparisons, not a market forecast.

## Alerts without repeated noise

Create a rule under **Markets → Alerts & Content**. Threshold rules watch above or below a price, daily-change rules watch percentage movement, and ratio rules compare two metals. Each rule belongs to you and your tenant. A rule fires when its condition becomes true, then observes its cooldown before it can fire again. Pause preserves the rule without evaluating it; delete removes it. Delivery follows your existing notification channels, quiet hours, digests and preferences.

## Branded market graphics and Social

Export square (1080×1080), portrait (1080×1350), story (1080×1920) or landscape (1600×900) SVGs. The graphic records its canonical timestamp, currency and quality. If Social is enabled, send it to the existing draft, approval and scheduling workflow. The server rejects unsupported publish targets; this tool never bypasses approvals or makes an automatic live post.

## Overlays, OBS and scenes

Use Overlay Studio to arrange live price blocks, charts, clocks, tickers and tenant branding. Layouts hold element positions; scenes group layouts for a broadcast; profiles hold reusable output settings. Copy the browser-source URL and key into OBS, match the canvas dimensions, and keep text inside the safe-area guides so mobile crops do not hide it. Transparent output should use an alpha-capable browser source rather than a captured white background.

Keep overlay keys private. Regenerate a key if it is exposed. Custom domains must be configured before sharing URLs, and any embed allowlist must include the exact parent domain (scheme and subdomain matter).

## Public pages, widgets and embedding

Enable only the public market widgets licensed for your tenant. Configure metal, currency, unit, refresh interval and branding once; the public market page, commerce/portfolio hosts and embeds reuse canonical quotes rather than inventing their own prices. Use the supplied embed code, preserve its full URL, and allow the parent domain. Never scrape the display HTML as a price feed.

## Troubleshooting

- **Prices unavailable:** check the quality badge and provider timestamp; wait for a fresh canonical snapshot.
- **Alert did not fire:** confirm it is not paused, the input is fresh, the condition crosses the threshold, and the cooldown has elapsed.
- **No notification:** check notification preferences, quiet hours and digest settings.
- **Blank OBS/browser source:** re-copy the URL and key, check dimensions, transparency and domain allowlists.
- **Widget refused to embed:** add the exact host to the allowlist and remove conflicting frame restrictions.
- **Social draft rejected:** enable Social, choose a connected publish-capable target, and satisfy that platform’s media requirements.

Market data is indicative and is not financial advice.`,
    tips: [
      "Use a cooldown long enough for the decision you are making; five-minute repeats rarely help.",
      "Read the timestamp and quality badge before relying on a chart, export or dealer comparison.",
      "Keep overlay keys private and use safe-area guides for story and portrait layouts.",
    ],
    relatedIds: ["commerce-overview", "dashboard-overview"],
    pageIds: ["markets", "markets-live-prices", "markets-charts", "markets-ratios", "markets-history", "markets-converter", "markets-settings", "markets-help", "markets-alerts"],
  },
];

export type HelpPageId = string;

export function getArticlesForPage(pageId: HelpPageId): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.pageIds.includes(pageId));
}

export function getArticleById(id: string): HelpArticle | undefined {
  return HELP_ARTICLES.find((a) => a.id === id);
}

export function searchArticles(query: string): HelpArticle[] {
  if (!query.trim()) return HELP_ARTICLES;
  const q = query.toLowerCase();
  return HELP_ARTICLES.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.body.toLowerCase().includes(q) ||
      a.tips.some((t) => t.toLowerCase().includes(q)) ||
      a.module.toLowerCase().includes(q),
  );
}
